import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { flushSync } from 'react-dom';


import {
  apiGetZodiacCrushMissionsToday,
  apiGetZodiacCrushProfile,
  apiSubmitZodiacCrushRun,
} from '../../../services/api';

import BoardView from './BoardView';
import ConstellationMapScreen from './ConstellationMapScreen';
import ZodiacCrushLevelGoals from './ZodiacCrushLevelGoals';
import ZodiacCrushMapScreen from './ZodiacCrushMapScreen';
import { registerBackHandler } from '../../../platform/navigation';

import { getMissionLabel as getMissionLabelFn, getMissionProgressText as getMissionProgressTextFn } from '../../missions/zcMissionLabels';
import { getMovesForLevel } from '../levels/zcLevelConfig';


import { createInitialBoard, getZcWorldProfile } from '../engine/board';
import { getWorldVisualProfile } from './worldVisualProfile';
import { projectMissionProgress } from './missionProgressProjection';
import { deriveCanonicalGoalState } from './goalSatisfaction';
import { getHint } from '../engine/boosters';
import { createRng } from '../engine/rng';
import { resolveUntilStable } from '../engine/resolve';
import { applySwap, isAdjacent, isHintSwapValid, swapWithEvents } from '../engine/swap';
import { SIGN_ELEMENT } from '../engine/type';
import {
  normalizeProtocolV1LevelConfig,
  protocolV1BoardSnapshot,
} from '../engine/protocolV1Adapter';
import { WORLD_IDENTITY_V2 } from '../engine/worldIdentityV2';
import {
  createProtocolV1RunRecorder,
  getReplayPayloadForRunSubmission,
} from '../engine/replayRecorder';
import { buildFinalRunSnapshot } from '../engine/finalRunSnapshot';

import { getGoalsForLevel, normalizeAbsoluteLevel } from '../levels/zcLevelGoals';
import { authenticatedRequest } from '../../../services/authApi';
import { getAuthToken as getCentralAuthToken } from '../../../utils/auth';
import { getAuthUserSnapshot } from '../../../utils/auth';
import { REST_API_ORIGIN } from '../../../platform/endpoints';
import {
  zcMapProgressCacheKeyForUser,
} from '../../hooks/useZodiacCrushMapProgress';
import { localizedServerContent, localizedValue } from '../../../localization/presentationLanguage';
import {
  beginZodiacCrushRun,
  completeZodiacCrushCompletionOperation,
  getZodiacCrushCompletionOperationId,
  getZodiacCrushRunOperationId,
  syncZodiacCrushOperationOwner,
} from '../utils/operationIdentity';

const DEBUG_ZC_LOGS = false && (process.env.NODE_ENV !== 'production');
const getCurrentZodiacCrushProgressCacheKey = () =>
  zcMapProgressCacheKeyForUser(getAuthUserSnapshot()?.id);

const WORLD_ORDER = ['aries','taurus','gemini','cancer','leo','virgo','libra','scorpio','sagittarius','capricorn','aquarius','pisces'];
const RUN_LIFECYCLE = Object.freeze({
  PLAYING: 'PLAYING',
  RESOLVING: 'RESOLVING',
  READY: 'READY',
  SUBMITTING_RUN: 'SUBMITTING_RUN',
  COMPLETING_LEVEL: 'COMPLETING_LEVEL',
  WON: 'WON',
  LOST: 'LOST',
  ERROR: 'ERROR',
});
const ZC_IDLE_HINT_DELAY_MS = 4500;
const zcText = (es, en) => localizedValue({ es, en });

// Completion diagnostics were intentionally removed after the authenticated
// lifecycle regression was proven and covered by deterministic tests.
const logCompletionTrace = () => {};
const logCompletionAbort = () => {};

// Stable empty progress object (module-level avoids init-order issues + stale UI flashes)
const EMPTY_LEVEL_PROGRESS = Object.freeze({
  score: 0,
  stripesActivated: 0,
  starsCreated: 0,
  cascades: 0,
  clearedFire: 0,
  clearedWater: 0,
  clearedAir: 0,
  clearedEarth: 0,
    cascadeDepth: 0,
  maxCascade: 0,
  cascadesBest: 0,
});



// --- Leaderboard fetch (ZodiacCrush) ---
const ZC_API_BASE = REST_API_ORIGIN;
const readAuthToken = () => {
  try {
    const clean = (v) => {
      let s = String(v || '').trim();
      if (!s) return '';
      if (s === 'undefined' || s === 'null') return '';

      // If someone stored "Bearer <jwt>" in LS/SS, normalize.
      s = s.replace(/^Bearer\s+/i, '').trim();

      // Strip wrapping quotes repeatedly (handles "'TOKEN'" or '"TOKEN"')
      // Example: "'TESTTOKEN'" -> TESTTOKEN
      for (let i = 0; i < 3; i++) {
        const before = s;
        s = s.replace(/^['"]+/, '').replace(/['"]+$/, '').trim();
        if (s === before) break;
      }

      return s;
    };

    const getLS = (k) => {
      try {
        return clean(localStorage.getItem(k));
      } catch {
        return '';
      }
    };

    const getSS = (k) => {
      try {
        return clean(sessionStorage.getItem(k));
      } catch {
        return '';
      }
    };

    const keys = [
      'accessToken', // 👈 CRÍTICO que vaya primero
      'token',
      'authToken',
      'zodiacus_token',
      'zodiacusToken',
      'access_token', // 👈 extra safe variant
      'jwt',
      'jwtToken',
    ];

    for (const k of keys) {
      const v = getLS(k);
      if (v) return v;
    }
    for (const k of keys) {
      const v = getSS(k);
      if (v) return v;
    }

    return '';
  } catch {
    return '';
  }
};

const classifyRunSubmitFailure = (error) => {
  const status = Number(error?.status || error?.response?.status || 0) || 0;
  const data = error?.data && typeof error.data === 'object' ? error.data : {};
  const rawCode = data.error ?? data.code ?? data.reason;
  const safeCode = typeof rawCode === 'string' && /^[a-z0-9_:-]{1,80}$/i.test(rawCode)
    ? rawCode
    : null;
  if (status === 401 || status === 403) return { category: 'auth', status, code: safeCode };
  if (status === 400) return { category: '400_validation', status, code: safeCode };
  if (status === 409) return { category: '409_conflict', status, code: safeCode };
  if (status === 422) return { category: '422_authority_replay', status, code: safeCode };
  if (status >= 500) return { category: '500_server', status, code: safeCode };
  if (error?.name === 'TypeError' || !status) return { category: 'network', status: 0, code: safeCode };
  return { category: 'unknown', status, code: safeCode };
};

async function apiGetZodiacCrushLeaderboard({ scope = 'global', limit = 50 } = {}) {
  const token = readAuthToken();
  const qs = new URLSearchParams({ scope: String(scope || 'global'), limit: String(limit || 50) });
  const url = `${ZC_API_BASE}/api/games/zodiac-crush/leaderboard?${qs.toString()}`;

  const resp = await authenticatedRequest(url, {
    rawResponse: true,
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    diagSource: 'ZodiacCrushScreen.leaderboard',
  });

  let data = null;
  try { data = await resp.json(); } catch (_) {}

  if (!resp.ok || !data?.ok) {
    const msg = data?.message || `No se pudo cargar el ranking (${resp.status})`;
    throw new Error(msg);
  }

  return data;
}

// --- Board playability helpers (Candy Crush style) ---
// ===== ZodiacCrush: Gamer UI polish (missions + topbar) =====
const ZC_UI_GAMER_CSS = `
/* ===== ZodiacCrush: Gamer UI polish (missions + topbar) ===== */
:root{
  --zc-safe-top: env(safe-area-inset-top, 0px);
  --zc-safe-bottom: env(safe-area-inset-bottom, 0px);
}

.zc-hint {
  outline: 3px solid rgba(255,215,0,0.95);
  box-shadow:
    0 0 0 4px rgba(75,0,130,0.45),
    0 0 26px rgba(255,215,0,0.75),
    0 14px 32px rgba(0,0,0,0.45);
  border-radius: 14px;
  animation: zcHintPulse 900ms ease-in-out infinite;
  z-index: 3;
}

@keyframes zcHintPulse {
  0% {
    transform: scale(1);
    filter: brightness(1.05);
  }
  50% {
    transform: scale(1.08);
    filter: brightness(1.25);
  }
  100% {
    transform: scale(1);
    filter: brightness(1.05);
  }
}

/* Top bar glass plate + title glow */
.zc-topbar{
  position: relative;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  margin: 10px 12px 8px;
  padding: calc(10px + var(--zc-safe-top)) 12px 10px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255,255,255,.12), rgba(255,255,255,.06));
  border: 1px solid rgba(255,255,255,.12);
  box-shadow: 0 16px 46px rgba(0,0,0,.28);
  backdrop-filter: blur(10px);
  will-change: transform;
  overflow: hidden;
}
.zc-topbar::before{
  content:"";
  position:absolute;
  inset:-2px;
  background:
    radial-gradient(circle at 15% 0%, rgba(218,165,32,.25), transparent 55%),
    radial-gradient(circle at 90% 110%, rgba(75,0,130,.28), transparent 60%);
  opacity:.9;
  pointer-events:none;
}
.zc-topbar__titleWrap{ position:relative; display:flex; flex-direction:column; gap:2px; }
.zc-topbar__title{
  font-weight: 900;
  letter-spacing: .6px;
  line-height: 1.05;
  text-transform: uppercase;
  filter:
    drop-shadow(0 2px 10px rgba(75,0,130,.35))
    drop-shadow(0 2px 12px rgba(218,165,32,.22));
}
.zc-topbar__subtitle{ opacity:.85; font-size: 12.5px; font-weight: 700; }

/* Game buttons */
.zc-btn{
  position: relative;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  padding: 9px 12px;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,.16);
  background: linear-gradient(180deg, rgba(255,255,255,.12), rgba(0,0,0,.12));
  color: inherit;
  box-shadow: 0 12px 22px rgba(0,0,0,.28);
  cursor: pointer;
  user-select:none;
  transform: translateY(0);
  transition: transform 120ms ease, filter 120ms ease, box-shadow 120ms ease;
  backdrop-filter: blur(8px);
}
.zc-btn::after{
  content:"";
  position:absolute;
  left: 10px;
  right: 10px;
  top: 8px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255,255,255,.22), rgba(255,255,255,0));
  opacity:.65;
  pointer-events:none;
}
.zc-btn:hover{
  transform: translateY(-1px);
  filter: brightness(1.06);
  box-shadow: 0 16px 28px rgba(0,0,0,.32);
}
.zc-btn:active{ transform: translateY(1px); filter: brightness(.98); }
.zc-btn--icon{ padding: 9px 10px; min-width: 42px; }

.zc-streakPill{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  padding: 7px 11px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 12px;
  letter-spacing: .2px;
  line-height: 1;
  color: rgba(255,255,255,0.92);
  background: linear-gradient(135deg, rgba(218,165,32,0.22), rgba(75,0,130,0.20));
  border: 1px solid rgba(218,165,32,0.22);
  box-shadow: 0 12px 24px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.10);
  white-space: nowrap;
  flex-wrap: nowrap;
}

.zc-streakPill > span{
  display: inline-block;
  white-space: nowrap;
}
.zc-streakPill__flame{ filter: drop-shadow(0 6px 12px rgba(0,0,0,0.45)); }
.zc-streakPill__label{ opacity: .92; font-weight: 900; }
.zc-streakPill__value{ opacity: .95; font-weight: 950; }
@media (max-width: 520px){
  .zc-streakPill{ padding: 6px 9px; font-size: 11px; }
}

/* Missions gamer panel */
.zc-missions{
  margin: 6px 12px 4px;
  padding: 8px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.045));
  border: 1px solid rgba(218,165,32,0.18);
  box-shadow: 0 10px 26px rgba(0,0,0,0.20);
  backdrop-filter: blur(9px);
  position: relative;
  overflow:hidden;
}
.zc-missions::before{
  content:"";
  position:absolute;
  inset:-2px;
  background:
    radial-gradient(circle at 18% -10%, rgba(218,165,32,.22), transparent 55%),
    radial-gradient(circle at 88% 120%, rgba(75,0,130,.26), transparent 62%);
  opacity:.9;
  pointer-events:none;
}
.zc-missions__top{ position:relative; display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px; }
.zc-missions__left{ display:flex; flex-direction:column; gap:2px; }
.zc-missions__titleRow{ display:flex; align-items:center; gap:8px; }
.zc-missions__title{ font-weight: 950; font-size: 11.5px; letter-spacing:.55px; text-transform: uppercase; }

/* ⭐ titila cada 3–4s */
.zc-missions__star{ display:inline-block; transform-origin: 50% 60%; opacity:.78; filter: drop-shadow(0 3px 12px rgba(218,165,32,.35)); }
@keyframes zcStarTwinkle{
  0%, 78% { opacity: .60; transform: scale(1); }
  84% { opacity: 1; transform: scale(1.06); }
  90% { opacity: .72; transform: scale(1.02); }
  100% { opacity: .60; transform: scale(1); }
}
@media (prefers-reduced-motion: no-preference){
  .zc-missions__star{ animation: zcStarTwinkle 3.6s ease-in-out infinite; }
}

.zc-missions__hint{ opacity:.80; font-size:10px; font-weight:750; }
.zc-missions__right{ display:flex; align-items:center; gap:8px; }
.zc-missions__toggle{
  min-width: 44px;
  min-height: 36px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:5px;
  padding: 7px 9px;
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 999px;
  background: rgba(0,0,0,.16);
  color: rgba(255,255,255,.92);
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
  touch-action: manipulation;
}
.zc-missions__toggle:focus-visible{
  outline: 2px solid rgba(255,215,0,.92);
  outline-offset: 2px;
}
.zc-missions__summary{
  position: relative;
  display:flex;
  align-items:center;
  gap:6px;
  min-height: 24px;
  margin: 1px 2px 0;
  color: rgba(255,255,255,.82);
  font-size: 10.5px;
  font-weight: 850;
}

.zc-missions__list{ position:relative; display:flex; flex-direction:column; gap:8px; }
.zc-missions__list[hidden]{ display:none !important; }
.zc-missions:not(.is-expanded) .zc-missions__hint{ display:none; }

/* Board-first gameplay flow: secondary panels stay reachable without delaying the board. */
.zc-content{
  display:flex;
  flex-direction:column;
  gap:0;
  min-height: calc(100dvh - 28px);
}
.zc-content > *{ order: 10; }
.zc-content > .zc-topbar{ order: 1; }
.zc-content > .zc-missions{ order: 2; }
.zc-content > .zc-hud{ order: 3; }
.zc-content > .zc-goalsWrap{ order: 4; }
.zc-content > .zc-boardWrap{ order: 5; }
.zc-content > .zc-legendWrap{ order: 6; }

.zc-hud__chip--boosters{ gap: 4px; }
.zc-hud__boostersLabel{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  width:100%;
  min-width:0;
}
.zc-hud__boosterControls{
  display:flex;
  align-items:center;
  justify-content:center;
  gap:4px;
  width:100%;
}
.zc-hudBoosterButton{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:2px;
  min-width:44px;
  min-height:44px;
  padding:0 5px;
  border:1px solid rgba(255,215,0,.24);
  border-radius:12px;
  background:linear-gradient(135deg,#FFD700,#FFB700);
  color:#4B0082;
  font-size:15px;
  font-weight:900;
  line-height:1;
  cursor:pointer;
  touch-action:manipulation;
}
.zc-hudBoosterButton:disabled{
  background:rgba(255,255,255,.08);
  border-color:rgba(255,255,255,.10);
  color:#999;
  opacity:.55;
  cursor:not-allowed;
}
.zc-hudBoosterButton:focus-visible{
  outline:2px solid rgba(255,215,0,.92);
  outline-offset:2px;
}
.zc-goalsCard{
  width: min(560px, 92vw);
  padding: 8px 10px !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 18px rgba(0,0,0,.16) !important;
}
.zc-goalsList{
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 6px !important;
}
.zc-goalsList > div{
  min-width: 0;
  padding: 6px 8px !important;
  border-radius: 10px !important;
}

@media (max-width: 520px){
  .zc-content{ min-height: calc(100dvh - 18px); }
  .zc-content > .zc-topbar{ margin-bottom: 3px; }
  .zc-content > .zc-hud{ margin-top: 4px; margin-bottom: 4px; }
  .zc-content > .zc-goalsWrap{ margin: 2px auto 4px !important; }
  .zc-content > .zc-boardWrap{ margin-top: 2px !important; }
  .zc-content > .zc-missions{ margin-top: 5px; }
  .zc-goalsCard{ width: min(560px, 96vw); }

  /* Active-play HUD: score, level and moves in one compact row. */
  .zc-hud{
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(104px, 1.35fr);
    width: min(560px, 96vw);
    gap: 5px;
    padding: 4px;
    margin-top: 3px;
    margin-bottom: 3px;
  }
  .zc-hud__chip{ min-height: 46px; padding: 7px 4px; }
  .zc-hud__chip:nth-child(2) .zc-xpDetailBar,
  .zc-hud__chip:nth-child(2) .zc-xpDetailValue,
  .zc-hud__chip:nth-child(2) [aria-label="Level unlocks"]{ display:none !important; }
  .zc-hud__chip:nth-child(2) .zc-hud__value{ gap: 0 !important; }
}

.zc-mission{
  position: relative;
  padding: 8px 10px;
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.10);
  background: linear-gradient(135deg, rgba(75,0,130,0.18), rgba(0,0,0,0.16));
  overflow:hidden;
}
/* Element tint per mission (premium) */
.zc-mission[data-element="gold"]{
  border-color: rgba(218,165,32,0.22);
  background: linear-gradient(135deg, rgba(218,165,32,0.10), rgba(75,0,130,0.18), rgba(0,0,0,0.16));
}
.zc-mission[data-element="violet"]{
  border-color: rgba(180,150,255,0.22);
  background: linear-gradient(135deg, rgba(75,0,130,0.28), rgba(180,150,255,0.10), rgba(0,0,0,0.16));
}
.zc-mission[data-element="air"]{
  border-color: rgba(190,235,255,0.22);
  background: linear-gradient(135deg, rgba(190,235,255,0.12), rgba(75,0,130,0.16), rgba(0,0,0,0.16));
}
.zc-mission[data-element="earth"]{
  border-color: rgba(255,210,140,0.22);
  background: linear-gradient(135deg, rgba(255,210,140,0.12), rgba(75,0,130,0.14), rgba(0,0,0,0.16));
}

/* Element icon pill */
.zc-elIcon{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  font-size: 14px;
  line-height: 1;
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 10px 18px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.12);
  backdrop-filter: blur(8px);
}
.zc-elIcon[data-el="gold"]{ border-color: rgba(218,165,32,0.30); }
.zc-elIcon[data-el="violet"]{ border-color: rgba(180,150,255,0.30); }
.zc-elIcon[data-el="air"]{ border-color: rgba(190,235,255,0.30); }
.zc-elIcon[data-el="earth"]{ border-color: rgba(255,210,140,0.30); }

.zc-mission::before{
  content:"";
  position:absolute;
  inset:-2px;
  background:
    radial-gradient(circle at 20% 0%, rgba(218,165,32,0.18), transparent 56%),
    radial-gradient(circle at 90% 120%, rgba(75,0,130,0.22), transparent 60%);
  opacity:.9;
  pointer-events:none;
}
.zc-mission__row{ position:relative; display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
.zc-mission__name{
  font-weight: 900;
  letter-spacing:.2px;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.zc-mission__sub{ opacity:.84; font-size: 10.75px; margin-top:2px; }
 .zc-mission__right{ display:flex; align-items:center; gap:8px; justify-content:flex-end; flex-wrap:wrap; }

/* Moves as crystals (premium) — compact HUD */
.zc-crystalHud{
  display:flex;
  align-items:center;
  gap:10px;
  min-width: 0;
}

/* ===== Crystal HUD compact (final) ===== */
.zc-crystalCompact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 13px;
  letter-spacing: 0.3px;

  background: linear-gradient(
    180deg,
    rgba(255,255,255,0.12),
    rgba(0,0,0,0.22)
  );
  border: 1px solid rgba(255,215,0,0.22);

  box-shadow:
    0 12px 24px rgba(0,0,0,0.32),
    inset 0 1px 0 rgba(255,255,255,0.12);

  white-space: nowrap;
}

.zc-crystalHudIcon{
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size: 16px;
  line-height: 1;
  background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(0,0,0,0.22));
  border: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 12px 22px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.12);
  backdrop-filter: blur(8px);
  flex: 0 0 auto;
}

.zc-crystalCount{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 950;
  letter-spacing: .2px;
  font-size: 14px;
  background: rgba(0,0,0,0.28);
  border: 1px solid rgba(255,255,255,0.16);
  box-shadow: 0 14px 26px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.10);
  width: fit-content;
  white-space: nowrap;
}

.zc-crystalCount .zc-crystalSmall{
  font-size: 14px;
  opacity: .95;
  filter: drop-shadow(0 8px 14px rgba(0,0,0,.25));
}

/* XP sticker (relieve + sombra) */
.zc-xpSticker{
  position: relative;
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding: 6px 9px;
  border-radius: 13px;
  font-size: 11.5px;
  font-weight: 900;
  letter-spacing:.2px;
  border: 1px solid rgba(218,165,32,0.42);
  background: linear-gradient(180deg, rgba(218,165,32,0.34), rgba(0,0,0,0.10));
  box-shadow:
    0 10px 18px rgba(0,0,0,0.28),
    inset 0 1px 0 rgba(255,255,255,0.24),
    inset 0 -1px 0 rgba(0,0,0,0.20);
  transform: translateZ(0);
}
.zc-xpSticker::after{
  content:"";
  position:absolute;
  left: 8px;
  right: 8px;
  top: 6px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255,255,255,.28), rgba(255,255,255,0));
  opacity:.65;
  pointer-events:none;
}

/* Progress bar candy: glossy border + shine sweep + smooth fill */
.zc-bar{
  position:relative;
  margin-top:10px;
}
.zc-barTrack{
  height: 8px;
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  overflow:hidden;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.10);
}
.zc-barFill{
  height: 100%;
  border-radius: 999px;
  width: 0%;
  background: linear-gradient(90deg, rgba(218,165,32,0.90), rgba(75,0,130,0.86));
  position: relative;
  box-shadow: 0 12px 24px rgba(0,0,0,0.25);
  transition: width 520ms cubic-bezier(.2,.9,.25,1);
  will-change: width;
}
.zc-barFill::before{
  /* glossy highlight (top light, bottom dark) */
  content:"";
  position:absolute;
  inset:0;
  background: linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0.0) 55%, rgba(0,0,0,0.14));
  opacity:.75;
}
.zc-barFill::after{
  /* candy stripes */
  content:"";
  position:absolute;
  inset:0;
  background:
    repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.28) 0px,
      rgba(255,255,255,0.28) 10px,
      rgba(255,255,255,0.08) 10px,
      rgba(255,255,255,0.08) 20px
    );
  opacity: .50;
}

/* shine sweep cada 4–6s (solo no reduce-motion) */
.zc-shine{
  position:absolute;
  top: -18px;
  left: -35%;
  width: 35%;
  height: 60px;
  transform: skewX(-18deg);
  background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.18), rgba(255,255,255,0));
  opacity: .0;
  pointer-events:none;
}
@keyframes zcShineSweep{
  0%{ left:-35%; opacity:0; }
  8%{ opacity:.85; }
  20%{ opacity:0; }
  100%{ left:110%; opacity:0; }
}
@media (prefers-reduced-motion: no-preference){
  .zc-mission:not(.is-done) .zc-shine{ animation: zcShineSweep 5.2s ease-in-out infinite; }
}

/* Done: check animation + sticker bounce */
.zc-mission.is-done{ border-color: rgba(218,165,32,0.35); }
@keyframes zcStickerBounce{
  0%{ transform: scale(1); }
  40%{ transform: scale(1.08); }
  70%{ transform: scale(0.98); }
  100%{ transform: scale(1); }
}
.zc-mission.is-done .zc-xpSticker{ animation: zcStickerBounce 520ms cubic-bezier(.2,.9,.25,1); }

/* Check svg stroke */
.zc-check{
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  filter: drop-shadow(0 6px 14px rgba(0,0,0,.25));
}
.zc-check path{
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
}
@keyframes zcDraw{
  to{ stroke-dashoffset: 0; }
}
.zc-mission.is-done .zc-check path{ animation: zcDraw 520ms ease-out forwards; }

/* mini confetti: 6–8 dots */
.zc-confetti{ position: relative; width: 22px; height: 22px; }
.zc-dot{
  position:absolute;
  width: 5px;
  height: 5px;
  border-radius: 999px;
  opacity: 0;
}
@keyframes zcPopDots{
  0%{ transform: translate(0,0) scale(0.7); opacity:0; }
  15%{ opacity:1; }
  100%{ transform: translate(var(--dx), var(--dy)) scale(1); opacity:0; }
}
.zc-mission.is-done .zc-dot{ animation: zcPopDots 680ms ease-out; }

@media (max-width: 520px){
  .zc-topbar{
    margin: 10px 10px 8px;
    padding: calc(10px + var(--zc-safe-top)) 10px 10px;
    border-radius: 18px;
  }

  /* Missions: minimal on mobile (board is the hero) */
  .zc-missions{
    margin: 6px 10px 6px;
    padding: 4px 6px;
    border-radius: 14px;
    max-width: 620px;
    /* IMPORTANT: no scroll on missions so users always see the 3 objetivos */
    max-height: none;
    overflow: visible;
  }

  /* Keep the header ultra-compact */
  .zc-missions__top{ margin-bottom: 5px; }
  .zc-missions__hint{ display:none; }
  .zc-missions__title{ font-size: 9.5px; }
  .zc-missions__star{ font-size: 11px; opacity: .75; }

  /* List tighter */
  .zc-missions__list{ gap: 5px; }

  /* Mission cards: ultra-compact row */
  .zc-mission{ padding: 5px 7px; border-radius: 12px; }
  .zc-mission__row{ flex-direction: row; align-items: flex-start; }
  .zc-mission__name{ font-size: 11px; line-height: 1.05; }
  .zc-mission__sub{
    display:block;
    opacity:.90;
    font-size: 10.5px;
    margin-top: 2px;
    line-height: 1.22;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  /* Element icon smaller */
  .zc-elIcon{ width: 20px; height: 20px; font-size: 11px; }

  /* Right side compact */
  .zc-mission__right{ justify-content:flex-end; gap: 6px; }
  .zc-xpSticker{ padding: 4px 6px; font-size: 9.75px; border-radius: 11px; }

  /* Progress bar smaller (still visible) */
  .zc-bar{ margin-top: 6px; }
  .zc-barTrack{ height: 5px; }
}

/* HUD chips */
/* HUD chips — four compact gameplay tiles */
.zc-hud{
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 10px 12px 6px;
  align-items: stretch;
  will-change: transform;
  filter: drop-shadow(0 0 12px var(--zc-world-glow, rgba(218,165,32,.24)));
}

.zc-hud__chip{
  min-width: 0;
  border-radius: 16px;
  padding: 10px 12px;
  background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(0,0,0,0.14));
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 14px 30px rgba(0,0,0,0.26);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 58px;
  border-top-color: var(--zc-world-primary, rgba(255,255,255,0.12));
}

@media (max-width: 640px){
  /* Keep HUD compact: four tiles in one row on mobile */
  .zc-hud{
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(104px, 1.35fr);
    gap: 8px;
    margin: 6px 10px 6px;
  }

  .zc-hud__chip{
    padding: 8px 10px;
    border-radius: 14px;
    min-height: 44px;
  }

  .zc-hud__label{ font-size: 9.5px; letter-spacing: .25px; }
  .zc-hud__value{
    margin-top: 4px;
    gap: 4px;
    font-size: 14px;
  }

  .zc-hud__value--row{ gap: 6px; }
}
.zc-hud__label{ position:relative; z-index:1; opacity:.86; font-size:11px; font-weight:850; letter-spacing:.3px; text-transform:uppercase; }
.zc-hud__value{
  position:relative;
  z-index:1;
  display:flex;
  flex-direction:column;
  gap: 6px;
  margin-top:6px;
  font-size:18px;
  font-weight:950;
  letter-spacing:.2px;
  min-width: 0;
}

/* When a HUD value needs to be in one line (e.g., 💎 20/20) */
.zc-hud__value--row{
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
}

.zc-hud__chip.is-busy{ filter: saturate(1.05) brightness(1.02); }

/* Root background */
.zc-bg{
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at var(--zc-world-position, 20% 10%), var(--zc-world-bg-a, rgba(218,165,32,0.16)), transparent 42%),
    radial-gradient(circle at 75% 25%, var(--zc-world-bg-b, rgba(75,0,130,0.48)), transparent 55%),
    radial-gradient(circle at 55% 80%, var(--zc-world-glow, rgba(218,165,32,0.10)), transparent 50%),
    linear-gradient(180deg, rgba(7,0,16,1), rgba(12,0,26,1));
  opacity: 1;
}

.zc-bg::after{
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: .52;
  background:
    radial-gradient(circle at 28% 34%, var(--zc-world-primary, #daa520) 0 1px, transparent 2px),
    radial-gradient(circle at 72% 62%, var(--zc-world-secondary, #4b0082) 0 1px, transparent 2px);
  background-size: 170px 170px, 230px 230px;
  animation: zcWorldAtmosphereDrift 18s ease-in-out infinite alternate;
}

@keyframes zcWorldAtmosphereDrift{
  from{ transform: translate3d(-3px, 0, 0); opacity: .36; }
  to{ transform: translate3d(3px, -4px, 0); opacity: .58; }
}

@media (prefers-reduced-motion: reduce){
  .zc-bg::after{ animation: none; opacity: .34; }
}

/* Ensure overlays/FX never block taps */
.zc-bg,
.zc-flash,
.zc-toast,
.zc-particles,
.zc-p{
  pointer-events: none !important;
}

/* iOS performance: reduce heavy blur/shadows on small screens */
@media (max-width: 520px){
  .zc-topbar,
  .zc-missions,
  .zc-hud__chip{
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: 0 10px 22px rgba(0,0,0,.22);
  }
}

/* Faster tap response on mobile */
.zc-boardWrap, .zc-board, .zc-tile{
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Board wrap feedback */
.zc-boardWrap{ will-change: transform; }
@keyframes zcShakeX{
  0%,100%{ transform: translateX(0); }
  20%{ transform: translateX(-6px); }
  40%{ transform: translateX(6px); }
  60%{ transform: translateX(-4px); }
  80%{ transform: translateX(4px); }
}
@keyframes zcShakeY{
  0%,100%{ transform: translateY(0); }
  20%{ transform: translateY(-6px); }
  40%{ transform: translateY(6px); }
  60%{ transform: translateY(-4px); }
  80%{ transform: translateY(4px); }
}
.zc-shake-x{ animation: zcShakeX 260ms ease-out; }
.zc-shake-y{ animation: zcShakeY 260ms ease-out; }
@keyframes zcPunch{
  0%{ transform: scale(1); }
  40%{ transform: scale(0.985); }
  100%{ transform: scale(1); }
}
.zc-punch{ animation: zcPunch 190ms ease-out; }

/* Small UI animations used in overlays */
@keyframes zodiacFlash{
  0%{ opacity: 0; }
  20%{ opacity: 1; }
  100%{ opacity: 0; }
}
@keyframes scorePop{
  0%{ transform: translateX(-50%) translateY(8px) scale(0.95); opacity: 0; }
  25%{ transform: translateX(-50%) translateY(0px) scale(1.02); opacity: 1; }
  100%{ transform: translateX(-50%) translateY(-14px) scale(1.0); opacity: 0; }
}
@keyframes zcXpFly{
  0%{ transform: translateX(-50%) translateY(10px) scale(0.96); opacity: 0; }
  18%{ transform: translateX(-50%) translateY(0px) scale(1.02); opacity: 1; }
  100%{ transform: translateX(-50%) translateY(-18px) scale(1.0); opacity: 0; }
}
@keyframes zcMultPop{
  0%{ transform: translateX(-50%) translateY(10px) scale(0.96); opacity: 0; }
  22%{ transform: translateX(-50%) translateY(0px) scale(1.02); opacity: 1; }
  100%{ transform: translateX(-50%) translateY(-16px) scale(1.0); opacity: 0; }
}
@keyframes zcHelpOverlayIn{ from{ opacity: 0; } to{ opacity: 1; } }
@keyframes zcHelpCardIn{ from{ transform: translateY(10px) scale(0.98); opacity: 0; } to{ transform: translateY(0) scale(1); opacity: 1; } }
@keyframes zcEndOverlayIn{ from{ opacity: 0; } to{ opacity: 1; } }
@keyframes zcEndCardIn{ from{ transform: translateY(10px) scale(0.985); opacity: 0; } to{ transform: translateY(0) scale(1); opacity: 1; } }

/* Particle base (keeps your existing per-kind classes safe even if missing) */
.zc-p{ border-radius: 999px; background: radial-gradient(circle, var(--c1), var(--c2)); opacity: .95; filter: drop-shadow(0 10px 22px rgba(0,0,0,0.35)); animation: zcParticleFly 900ms ease-out forwards; }
@keyframes zcParticleFly{
  0%{ transform: translate(-50%,-50%) rotate(0deg) scale(0.6); opacity: 0; }
  15%{ opacity: .95; }
  100%{ transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(90deg) scale(1); opacity: 0; }
}

 /* =========================
   Level Win: premium banner
   ========================= */
.zc-win-banner {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  pointer-events: none;
}

.zc-win-banner__card {
  width: min(560px, calc(100vw - 28px));
  border-radius: 22px;
  padding: 18px 18px 16px;
  background: linear-gradient(180deg, rgba(18, 9, 32, 0.92), rgba(9, 7, 16, 0.90));
  border: 1px solid rgba(218, 165, 32, 0.34);
  box-shadow:
    0 18px 60px rgba(0,0,0,0.55),
    inset 0 0 0 1px rgba(255,255,255,0.06);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transform-origin: center;
  animation: zcWinPop 520ms cubic-bezier(.2,.9,.2,1) both;
}

.zc-win-banner__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.zc-win-banner__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(218,165,32,0.14);
  border: 1px solid rgba(218,165,32,0.28);
  color: rgba(255,255,255,0.92);
  font-weight: 700;
  letter-spacing: 0.2px;
  font-size: 13px;
}

.zc-win-banner__title {
  margin: 10px 0 6px;
  font-size: 20px;
  line-height: 1.1;
  font-weight: 900;
  color: rgba(255,255,255,0.96);
}

.zc-win-banner__sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: rgba(255,255,255,0.72);
}

.zc-win-banner__stars {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  margin-top: 10px;
}

.zc-win-banner__star {
  filter: drop-shadow(0 6px 14px rgba(218,165,32,0.25));
  animation: zcStarPing 680ms ease both;
}
.zc-win-banner__star:nth-child(2) { animation-delay: 90ms; }
.zc-win-banner__star:nth-child(3) { animation-delay: 180ms; }

@keyframes zcWinPop {
  0% { opacity: 0; transform: translateY(12px) scale(0.92); }
  60% { opacity: 1; transform: translateY(0) scale(1.02); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes zcStarPing {
  0% { transform: translateY(6px) scale(0.4); opacity: 0; }
  70% { transform: translateY(0) scale(1.08); opacity: 1; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}



.zc-confetti {
  position: absolute;
  top: -16px;
  opacity: 0.95;
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(218,165,32,1), rgba(75,0,130,0.95));
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  animation-name: zcConfettiFall;
  animation-timing-function: cubic-bezier(.1,.8,.2,1);
  animation-fill-mode: both;
  will-change: transform, opacity;
}


`;

const ZcCheckAndConfetti = ({ color = "rgba(120,255,200,1)" }) => {
  // 8 dots, deterministic positions
  const dots = [
    { dx: -14, dy: -10 }, { dx: 14, dy: -12 }, { dx: -16, dy: 8 }, { dx: 16, dy: 10 },
    { dx: -4, dy: -16 }, { dx: 6, dy: -16 }, { dx: -8, dy: 16 }, { dx: 10, dy: 16 },
  ];

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <svg className="zc-check" viewBox="0 0 24 24" aria-hidden>
        <path
          d="M20 6 L9 17 L4 12"
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="zc-mini-confetti" aria-hidden>
        {dots.map((d, i) => (
          <span
            key={i}
            className="zc-mini-dot"
            style={{
              left: 9,
              top: 9,
              background: i % 2 === 0 ? "rgba(218,165,32,.95)" : "rgba(75,0,130,.95)",
              "--dx": `${d.dx}px`,
              "--dy": `${d.dy}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
};



const MissionsPanelGamer = ({
  missions,
  missionPulse = {},
  streak = 0,
  // allow override from parent, but default to centralized helpers
  getMissionLabel = getMissionLabelFn,
  getMissionProgressText = getMissionProgressTextFn,
}) => {
  // Presentation-only state: mission progress and completion remain authoritative
  // in the existing mission data; this only controls how much of the panel is shown.
  const [expanded, setExpanded] = useState(false);
  // Icon + tint for each mission type (so they don't all look the same)
  const missionStyleForType = (typeRaw) => {
    const type = String(typeRaw || '').toLowerCase();

    // Element tint (for premium card color) — reuse existing CSS buckets
    // gold = fire-ish / default premium
    // violet = cosmic / specials
    // air = neutral/any/utility
    // earth = heavy/score/breakers
    let element = 'gold';

    // Icon shown at the left of the mission
    let icon = '🔥';

    // ----- Clear missions -----
    if (type.includes('clear_element_fire') || type.includes('fire')) {
      element = 'gold';
      icon = '🔥';
      return { element, icon };
    }
    if (type.includes('clear_element_water') || type.includes('water')) {
      element = 'violet';
      icon = '💧';
      return { element, icon };
    }
    if (type.includes('clear_element_air') || type.includes('air')) {
      element = 'air';
      icon = '🌬️';
      return { element, icon };
    }
    if (type.includes('clear_element_earth') || type.includes('earth')) {
      element = 'earth';
      icon = '🪨';
      return { element, icon };
    }

// Generic clear tiles (no element specified)
if (
  type.includes('clear') ||
  type.includes('tiles') ||
  type.includes('cells') ||
  type.includes('fichas') ||
  type.includes('limpia') ||
  type.includes('limpiá')
) {
  element = 'air';
  icon = '✨';
  return { element, icon };
}

    if (type.includes('clear_element_any') || type.includes('clear_any') || type.includes('clear element any')) {
      element = 'air';
      icon = '✨';
      return { element, icon };
    }

    // ----- Score / moves -----
    if (type.includes('score') || type.includes('points')) {
      element = 'earth';
      icon = '🎯';
      return { element, icon };
    }

    // ----- Combos / cascades -----
    if (type.includes('combo')) {
      element = 'violet';
      icon = '⭐';
      return { element, icon };
    }
    if (type.includes('cascade')) {
      element = 'violet';
      icon = '🌊';
      return { element, icon };
    }

    // ----- Specials / explosions / stripes / stars -----
    if (type.includes('stripe') || type.includes('rayo') || type.includes('rayos')) {
      element = 'violet';
      icon = '⚡';
      return { element, icon };
    }
if (type.includes('booster') || type.includes('boost')) {
  element = 'violet';
  icon = '🎁';
  return { element, icon };
}

    if (type.includes('star') || type.includes('estrella')) {
      element = 'violet';
      icon = '🌟';
      return { element, icon };
    }
    if (type.includes('burst') || type.includes('explos')) {
      element = 'earth';
      icon = '💥';
      return { element, icon };
    }

    // Default
    return { element, icon };
  };

  const elementIcon = (missionOrEl) => {
    // Back-compat: sometimes we pass element, sometimes we want by type
    // If it looks like a mission type, style by type; otherwise keep old mapping by element.
    const s = String(missionOrEl || '');
    if (s.includes('_') || s.includes('score') || s.includes('combo') || s.includes('clear')) {
      return missionStyleForType(s).icon;
    }
    // Treat as element key
    switch (String(missionOrEl || 'gold')) {
      case 'violet':
        return '💧';
      case 'air':
        return '🌬️';
      case 'earth':
        return '🪨';
      default:
        return '🔥';
    }
  };
  // Normaliza misiones del backend -> UI gamer
  const normalizeMissionTypeKey = (raw) => {
    const s0 = String(raw || '').trim();
    if (!s0) return '';

    // Lowercase + strip accents/diacritics (Géminis -> geminis)
    let s = s0.toLowerCase();
    try {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (_) {}

    // Common separators to underscores
    s = s.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

    // A few legacy aliases
    if (s === 'reach_score') s = 'score_reach';
    if (s === 'clear_any') s = 'clear_element_any';
    if (s === 'cascade4plus') s = 'cascade_4_plus';
    if (s === 'cascade5plus') s = 'cascade_5_plus';

    // Additional aliases
    if (s === 'play_once' || s === 'play_1' || s === 'play_one') s = 'play_once';
    if (s === 'play_game' || s === 'play_match' || s === 'play_run') s = 'play_once';
    if (s === 'win_run' || s === 'win_game' || s === 'win_match') s = 'win_runs';

    return s;
  };

  const normalize = (m, idx) => {
    const rawType = String(m?.type || '').trim();
    const type = normalizeMissionTypeKey(rawType);
    const id = String(m?.id || type || rawType || `m_${idx}`);

    const target = Math.max(1, Number(m?.target ?? m?.goal ?? m?.required ?? 1) || 1);
    const rawProgress = Math.max(0, Number(m?.progress ?? m?.current ?? m?.value ?? 0) || 0);

    const xp = Math.max(0, Number(m?.reward?.xp ?? m?.xp ?? 0) || 0);
    const done = !!(m?.done || m?.completed || rawProgress >= target);
    const current = done ? Math.max(target, rawProgress) : rawProgress;

    

    const subtitleByType = {
      // ---- Daily missions (common) ----
      clear_element_any: zcText(`Limpiá ${target} ficha${target === 1 ? '' : 's'} (cualquier elemento)`, `Clear ${target} tile${target === 1 ? '' : 's'} (any element)`),
      clear_element_fire: zcText(`Limpiá ${target} ficha${target === 1 ? '' : 's'} de fuego 🔥`, `Clear ${target} fire tile${target === 1 ? '' : 's'} 🔥`),
      clear_element_water: zcText(`Limpiá ${target} ficha${target === 1 ? '' : 's'} de agua 💧`, `Clear ${target} water tile${target === 1 ? '' : 's'} 💧`),
      clear_element_air: zcText(`Limpiá ${target} ficha${target === 1 ? '' : 's'} de aire 🌬️`, `Clear ${target} air tile${target === 1 ? '' : 's'} 🌬️`),
      clear_element_earth: zcText(`Limpiá ${target} ficha${target === 1 ? '' : 's'} de tierra 🪨`, `Clear ${target} earth tile${target === 1 ? '' : 's'} 🪨`),

      combo_3: zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de 3+`, `Create ${target} 3+ combo${target === 1 ? '' : 's'}`),
      combo_4: zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de 4+`, `Create ${target} 4+ combo${target === 1 ? '' : 's'}`),
      combo_5: zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de 5+`, `Create ${target} 5+ combo${target === 1 ? '' : 's'}`),
      combos_3: zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de 3+`, `Create ${target} 3+ combo${target === 1 ? '' : 's'}`),
      combos_4: zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de 4+`, `Create ${target} 4+ combo${target === 1 ? '' : 's'}`),
      combos_5: zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de 5+`, `Create ${target} 5+ combo${target === 1 ? '' : 's'}`),

      score_in_n_moves: zcText(`Sumá ${target} puntos antes de que se terminen los movimientos`, `Score ${target} points before you run out of moves`),
      score_reach: zcText(`Alcanzá ${target} puntos en una partida`, `Reach ${target} points in one game`),
      activate_any_special: zcText(
        `Activá ${target} especial${target === 1 ? '' : 'es'}`,
        `Activate ${target} special tile${target === 1 ? '' : 's'}`
      ),

      // ---- Specials / advanced ----
      activate_stripe_via_star: zcText(`Activá ${target} rayo${target === 1 ? '' : 's'} usando ⭐`, `Activate ${target} lightning tile${target === 1 ? '' : 's'} with a ⭐`),
      activate_stripe_via_swap: zcText(`Activá ${target} rayo${target === 1 ? '' : 's'} con un swap válido`, `Activate ${target} lightning tile${target === 1 ? '' : 's'} with a valid swap`),
      activate_stripe: zcText(`Activá ${target} rayo${target === 1 ? '' : 's'} (⚡)`, `Activate ${target} lightning tile${target === 1 ? '' : 's'} (⚡)`),
      make_stripe_vertical: zcText(`Creá ${target} rayo${target === 1 ? '' : 's'} vertical${target === 1 ? '' : 'es'} (⚡)`, `Create ${target} vertical lightning tile${target === 1 ? '' : 's'} (⚡)`),
      make_stripe_horizontal: zcText(`Creá ${target} rayo${target === 1 ? '' : 's'} horizontal${target === 1 ? '' : 'es'} (⚡)`, `Create ${target} horizontal lightning tile${target === 1 ? '' : 's'} (⚡)`),

      make_star: zcText(`Creá ${target} estrella${target === 1 ? '' : 's'} (🌟)`, `Create ${target} star${target === 1 ? '' : 's'} (🌟)`),
      use_booster: zcText('Usá 1 booster (💡 Pista / 💣 Bomba)', 'Use 1 booster (💡 Hint / 💣 Bomb)'),
      burst_clear_any: zcText(`Hacé ${target} explosión${target === 1 ? '' : 'es'} (💥)`, `Create ${target} explosion${target === 1 ? '' : 's'} (💥)`),
      burst_clear_fire: zcText(`Hacé ${target} explosión${target === 1 ? '' : 'es'} de fuego (💥🔥)`, `Create ${target} fire explosion${target === 1 ? '' : 's'} (💥🔥)`),
      burst_clear_water: zcText(`Hacé ${target} explosión${target === 1 ? '' : 'es'} de agua (💥💧)`, `Create ${target} water explosion${target === 1 ? '' : 's'} (💥💧)`),
      burst_clear_air: zcText(`Hacé ${target} explosión${target === 1 ? '' : 'es'} de aire (💥🌬️)`, `Create ${target} air explosion${target === 1 ? '' : 's'} (💥🌬️)`),
      burst_clear_earth: zcText(`Hacé ${target} explosión${target === 1 ? '' : 'es'} de tierra (💥🪨)`, `Create ${target} earth explosion${target === 1 ? '' : 's'} (💥🪨)`),

      multi_element_move: zcText(`En 1 movimiento limpiá ${target} elementos distintos`, `Clear ${target} different elements in one move`),
      cascade_2p: zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'} de 2+`, `Create ${target} 2+ cascade${target === 1 ? '' : 's'}`),
      cascade_3p: zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'} de 3+`, `Create ${target} 3+ cascade${target === 1 ? '' : 's'}`),
      cascade_4_plus: zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'} de 4+`, `Create ${target} 4+ cascade${target === 1 ? '' : 's'}`),
      cascade_5_plus: zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'} de 5+`, `Create ${target} 5+ cascade${target === 1 ? '' : 's'}`),
      cascades_4_plus: zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'} de 4+`, `Create ${target} 4+ cascade${target === 1 ? '' : 's'}`),
      cascades_5_plus: zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'} de 5+`, `Create ${target} 5+ cascade${target === 1 ? '' : 's'}`),
    };

    // Centralized labels (Spanish) — uses your `zcMissionLabels` file
    // We pass a normalized mission shape so the helper can decide text cleanly.
    const missionForText = {
      ...(m && typeof m === 'object' ? m : {}),
      id,
      type,
      target,
      progress: current,
      done,
      reward: { xp },
    };

    let title = zcText('Misión', 'Mission');
    // Subtitle is a human description (NOT the numeric progress)
    let subtitle = subtitleByType[type] || '';
    // Progress text is rendered on the right (avoid duplicating 0/40 twice)
    let progressText = done ? `${target}/${target}` : `${Math.min(current, target)}/${target}`;

    // --- Title cleanup: avoid duplicating the left icon inside the title ---
    const cleanMissionTitle = (rawTitle, leftIcon) => {
      let t = String(rawTitle || '').trim();

      // Remove common trailing emojis/symbols (the left icon already represents it)
      // Examples: "... ✨" "... ⭐" "... 🎯" etc.
      t = t.replace(/\s+(?:[\u2600-\u27BF]|[\u{1F300}-\u{1FAFF}])+$/u, '').trim();

      // Also remove the exact leftIcon if it still ends the string
      const icon = String(leftIcon || '').trim();
      if (icon && t.endsWith(icon)) {
        t = t.slice(0, -icon.length).trim();
      }

      // Collapse double spaces
      t = t.replace(/\s{2,}/g, ' ').trim();
      return t || zcText('Misión', 'Mission');
    };

    try {
      const t = typeof getMissionLabel === 'function' ? getMissionLabel(missionForText) : '';
      if (t) title = String(t);
    } catch (e) {}

    try {
      const s = typeof getMissionProgressText === 'function' ? getMissionProgressText(missionForText) : '';
      if (s) progressText = done ? `${target}/${target}` : String(s);
    } catch (e) {}

const humanizeType = (tRaw) => {
      const t = String(tRaw || '').trim();
      if (!t) return zcText('Objetivo', 'Objective');
      const spaced = t
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      // Capitalize first letter only (keeps acronyms readable)
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };

    // If we still have no subtitle, fall back to a specific, readable objective.
    // IMPORTANT: never show generic "Cumplí el objetivo..." because users don't know what to do.
    if (!subtitle) {
      // Prefer targeted fallbacks by keyword (still clear), otherwise humanize the type.
      if (type.includes('combo')) subtitle = zcText(`Hacé ${target} combo${target === 1 ? '' : 's'} de ${type.includes('5') ? '5+' : type.includes('4') ? '4+' : '3+'}`, `Create ${target} ${type.includes('5') ? '5+' : type.includes('4') ? '4+' : '3+'} combo${target === 1 ? '' : 's'}`);
      else if (type.includes('cascade') || type.includes('cascad')) subtitle = zcText(`Lográ ${target} cascada${target === 1 ? '' : 's'}`, `Create ${target} cascade${target === 1 ? '' : 's'}`);
      else if (type.includes('score')) subtitle = zcText(`Alcanzá ${target} puntos en una partida`, `Reach ${target} points in one game`);
      else if (type.includes('clear')) subtitle = zcText(`Limpiá ${target} ficha${target === 1 ? '' : 's'}`, `Clear ${target} tile${target === 1 ? '' : 's'}`);
      else if (type.includes('play') || type.includes('run') || type.includes('match') || type.includes('game')) subtitle = zcText(`Jugá ${target} partida${target === 1 ? '' : 's'}`, `Play ${target} game${target === 1 ? '' : 's'}`);
      else subtitle = `${zcText('Objetivo', 'Objective')}: ${humanizeType(type)}`;
    }

    // If title is still generic, make it specific and readable.
    if (!title || String(title).trim().toLowerCase() === 'misión' || String(title).trim() === 'Misión') {
      title = humanizeType(type) || zcText('Misión', 'Mission');
    }

    // Element style and icon for check color and icon
    const style = missionStyleForType(type);
    const element = style.element;
    const icon = style.icon;

    // Ensure the mission title doesn't repeat the icon/emojis at the end
    title = cleanMissionTitle(title, icon);

    return { id, title, subtitle, progressText, current, target, xp, completed: done, element, icon, type };
  };

  const items = Array.isArray(missions) && missions.length
    ? missions.map(normalize)
    : [
        { id: 'm1', title: zcText('Alcanzá score 🎯', 'Reach a score 🎯'), subtitle: zcText('Alcanzá 2000 puntos', 'Reach 2,000 points'), current: 0, target: 2000, xp: 120, completed: false, element: 'gold' },
        { id: 'm2', title: zcText('Hacé combos 🔥', 'Create combos 🔥'), subtitle: zcText('Lográ 1 combo', 'Create 1 combo'), current: 0, target: 1, xp: 80, completed: false, element: 'violet' },
      ];

  const completedCount = items.filter((item) => item.completed || item.done || Number(item.current || 0) >= Number(item.target || 1)).length;
  const totalXp = items.reduce((sum, item) => sum + Math.max(0, Number(item.xp || 0) || 0), 0);
  const missionsListId = 'zc-missions-list';

  return (
    <section className={`zc-missions ${expanded ? 'is-expanded' : ''}`} aria-label={zcText('Misiones de hoy', "Today's missions")}>
      <div className="zc-missions__top">
  <div className="zc-missions__left">
    <div className="zc-missions__titleRow">
      <span className="zc-missions__star" aria-hidden>⭐</span>
      <div className="zc-missions__title">{zcText('Misiones de hoy', "Today's missions")}</div>
    </div>
    <div className="zc-missions__hint">{zcText('Completá misiones para ganar XP y badges', 'Complete missions to earn XP and badges')}</div>
  </div>

  <div className="zc-missions__right">
    {Number(streak || 0) > 0 ? (
      <div
        className="zc-streakPill"
        aria-label={zcText(`Racha diaria ${Number(streak)} día${Number(streak) === 1 ? '' : 's'}`, `${Number(streak)}-day streak`)}
        title={zcText(`Racha diaria: ${Number(streak)} día${Number(streak) === 1 ? '' : 's'} (completá 3/3 para mantenerla)`, `Daily streak: ${Number(streak)} day${Number(streak) === 1 ? '' : 's'} (complete 3/3 to keep it)`)}
      >
        <span className="zc-streakPill__flame" aria-hidden>🔥</span>
        <span className="zc-streakPill__label">{zcText('Racha', 'Streak')}</span>
        <span className="zc-streakPill__value">{Number(streak)}d</span>
      </div>
    ) : null}
    <button
      type="button"
      className="zc-missions__toggle"
      aria-expanded={expanded}
      aria-controls={missionsListId}
      onClick={() => setExpanded((value) => !value)}
    >
      <span>{expanded ? zcText('Ocultar', 'Hide') : zcText('Ver', 'View')}</span>
      <span aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>
    </button>
  </div>
</div>

      <div className="zc-missions__summary" aria-live="polite">
        <span>{zcText(`${completedCount}/${items.length} completas`, `${completedCount}/${items.length} complete`)}</span>
        <span aria-hidden="true">·</span>
        <span>+{totalXp} XP</span>
      </div>

      <div className="zc-missions__list" id={missionsListId} hidden={!expanded}>
        {items.map((m) => {
          const safeTarget = Math.max(1, Number(m.target) || 1);
const rawCurrent = Math.max(0, Number(m.current) || 0);
const done = !!(m.completed || m.done || rawCurrent >= safeTarget);
const safeCurrent = done ? safeTarget : rawCurrent;
const pct = Math.min(100, Math.round((safeCurrent / safeTarget) * 100));
const visualProgressText = done
  ? `${safeTarget}/${safeTarget}`
  : (m.progressText || `${safeCurrent}/${safeTarget}`);
          const checkColor =
  m.element === 'violet' ? "rgba(180,150,255,1)" :
  m.element === 'air' ? "rgba(190,235,255,1)" :
  m.element === 'earth' ? "rgba(255,210,140,1)" :
  "rgba(120,255,200,1)";

          const displayTitle = (() => {
            try {
              const t = (typeof getMissionLabel === 'function')
                ? String(getMissionLabel({
                    type: m.type,
                    target: m.target,
                    progress: m.current,
                    done,
                    reward: { xp: m.xp },
                  }) || '').trim()
                : '';

              // Prefer the centralized label unless it's the generic fallback.
              const localizedMission = getLocalizedMissionLabel(m.type);
              if (localizedMission) return localizedMission;
              if (t && t !== 'Misión') return t;
              return String(m.title || t || zcText('Misión', 'Mission'));
            } catch {
              return String(getLocalizedMissionLabel(m.type) || m.title || zcText('Misión', 'Mission'));
            }
          })();

          return (
            <div
              key={m.id}
              className={
                "zc-mission " +
                (done ? "is-done" : "") +
                (missionPulse && missionPulse[m.id] ? " zc-mission-pulse" : "")
              }
              data-element={m.element || ''}
            >
              <div className="zc-mission__row">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      className="zc-elIcon"
                      data-el={m.element || 'gold'}
                      title={
                        m.type && String(m.type).includes('score')
                          ? 'Score'
                          : m.type && String(m.type).includes('combo')
                          ? 'Combo'
                          : m.type && String(m.type).includes('clear_element_any')
                          ? zcText('Limpieza', 'Clear')
                          : m.element === 'violet'
                          ? zcText('Agua', 'Water')
                          : m.element === 'air'
                          ? zcText('Aire', 'Air')
                          : m.element === 'earth'
                          ? zcText('Tierra', 'Earth')
                          : zcText('Fuego', 'Fire')
                      }
                      aria-hidden="true"
                    >
                      {m.icon || elementIcon(m.type || m.element)}
                    </span>
                    <div className="zc-mission__name">{displayTitle}</div>
                  </div>
                  <div className="zc-mission__sub">{m.subtitle || ''}</div>
                </div>

               <div className="zc-mission__right">
  <span className="zc-xpSticker" title={zcText('Recompensa', 'Reward')}>+{Number(m.xp || 0)} XP</span>
  {done ? (
    <>
      <span style={{ opacity: .92, fontWeight: 800, fontSize: 12 }} title="Progreso completo">
        {visualProgressText}
      </span>
      <ZcCheckAndConfetti color={checkColor} />
    </>
  ) : (
    <span style={{ opacity: .9, fontWeight: 800, fontSize: 12 }} title="Progreso">
      {visualProgressText}
    </span>
  )}
</div>
              </div>

              <div className="zc-bar" aria-label={`Progreso ${pct}%`}>
                <div className="zc-barTrack">
                  <div className="zc-barFill" style={{ width: `${pct}%` }}>
                    <div className="zc-shine" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};




const XpLevelChip = ({ profile, rewards = null }) => {
  const level = Math.max(1, Number(profile?.level || 1) || 1);
  const xp = Math.max(0, Number(profile?.xp || 0) || 0);

  // Backend may omit nextLevelXp on some responses; keep UI premium by deriving a safe goal.
  // Priority: explicit fields from API -> computed fallback.
  const rawNext =
    Number(profile?.nextLevelXp || 0) ||
    Number(profile?.xpToNext || 0) ||
    Number(profile?.nextXp || 0) ||
    Number(profile?.goalXp || 0) ||
    0;

  // Fallback curve — mirrors backend xpNeededForLevel: Math.floor(60 + (L-1)*28 + (L-1)^2*4)
  // This DOES NOT change backend leveling logic; it's only a UI goal display.
  const computedNext = Math.floor(60 + (level - 1) * 28 + Math.pow(level - 1, 2) * 4);

  // If API gave a *delta to next* instead of a total, convert it to a total.
  const nextTotal = rawNext > 0 && rawNext < xp ? xp + rawNext : (rawNext > 0 ? rawNext : computedNext);

  // Prevent weird states
  const safeNext = Math.max(1, nextTotal);
  const clampedXp = Math.min(xp, safeNext);
  const pct = Math.min(100, Math.round((clampedXp / safeNext) * 100));

  const title = `Nivel ${level} · ${xp.toLocaleString('es-AR')}/${safeNext.toLocaleString('es-AR')} XP`;

  const isHudNarrow =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 640px)').matches;

const rewardList = Array.isArray(rewards) && rewards.length
  ? rewards
  : (Array.isArray(profile?.rewardsUnlocked) ? profile.rewardsUnlocked : []);

const topRewards = rewardList.slice(0, 3);
const extraCount = Math.max(0, rewardList.length - topRewards.length);

const rewardsTooltip = rewardList && rewardList.length
  ? rewardList
      .map((r) => {
        const lvl = r?.level != null ? `Lv ${r.level}: ` : '';
        const t = String(r?.title || r?.id || 'Desbloqueo');
        const d = r?.desc ? ` — ${String(r.desc)}` : '';
        return `${lvl}${t}${d}`;
      })
      .join('\n')
  : '';

  return (
    <div className="zc-hud__chip" aria-label={zcText('Nivel y experiencia', 'Level and experience')} title={title}>
      <div className="zc-hud__label">{zcText('Nivel', 'Level')}</div>

      <div className="zc-hud__value" style={{ gap: 6 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: isHudNarrow ? 6 : 10,
            rowGap: 2,
            minWidth: 0,
            width: '100%',
            flexWrap: isHudNarrow ? 'wrap' : 'nowrap',
          }}
        >
          <span
            style={{
              fontWeight: 950,
              fontSize: isHudNarrow ? 16 : 18,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            🌟 {level}
          </span>

          <span
  style={{
    opacity: 0.9,
    fontWeight: 850,
    fontSize: isHudNarrow ? 11 : 12,
    minWidth: 0,
    maxWidth: '100%',
    textAlign: 'right',
    flex: isHudNarrow ? '1 1 100%' : '0 0 auto',
    marginLeft: isHudNarrow ? 0 : 'auto',

    // 🔑 comportamiento responsive correcto
    whiteSpace: isHudNarrow ? 'normal' : 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.15,
  }}
  title={`Faltan ${(Math.max(0, safeNext - xp)).toLocaleString('es-AR')} XP para subir`}
>
  {xp.toLocaleString('es-AR')} XP
</span>
        </div>

        {/* Always render progress bar (premium). Uses safeNext fallback if API doesn't send nextLevelXp. */}
        <div className="zc-bar zc-xpDetailBar" style={{ marginTop: 2 }} aria-label={`Progreso de experiencia ${pct}%`}>
          <div className="zc-barTrack">
            <div className="zc-barFill" style={{ width: `${pct}%` }}>
              <div className="zc-shine" />
            </div>
          </div>
        </div>

        {/* Micro line: shows exact goal without clutter */}
        <div className="zc-xpDetailValue" style={{ fontSize: 10.5, opacity: 0.82, fontWeight: 800, marginTop: 2 }}>
          {xp.toLocaleString('es-AR')}/{safeNext.toLocaleString('es-AR')} XP
        </div>

        {rewardList && rewardList.length ? (
  <div
    style={{
      marginTop: 6,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    }}
    title={rewardsTooltip}
    aria-label={zcText('Desbloqueos por nivel', 'Level unlocks')}
  >
    <span style={{ fontSize: 10.5, opacity: 0.8, fontWeight: 900 }}>
      Desbloqueos
    </span>

    {topRewards.map((r, rewardIndex) => (
      <span
        key={String(r?.id || r?.title || `reward-${rewardIndex}`)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          borderRadius: 999,
          background: 'rgba(255,215,0,0.10)',
          border: '1px solid rgba(255,215,0,0.22)',
          color: '#FFD700',
          fontWeight: 950,
          fontSize: 11,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
        title={(r?.desc ? String(r.desc) : '')}
      >
        ✨ {String(r?.title || r?.id || 'Reward')}
      </span>
    ))}

    {extraCount > 0 ? (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: 999,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.14)',
          color: '#FFD700',
          fontWeight: 900,
          fontSize: 11,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        +{extraCount}
      </span>
    ) : null}
  </div>
) : null}

      </div>
    </div>
  );
};

// If a board ends up with no valid moves, we auto-reshuffle so the player can always spend all moves.
function hasAnyValidMove(board) {
  try {
    const rows = Number(board?.rows || 0);
    const cols = Number(board?.cols || 0);
    if (!rows || !cols) return true;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const a = { r, c };

        if (c + 1 < cols) {
          const b = { r, c: c + 1 };
          const res = swapWithEvents(board, a, b);
          if (res?.valid) return true;
        }

        if (r + 1 < rows) {
          const b = { r: r + 1, c };
          const res = swapWithEvents(board, a, b);
          if (res?.valid) return true;
        }
      }
    }

    return false;
  } catch (_) {
    return true;
  }
}


function createPlayableBoard(rng, maxTries = 25) {
  for (let i = 0; i < maxTries; i++) {
    const b = createInitialBoard(rng);
    if (hasAnyValidMove(b)) return b;
  }
  // fallback
  return createInitialBoard(rng);
}

const ELEMENT_FLASH = {
  fire: 'rgba(255,90,0,0.25)',
  earth: 'rgba(160,120,60,0.22)',
  air: 'rgba(180,220,255,0.22)',
  water: 'rgba(90,170,255,0.25)',
};


const ELEMENT_PARTICLE = {
  fire: { a: 'rgba(255,120,0,0.95)', b: 'rgba(255,215,0,0.85)' },
  earth: { a: 'rgba(160,120,60,0.95)', b: 'rgba(255,215,0,0.55)' },
  air: { a: 'rgba(200,240,255,0.95)', b: 'rgba(140,210,255,0.65)' },
  water: { a: 'rgba(90,170,255,0.95)', b: 'rgba(140,220,255,0.75)' },
  neutral: { a: 'rgba(255,215,0,0.90)', b: 'rgba(255,255,255,0.55)' },
};


// ✅ Compatibilidad total con clearedIds (id, "r,c", "r:c", {r,c})
// IMPORTANT: devolvemos SIEMPRE { r, c, tile } (no solo {tile}) para FX/particles + conteos robustos.
function deriveClearedCellsFromIds(board, clearedIds) {
  if (!board || !Array.isArray(clearedIds) || clearedIds.length === 0) return [];

  const rows = Number(board?.rows || 0);
  const cols = Number(board?.cols || 0);
  if (!rows || !cols) return [];

  // Caso {r,c}
  if (typeof clearedIds[0] === 'object' && clearedIds[0] !== null) {
    return clearedIds
      .map((p) => {
        const r = Number(p?.r);
        const c = Number(p?.c);
        const tile = board.grid?.[r]?.[c];
        return tile ? { r, c, tile } : null;
      })
      .filter(Boolean);
  }

  // Caso "r,c" o "r:c"
  if (typeof clearedIds[0] === 'string' && clearedIds[0].match(/\d+[,|:]\d+/)) {
    return clearedIds
      .map((s) => {
        const [r, c] = String(s).split(/[,|:]/).map(Number);
        const tile = board.grid?.[r]?.[c];
        return tile ? { r, c, tile } : null;
      })
      .filter(Boolean);
  }

  // Caso tile.id (number/string)
  const idSet = new Set(clearedIds.map((x) => String(x)));
  const found = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const t = board.grid?.[r]?.[c];
      if (t && idSet.has(String(t.id))) {
        found.push({ r, c, tile: t });
      }
    }
  }

  return found;
}

// ✅ Fallback ultra-robusto: deriva celdas limpiadas comparando el board antes y después del clear
function deriveClearedCellsByDiff(preBoard, postBoard) {
  try {
    const rows = Number(preBoard?.rows || 0);
    const cols = Number(preBoard?.cols || 0);
    if (!rows || !cols) return [];

    const out = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const pre = preBoard?.grid?.[r]?.[c];
        if (!pre) continue;
        const post = postBoard?.grid?.[r]?.[c];

        // IMPORTANT:
        // We ONLY treat a cell as "cleared" if the post-board cell is empty (null/undefined).
        // We DO NOT treat "changed id at same position" as cleared because many engines
        // apply gravity in the same state board snapshot, which would create massive false positives.
        if (!post) {
          out.push({ r, c, tile: pre });
        }
      }
    }
    return out;
  } catch (e) {
    return [];
  }
}

// ✅ Single entry-point to get cleared cells for a resolver state, supporting many shapes.
// IMPORTANT: Always returns an array of { r, c, tile } (tile is the PRE-board tile),
// and we only derive cleared cells for CLEAR phases. This prevents false “rayo” FX/SFX
// caused by diffing gravity/refill boards.
function getClearedCellsForState(preBoard, st) {
  try {
    // Only a CLEAR phase should ever produce cleared cells.
    // Gravity/refill/shuffle states may change many positions and would create false positives.
    const phase = String(st?.phase || '').toLowerCase();

const looksLikeClear =
  phase.includes('clear') ||
  st?.clearedCells?.length ||
  st?.clearedIds?.length ||
  st?.removed?.length ||
  st?.matches?.length;

if (!looksLikeClear) return [];

    const rows = Number(preBoard?.rows || 0);
    const cols = Number(preBoard?.cols || 0);

    const withTileFromPos = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return [];
      return arr
        .map((p) => {
          const r = Number(p?.r);
          const c = Number(p?.c);
          if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
          const tile = preBoard?.grid?.[r]?.[c];
          return tile ? { r, c, tile } : null;
        })
        .filter(Boolean);
    };

    const normalizeAnyClearedArray = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return [];
      const first = arr[0];

      // Already in our desired shape
      if (first && typeof first === 'object' && typeof first.r === 'number' && typeof first.c === 'number' && first.tile) {
        return arr;
      }

      // Engine shape: {r,c,id,key} (no tile)
      if (first && typeof first === 'object' && typeof first.r === 'number' && typeof first.c === 'number') {
        const byPos = withTileFromPos(arr);
        if (byPos.length) return byPos;
      }

      // Array of {id}
      if (first && typeof first === 'object' && first.id != null) {
        return deriveClearedCellsFromIds(preBoard, arr.map((t) => String(t.id)));
      }

      // Array of ids (string/number)
      if (typeof first === 'string' || typeof first === 'number') {
        return deriveClearedCellsFromIds(preBoard, arr);
      }

      return [];
    };

    // 1) native clearedCells shape (engine)
    {
      const out = normalizeAnyClearedArray(st?.clearedCells);
      if (out.length) return out;
    }

    // 2) common alternates: removed / cleared / tiles
    {
      const out = normalizeAnyClearedArray(st?.removedCells);
      if (out.length) return out;
    }

    if (Array.isArray(st?.removed) && st.removed.length) {
      const out = normalizeAnyClearedArray(st.removed);
      if (out.length) return out;

      // Extra: removed might be [{r,c}] but preBoard shape missing rows/cols — still try
      const maybePos = withTileFromPos(st.removed);
      if (maybePos.length) return maybePos;
    }

    if (Array.isArray(st?.cleared) && st.cleared.length) {
      const out = normalizeAnyClearedArray(st.cleared);
      if (out.length) return out;

      const maybePos = withTileFromPos(st.cleared);
      if (maybePos.length) return maybePos;
    }

    // 3) clearedIds (existing support)
    if ((st?.clearedIds?.length || 0) > 0) {
      const out = deriveClearedCellsFromIds(preBoard, st.clearedIds);
      if (out.length) return out;
    }

    // 4) matches/groups shapes (cells under match) — ONLY for clear phase
    // NOTE: This is a weak fallback; prefer the engine clearedCells/clearedIds.
    if (Array.isArray(st?.matches) && st.matches.length) {
      const first = st.matches[0];
      if (first && Array.isArray(first.cells)) {
        const flat = st.matches.flatMap((m) => (Array.isArray(m?.cells) ? m.cells : []));
        const out = withTileFromPos(flat);
        if (out.length) return out;
      }
      if (first && typeof first.r === 'number' && typeof first.c === 'number') {
        const out = withTileFromPos(st.matches);
        if (out.length) return out;
      }
    }

    if (Array.isArray(st?.groups) && st.groups.length) {
      const flat = st.groups.flatMap((g) => (Array.isArray(g?.cells) ? g.cells : Array.isArray(g) ? g : []));
      const out = withTileFromPos(flat);
      if (out.length) return out;
    }

    // 5) last resort: diff preBoard vs st.board (ONLY for clear phase)
    if (rows && cols && preBoard && st?.board) {
      const derived = deriveClearedCellsByDiff(preBoard, st.board);
      if (derived.length) return derived;
    }

    return [];
  } catch (e) {
    return [];
  }
}

// Normalize UI/engine element keys to backend-expected keys (fire, water, air, earth, neutral)
function normalizeElementKey(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return null;

  // UI/backwards-compat element aliases
  if (s === 'violet' || s === 'agua') return 'water';
  if (s === 'gold' || s === 'fuego') return 'fire';
  if (s === 'tierra') return 'earth';
  if (s === 'aire') return 'air';

  if (s === 'fire' || s === 'water' || s === 'air' || s === 'earth' || s === 'neutral') return s;
  return null;
}

const MISSION_LABELS = {
  // --- Básicas ---
  make_star: 'Creá estrellas 🌟',
  activate_star: 'Activá estrellas 🌟',

  activate_stripe: 'Activá rayos ⚡',
  make_stripe_horizontal: 'Creá rayo horizontal ⚡',
  make_stripe_vertical: 'Creá rayo vertical ⚡',

  score_reach: 'Alcanzá puntaje 🎯',
  reach_score: 'Alcanzá puntaje 🎯',
  score_in_n_moves: 'Puntaje con límite de movimientos ⏳',
  score_without_boosters: 'Puntaje sin boosters 🧠',
  score_no_boosters: 'Puntaje sin boosters 🧠',

  combo_3: 'Lográ combos (3+) ⭐',
  combo_4: 'Lográ combos (4+) ⭐',
  combo_5: 'Lográ combos (5+) ⭐',

  // --- Limpiezas por elemento ---
  clear_element_fire: 'Limpiá fuego 🔥',
  clear_element_water: 'Limpiá agua 💧',
  clear_element_air: 'Limpiá aire 🌬️',
  clear_element_earth: 'Limpiá tierra 🪨',
  clear_element_any: 'Limpiá cualquier elemento ✨',

  // --- Tablero (zonas) ---
  clear_center_tiles: 'Limpiá el centro 🎯',
  clear_edge_tiles: 'Limpiá los bordes 🧱',

  // --- Especiales / activaciones ---
  activate_any_special: 'Activá cualquier especial ✨',
  activate_stripe_via_star: 'Activá rayos con estrella ⚡',
  activate_stripe_via_swap: 'Activá rayos por swap ⚡',

  // --- Explosiones (burst) ---
  burst_clear_any: 'Hacé explosiones 💥',
  burst_clear_fire: 'Explosiones de fuego 💥🔥',
  burst_clear_water: 'Explosiones de agua 💥💧',
  burst_clear_air: 'Explosiones de aire 💥🌬️',
  burst_clear_earth: 'Explosiones de tierra 💥🪨',

  // --- Movimientos / cascadas ---
  multi_element_move: 'Movimiento multi-elemento 🧩',

  cascade_2p: 'Cascadas (2+) 🌊',
  cascade_3p: 'Cascadas (3+) 🌊',
  cascade_4p: 'Cascadas (4+) 🌊',
  cascade_4_plus: 'Cascadas (4+) 🌊',
  cascade_5_plus: 'Cascadas (5+) 🌊',

  // --- Uso de boosters ---
  use_booster: 'Usá un booster 🎁',
  use_booster_hint: 'Usá Pista 💡',
  use_booster_reroll: 'Usá Reordenar 🔄',

  // --- Legacy / compat ---
  play_moves: 'Jugá movimientos 🎮',
  win_runs: 'Ganás partidas 🏁',
};

const MISSION_LABELS_EN = {
  make_star: 'Create stars 🌟', activate_star: 'Activate stars 🌟', activate_stripe: 'Activate lightning tiles ⚡',
  make_stripe_horizontal: 'Create horizontal lightning ⚡', make_stripe_vertical: 'Create vertical lightning ⚡',
  score_reach: 'Reach the target score 🎯', reach_score: 'Reach the target score 🎯', score_in_n_moves: 'Score within the move limit ⏳',
  score_without_boosters: 'Score without boosters 🧠', score_no_boosters: 'Score without boosters 🧠',
  combo_3: 'Create combos (3+) ⭐', combo_4: 'Create combos (4+) ⭐', combo_5: 'Create combos (5+) ⭐',
  clear_element_fire: 'Clear fire 🔥', clear_element_water: 'Clear water 💧', clear_element_air: 'Clear air 🌬️', clear_element_earth: 'Clear earth 🪨', clear_element_any: 'Clear any element ✨',
  clear_center_tiles: 'Clear the center 🎯', clear_edge_tiles: 'Clear the edges 🧱', activate_any_special: 'Activate any special tile ✨',
  activate_stripe_via_star: 'Activate lightning with a star ⚡', activate_stripe_via_swap: 'Activate lightning with a swap ⚡',
  burst_clear_any: 'Create explosions 💥', burst_clear_fire: 'Fire explosions 💥🔥', burst_clear_water: 'Water explosions 💥💧',
  burst_clear_air: 'Air explosions 💥🌬️', burst_clear_earth: 'Earth explosions 💥🪨', multi_element_move: 'Multi-element move 🧩',
  cascade_2p: 'Cascades (2+) 🌊', cascade_3p: 'Cascades (3+) 🌊', cascade_4p: 'Cascades (4+) 🌊', cascade_4_plus: 'Cascades (4+) 🌊', cascade_5_plus: 'Cascades (5+) 🌊',
  use_booster: 'Use a booster 🎁', use_booster_hint: 'Use a hint 💡', use_booster_reroll: 'Use reshuffle 🔄', play_moves: 'Play moves 🎮', win_runs: 'Win games 🏁',
};

const getLocalizedMissionLabel = (type) => localizedValue({ es: MISSION_LABELS[type], en: MISSION_LABELS_EN[type] }, type);


const DEBUG_SOUND = false;
// IMPORTANT: keep this OFF on mobile/production; console logging inside resolve loops can cause noticeable jank.
const DEBUG_RESOLVE_LOGS = false;
// eslint-disable-next-line no-unused-vars
const DEBUG_MISSION_REPORTS = false;

const afterNextPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });

const ZC_HELP_SEEN_KEY = 'zodiacus_zodiaccrush_help_seen_v1';
const ZC_SEEN_LEVEL_REWARDS_KEY = 'zodiacus_zodiaccrush_seen_level_rewards_v1';
const ZC_BEST_SCORE_KEY = 'zodiacus_zodiaccrush_best_score_v1';

// Preserve local mission progress to avoid UI “bounce back” when the server responds with a stale snapshot.
// Today we preserve for “generic clear” missions (e.g. clear_any / clear_tiles variants) because they can be
// tracked optimistically client-side and sometimes arrive late from the backend.
function zcShouldPreserveLocalMissionProgress(typeRaw) {
  const t = String(typeRaw || '').toLowerCase().trim();
  if (!t) return false;

  // Explicit generic clear types
  if (t === 'clear_element_any' || t === 'clear_any') return true;
  if (t.includes('clear_tiles') || t.includes('clear_cells') || t.includes('clear_board')) return true;

  // ✅ Preserve client-side progress for cascades/combos/specials too.
  // Reason: backend snapshots can arrive slightly stale (or only update after run submit),
  // which makes the UI bar “sube y baja”. We prefer the MAX progress we ever rendered.
  if (
    t.includes('cascade') ||
    t.includes('cascada') ||
    t.includes('cascades')
  ) {
    return true;
  }

  if (
    t.includes('combo') ||
    t.includes('combos')
  ) {
    return true;
  }

  if (
    t.includes('activate_stripe') ||
    t.includes('stripe') ||
    t.includes('rayo') ||
    t.includes('star') ||
    t.includes('estrella')
  ) {
    return true;
  }

  // Spanish variants (sometimes encoded in type)
  const isClearLike =
    t.includes('clear') ||
    t.includes('limpia') ||
    t.includes('limpiá') ||
    t.includes('fichas') ||
    t.includes('tiles') ||
    t.includes('cells');

  const isElementSpecific =
    t.includes('clear_element_fire') ||
    t.includes('clear_element_water') ||
    t.includes('clear_element_air') ||
    t.includes('clear_element_earth') ||
    t.includes('fire') ||
    t.includes('water') ||
    t.includes('air') ||
    t.includes('earth');

  return isClearLike && !isElementSpecific;
}
// Helper to dedupe missions by type, keeping the one with highest target, and merging progress/done.
function dedupeMissionsByType(list = []) {
  const map = new Map();
  for (const m of list) {
    if (!m || !m.type) continue;
    const prev = map.get(m.type);
    if (!prev) {
      map.set(m.type, m);
    } else {
      // keep the one with higher target (more demanding)
      const prevTarget = Number(prev.target || 0);
      const nextTarget = Number(m.target || 0);
      if (nextTarget > prevTarget) {
        // carry over progress if backend split it
        map.set(m.type, {
          ...m,
          progress: Math.max(Number(prev.progress || 0), Number(m.progress || 0)),
          done: !!(prev.done || m.done),
        });
      } else {
        // keep prev but ensure progress/done are not lost
        map.set(m.type, {
          ...prev,
          progress: Math.max(Number(prev.progress || 0), Number(m.progress || 0)),
          done: !!(prev.done || m.done),
        });
      }
    }
  }
  return Array.from(map.values());
}

// Merge server missions with previous local snapshot, preferring MAX progress/target per type.
// Esto evita el efecto "la barra sube y luego baja" si el backend responde con un snapshot atrasado.
function mergeMissionsPreferMaxProgress(serverList = [], prevList = []) {
  const sList = Array.isArray(serverList) ? serverList.filter(Boolean) : [];
  const pList = Array.isArray(prevList) ? prevList.filter(Boolean) : [];

  // Index previous by type (primary) and id (secondary)
  const prevByType = new Map();
  const prevById = new Map();

  for (const m of pList) {
    const t = m?.type ? String(m.type) : '';
    const id = m?.id != null ? String(m.id) : '';
    if (t && !prevByType.has(t)) prevByType.set(t, m);
    if (id && !prevById.has(id)) prevById.set(id, m);
  }

  return sList.map((m) => {
    const type = m?.type ? String(m.type) : '';
    const id = m?.id != null ? String(m.id) : '';

    const preserveLocal = typeof zcShouldPreserveLocalMissionProgress === 'function'
      ? zcShouldPreserveLocalMissionProgress(type)
      : false;

    // Prefer matching by type; fallback to id (muchos backends mantienen m1/m2/m3)
    const prev = (type && prevByType.get(type)) || (id && prevById.get(id)) || null;

    const sProg = Number(m?.progress ?? 0) || 0;
    const pProg = Number(prev?.progress ?? 0) || 0;

    const sTarget = Number(m?.target ?? 0) || 0;
    const pTarget = Number(prev?.target ?? 0) || 0;

    const progress = preserveLocal ? Math.max(sProg, pProg) : sProg;
    const target = preserveLocal ? Math.max(sTarget, pTarget) : sTarget;

    const done = !!(m?.done || prev?.done || (target > 0 && progress >= target));

    return {
  ...(prev && typeof prev === 'object' ? prev : {}),
  ...(m && typeof m === 'object' ? m : {}),
  type: type || (prev?.type ? String(prev.type) : ''),
  id: id || (prev?.id != null ? String(prev.id) : undefined),
  target: target || (sTarget || pTarget || 0),
  progress,
  done,
  completed: done,
  current: done ? Math.max(target || 0, progress) : progress,
  progressText: done
    ? `${Math.max(target || 0, 1)}/${Math.max(target || 0, 1)}`
    : (m?.progressText || prev?.progressText),
};
  });
}

// Returns true if an "agg" progress payload has any meaningful fields (>0) to apply optimistically.
// This is NOT mission-list diffing; it's for objects like { clearedEarth: 3 } / { combo_3: 1 } etc.


// --- Updated normalizeKey ---
// eslint-disable-next-line no-unused-vars
const normalizeKey = (k) => {
  // Make key matching resilient to casing, separators, and legacy variants.
  // We normalize first, then map to our canonical progress keys (camelCase).
  let key = String(k || '').trim();
  if (!key) return '';

  // Normalize: lower-case, replace any non-alnum sequence with `_`, trim/compact `_`.
  key = key
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  switch (key) {
    // ----- SCORE -----
    case 'score_reach':
    case 'reach_score':
    case 'score_reach_in_run':
    case 'final_score':
    case 'finalscore':
    case 'best_score':
    case 'bestscore':
    case 'score':
      return 'score';

    // ----- STARS CREATED -----
    // Mission/objective types (legacy) + payload keys
    case 'stars_created':
    case 'created_stars':
    case 'createdstars':
    case 'created_stars_count':
    case 'make_star':
    case 'make_stars':
    case 'createdstars_count':
    case 'createdstarscount':
    case 'created_stars_created':
    case 'createdstarscreated':
    case 'createdstars_total':
    case 'createdstarstotal':
    case 'created_stars_total_count':
    case 'createdstarstotalcount':
    case 'createdstars_totalcount':
    case 'created_stars_totalcount':
    case 'created_stars_createdcount':
    case 'createdstars_createdcount':
    case 'created_stars_created_count':
    case 'createdstars_created_count':
    case 'starscreated':
      return 'starsCreated';

    // ----- STRIPES ACTIVATED -----
    // Mission/objective types (legacy) + payload keys
    case 'stripes_activated':
    case 'activate_stripe':
    case 'activated_stripes':
    case 'activatedstripes':
    case 'stripesactivated':
      return 'stripesActivated';

    // ----- CASCADES -----
    // IMPORTANT: We store cascades as EXTRA cascades (depth-1) in the aggregated progress.
    // normalizeKey only maps field names; conversion logic is elsewhere.
    case 'cascade_depth':
    case 'cascadedepth':
    case 'max_cascade':
    case 'maxcascade':
    case 'best_cascade':
    case 'bestcascade':
    case 'cascades_best':
    case 'cascadesbest':
    case 'cascades':
    case 'cascade':
    case 'cascade_count':
    case 'cascadecount':
      return 'cascades';

    // ----- CLEARS BY ELEMENT -----
    case 'cleared_fire':
    case 'clear_element_fire':
    case 'clearedfire':
      return 'clearedFire';

    case 'cleared_water':
    case 'clear_element_water':
    case 'clearedwater':
      return 'clearedWater';

    case 'cleared_air':
    case 'clear_element_air':
    case 'clearedair':
      return 'clearedAir';

    case 'cleared_earth':
    case 'clear_element_earth':
    case 'clearedearth':
      return 'clearedEarth';

    // ----- GENERIC CLEARS -----
    case 'cleared_cells':
    case 'clearedcells':
    case 'cleared':
    case 'clear':
      return 'clearedCells';

    default:
      // Return the normalized key (useful for debugging unknown payloads)
      return key;
  }
};
function ZodiacCrushHelpCard({ onClose, onOpenLeaderboard }) {
  // Modal overlay + premium card (compact, easy to read)
  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // close when clicking outside the card
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 14,
        paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
        background:
          'radial-gradient(circle at 30% 20%, rgba(255,215,0,0.14), transparent 45%), ' +
          'radial-gradient(circle at 70% 30%, rgba(75,0,130,0.40), transparent 55%), ' +
          'rgba(0,0,0,0.62)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        animation: 'zcHelpOverlayIn 180ms ease-out',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          width: 'min(520px, 92vw)',
          borderRadius: 20,
          background:
            'linear-gradient(180deg, rgba(75,0,130,0.78), rgba(18,0,34,0.96))',
          border: '1px solid rgba(255,215,0,0.28)',
          color: '#FFD700',
          boxShadow: '0 26px 60px rgba(0,0,0,0.55)',
          overflow: 'hidden',
          animation: 'zcHelpCardIn 240ms cubic-bezier(.2,.9,.2,1)',
          maxHeight: 'calc(92vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: 0,
          contain: 'layout paint',
        }}
      >
        {/* subtle star dust */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.85,
            background:
              'radial-gradient(circle at 18% 22%, rgba(255,215,0,0.10), transparent 42%),' +
              'radial-gradient(circle at 78% 30%, rgba(255,255,255,0.07), transparent 45%),' +
              'radial-gradient(circle at 45% 78%, rgba(255,215,0,0.08), transparent 46%)',
          }}
        />

        <div
          style={{
            padding: '12px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            background:
              'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,255,255,0.04))',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div style={{ fontWeight: 950, fontSize: 14.5, letterSpacing: 0.2 }}>
            🌟 {zcText('Cómo jugar', 'How to play')}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={zcText('Cerrar ayuda', 'Close help')}
            style={{
              borderRadius: 999,
              width: 44,
              height: 44,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 950,
              fontSize: 16,
              background: 'rgba(255,255,255,0.12)',
              color: '#FFD700',
              border: '1px solid rgba(255,255,255,0.18)',
              cursor: 'pointer',
              lineHeight: 1,
              boxShadow: '0 10px 22px rgba(0,0,0,0.35)',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            ✕
          </button>
        </div>



        <div
          style={{
            position: 'relative',
            zIndex: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: 12,
          }}
        >
          <div style={{ fontSize: 12.9, opacity: 0.97, lineHeight: 1.55 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 16,
                background: 'rgba(0,0,0,0.18)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div style={{ fontWeight: 950, letterSpacing: 0.2 }}>
                🌟 {zcText('Objetivo del juego', 'Game objective')}
              </div>
              <div style={{ marginTop: 6, opacity: 0.95 }}>
                {zcText('Hacé el mejor score posible y completá las 3 misiones del día para ganar XP, subir de nivel y desbloquear recompensas.', 'Earn the highest score you can and complete the 3 daily missions to gain XP, level up, and unlock rewards.')}
              </div>

              <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <div style={{ fontWeight: 950 }}>🕹️ {zcText('Cómo jugar', 'How to play')}</div>
                  <div style={{ marginTop: 6, opacity: 0.95 }}>
                    {zcText('Intercambiá 2 fichas adyacentes. Si formás 3 o más iguales, se limpian y sumás puntos.', 'Swap 2 adjacent tiles. Match 3 or more identical tiles to clear them and score points.')}
                  </div>
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: 'rgba(0,0,0,0.14)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <div style={{ fontWeight: 950 }}>✨ {zcText('Especiales', 'Special tiles')}</div>
                  <div style={{ marginTop: 6, display: 'grid', gap: 6, opacity: 0.95 }}>
                    <div>⚡ {zcText('Rayo: limpia una fila o columna.', 'Lightning: clears a row or column.')}</div>
                    <div>💣 {zcText('Bomba: explota una zona alrededor.', 'Bomb: clears the surrounding area.')}</div>
                    <div>🌟 {zcText('Estrella: al intercambiarla, borra todas las fichas de un mismo signo.', 'Star: swap it to clear every tile of one sign.')}</div>
                  </div>

                  <div style={{ marginTop: 10, fontWeight: 950 }}>🎁 {zcText('Boosters de recompensa', 'Reward boosters')}</div>
                  <div style={{ marginTop: 6, display: 'grid', gap: 6, opacity: 0.95 }}>
                    <div>💡 {zcText('Pistas: muestran un movimiento posible cuando no hay jugadas claras.', 'Hints: show an available move when no clear play is visible.')}</div>
                    <div>💣 {zcText('Bomba de fila: rompe una fila horizontal completa.', 'Row bomb: clears an entire horizontal row.')}</div>
                  </div>
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background:
                      'linear-gradient(180deg, rgba(255,215,0,0.10), rgba(0,0,0,0.22))',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  <div style={{ fontWeight: 950 }}>⚡ {zcText('Cómo se crean', 'How to create them')}</div>
                  <div style={{ marginTop: 6, display: 'grid', gap: 6, opacity: 0.95 }}>
                    <div>⚡ {zcText('4 en línea → Rayo', '4 in a row → Lightning')}</div>
                    <div>💣 {zcText('Forma T o L → Bomba', 'T or L shape → Bomb')}</div>
                    <div>🌟 {zcText('5 en línea → Estrella', '5 in a row → Star')}</div>
                    <div>⚡💣🌟 {zcText('3 cargas → aparece un especial aleatorio en el tablero', '3 charges → a random special tile appears on the board')}</div>
                  </div>
                </div>

                <div
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: 'rgba(0,0,0,0.14)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <div style={{ fontWeight: 950 }}>🎯 {zcText('Misiones y movimientos', 'Missions and moves')}</div>
                  <div style={{ marginTop: 6, opacity: 0.95 }}>
                    {zcText('Tenés cristales por partida. Cada movimiento válido gasta 1. Al llegar a 0, termina la partida.', 'Each game gives you a set of crystals. Every valid move uses 1. The game ends when you reach 0.')}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  borderRadius: 999,
                  padding: '10px 14px',
                  fontWeight: 950,
                  background: 'linear-gradient(135deg,#FFD700,#FFB700)',
                  color: '#4B0082',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 14px 26px rgba(0,0,0,0.35)',
                }}
              >
                Entendido ✨
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ZodiacCrushLeaderboardModal({
  onClose,
  title = '🏆 Ranking ZodiacCrush',
  scopes = [],
  scope, // alias/back-compat
  activeScope,
  onChangeScope,
  loading,
  error,
  entries,
  my,
}) {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const myUserId = my?.userId ? String(my.userId) : null;

  const getLocalISODate = () => {
    try {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch (e) {
      return String(new Date().toISOString()).slice(0, 10);
    }
  };

  const normalizeSignSlug = (raw) => {
  let s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  try { s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (_) {}

  if (s.startsWith('sign:')) s = s.slice('sign:'.length);

  const map = {
    aries: 'aries',
    taurus: 'tauro',
    gemini: 'geminis',
    cancer: 'cancer',
    leo: 'leo',
    virgo: 'virgo',
    libra: 'libra',
    scorpio: 'escorpio',
    sagittarius: 'sagitario',
    capricorn: 'capricornio',
    aquarius: 'acuario',
    pisces: 'piscis',

    tauro: 'tauro',
    geminis: 'geminis',
    escorpio: 'escorpio',
    sagitario: 'sagitario',
    capricornio: 'capricornio',
    acuario: 'acuario',
    piscis: 'piscis',
  };

  return map[s] || s;
};

  const signEmoji = (raw) => {
    const slug = normalizeSignSlug(raw);
    if (!slug) return '♈';

    // allow both spanish names and slugs
    const map = {
      aries: '♈',
      tauro: '♉',
      geminis: '♊',
      cancer: '♋',
      leo: '♌',
      virgo: '♍',
      libra: '♎',
      escorpio: '♏',
      sagitario: '♐',
      capricornio: '♑',
      acuario: '♒',
      piscis: '♓',
    };

    // some users might store "géminis" / "cáncer" etc; normalizeSignSlug already strips accents
    return map[slug] || '♈';
  };

  const SIGN_OPTIONS = [
    { label: '♈ Aries', slug: 'aries' },
    { label: `♉ ${zcText('Tauro', 'Taurus')}`, slug: 'tauro' },
    { label: `♊ ${zcText('Géminis', 'Gemini')}`, slug: 'geminis' },
    { label: `♋ ${zcText('Cáncer', 'Cancer')}`, slug: 'cancer' },
    { label: '♌ Leo', slug: 'leo' },
    { label: '♍ Virgo', slug: 'virgo' },
    { label: '♎ Libra', slug: 'libra' },
    { label: `♏ ${zcText('Escorpio', 'Scorpio')}`, slug: 'escorpio' },
    { label: `♐ ${zcText('Sagitario', 'Sagittarius')}`, slug: 'sagitario' },
    { label: `♑ ${zcText('Capricornio', 'Capricorn')}`, slug: 'capricornio' },
    { label: `♒ ${zcText('Acuario', 'Aquarius')}`, slug: 'acuario' },
    { label: `♓ ${zcText('Piscis', 'Pisces')}`, slug: 'piscis' },
  ];

  const resolvedScope =
  activeScope != null
    ? String(activeScope)
    : scope != null
    ? String(scope)
    : 'global';

const isSignScope = String(resolvedScope).startsWith('sign:');

const selectedSignSlug = isSignScope
  ? String(resolvedScope).slice('sign:'.length)
  : '';
  const mySignRaw = my?.zodiacSign || my?.sign || my?.zodiac || '';
  const mySignSlug = normalizeSignSlug(mySignRaw);
  const todayKey = getLocalISODate();

  const defaultScopes = [
    { label: zcText('🌍 Global', '🌍 Global'), value: 'global' },
    {
      label: zcText('♑ Signo', '♑ Sign'),
      value: `sign:${mySignSlug || 'capricornio'}`,
    },
    { label: zcText('📅 Hoy', '📅 Today'), value: `daily:${todayKey}` },
  ];

  const effectiveScopes = (Array.isArray(scopes) && scopes.length) ? scopes : defaultScopes;

  const isNarrow =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 520px)').matches;

  // ---------- Premium avatar (safe fallback) ----------
  const resolveAvatarUrl = (raw) => {
    const u = raw ? String(raw).trim() : '';
    if (!u) return '';
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    if (u.startsWith('//')) return `https:${u}`;

    const base = REST_API_ORIGIN;

    // If backend returns '/uploads/..' or similar, prefix with base
    if (u.startsWith('/')) return base ? `${base}${u}` : u;

    // Relative path: prefix with base
    return base ? `${base}/${u}` : u;
  };

  const pickAvatarUrl = (e) => {
    const v =
      e?.avatarUrl ||
      e?.avatarURL ||
      e?.photoUrl ||
      e?.photoURL ||
      e?.picture ||
      e?.image ||
      e?.profilePic ||
      e?.profileImage ||
      e?.profileImageUrl ||
      e?.profileImageURL ||
      null;
    const url = v ? String(v) : '';
    return resolveAvatarUrl(url);
  };

  const Avatar = ({ entry, size = 34 }) => {
    const [failed, setFailed] = useState(false);

    const name = String(entry?.displayName || 'Jugador');
    const initial = (name.trim()[0] || 'J').toUpperCase();
    const url = !failed ? pickAvatarUrl(entry) : '';

    if (url) {
      return (
        <img
          src={url}
          alt={name}
          loading="lazy"
          referrerPolicy="no-referrer"
          style={{
            width: size,
            height: size,
            borderRadius: 999,
            objectFit: 'cover',
            border: '1px solid rgba(255,215,0,0.40)',
            boxShadow: '0 0 0 1px rgba(255,215,0,0.10), 0 10px 22px rgba(0,0,0,0.32)',
            background: 'rgba(255,255,255,0.06)',
            flex: '0 0 auto',
          }}
          onError={() => setFailed(true)}
        />
      );
    }

    return (
      <div
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 950,
          fontSize: Math.max(12, Math.round(size * 0.44)),
          color: '#4B0082',
          background: 'linear-gradient(135deg, rgba(255,215,0,0.95), rgba(255,183,0,0.92))',
          border: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 0 0 1px rgba(255,215,0,0.14), 0 12px 26px rgba(0,0,0,0.35)',
          flex: '0 0 auto',
        }}
        title={name}
      >
        {initial}
      </div>
    );
  };

  const crownForRank = (idx) => {
    if (idx === 0) return '👑';
    if (idx === 1) return '🥈';
    if (idx === 2) return '🥉';
    return null;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isNarrow ? 12 : 16,
        paddingTop: `calc(${isNarrow ? 12 : 16}px + env(safe-area-inset-top, 0px))`,
        paddingBottom: `calc(${isNarrow ? 12 : 16}px + env(safe-area-inset-bottom, 0px))`,
        background:
          'radial-gradient(circle at 30% 20%, rgba(255,215,0,0.16), transparent 45%), ' +
          'radial-gradient(circle at 70% 30%, rgba(75,0,130,0.45), transparent 55%), ' +
          'rgba(0,0,0,0.62)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        animation: 'zcHelpOverlayIn 180ms ease-out',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          width: 'min(680px, 94vw)',
          borderRadius: 22,
          background: 'linear-gradient(180deg, rgba(75,0,130,0.76), rgba(18,0,34,0.96))',
          border: '1px solid rgba(255,215,0,0.28)',
          color: '#FFD700',
          boxShadow: '0 28px 70px rgba(0,0,0,0.62)',
          overflow: 'hidden',
          position: 'relative',
          maxHeight: 'calc(92vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.85,
            background:
              'radial-gradient(circle at 18% 22%, rgba(255,215,0,0.12), transparent 42%),' +
              'radial-gradient(circle at 78% 30%, rgba(255,255,255,0.08), transparent 45%),' +
              'radial-gradient(circle at 45% 78%, rgba(255,215,0,0.10), transparent 46%)',
          }}
        />

        <div
          style={{
            padding: `${isNarrow ? 12 : 14}px ${isNarrow ? 12 : 16}px`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            background:
              'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,255,255,0.04))',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div>
            <div style={{ fontWeight: 950, fontSize: 15, letterSpacing: 0.25 }}>{title}</div>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
              {zcText('Competí por el mejor score · Actualiza cuando terminás una partida', 'Compete for the highest score · Updates when you finish a run')}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={zcText('Cerrar', 'Close')}
            style={{
              borderRadius: 999,
              width: 44,
              height: 44,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 950,
              background: 'rgba(255,255,255,0.10)',
              color: '#FFD700',
              border: '1px solid rgba(255,255,255,0.16)',
              cursor: 'pointer',
              lineHeight: 1,
              boxShadow: '0 10px 22px rgba(0,0,0,0.32)',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            padding: `${isNarrow ? 12 : 14}px ${isNarrow ? 12 : 16}px`,
            overflowY: 'auto',
          }}
        >
          {Array.isArray(effectiveScopes) && effectiveScopes.length ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {effectiveScopes.map((s) => {
                const isActive = String(resolvedScope) === String(s.value);
                return (
                  <button
                    key={String(s.value)}
                    type="button"
                    onClick={() => onChangeScope && onChangeScope(s.value)}
                    style={{
                      borderRadius: 999,
                      padding: '8px 12px',
                      fontWeight: 950,
                      fontSize: 12,
                      cursor: 'pointer',
                      border: isActive
                        ? '1px solid rgba(255,215,0,0.40)'
                        : '1px solid rgba(255,255,255,0.14)',
                      background: isActive
                        ? 'linear-gradient(135deg, rgba(255,215,0,0.22), rgba(75,0,130,0.18))'
                        : 'rgba(255,255,255,0.07)',
                      color: '#FFD700',
                      boxShadow: isActive ? '0 14px 28px rgba(0,0,0,0.30)' : 'none',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          {isSignScope ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 16,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div style={{ fontWeight: 950, fontSize: 12, opacity: 0.95 }}>
                {zcText('Elegí un signo', 'Choose a sign')}
              </div>

              <select
                value={selectedSignSlug}
                onChange={(e) => {
                  const next = String(e.target.value || '').trim();
                  if (next) onChangeScope && onChangeScope(`sign:${next}`);
                }}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  borderRadius: 999,
                  padding: '10px 14px',
                  fontWeight: 950,
                  fontSize: 12,
                  cursor: 'pointer',
                  border: '1px solid rgba(255,215,0,0.32)',
                  background:
                    'linear-gradient(135deg, rgba(255,215,0,0.18), rgba(75,0,130,0.16))',
                  color: '#FFD700',
                  boxShadow: '0 14px 28px rgba(0,0,0,0.28)',
                  outline: 'none',
                  minWidth: 170,
                  maxWidth: '70%',
                }}
                aria-label={zcText('Elegir signo para el ranking', 'Choose a sign for the leaderboard')}
              >
                {SIGN_OPTIONS.map((s) => (
                  <option key={s.slug} value={s.slug} style={{ color: '#111' }}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {loading ? (
            <div
              style={{
                padding: 14,
                borderRadius: 18,
                background: 'rgba(0,0,0,0.18)',
                border: '1px solid rgba(255,255,255,0.10)',
                fontWeight: 900,
                opacity: 0.92,
              }}
            >
              {zcText('Cargando ranking…', 'Loading leaderboard…')}
            </div>
          ) : error ? (
            <div
              style={{
                padding: 14,
                borderRadius: 18,
                background: 'rgba(0,0,0,0.18)',
                border: '1px solid rgba(255,80,80,0.28)',
                color: 'rgba(255,215,0,0.95)',
                fontWeight: 900,
                opacity: 0.95,
              }}
            >
              {String(error)}
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>
                {zcText('Tip: en dev asegurate de tener proxy/API base configurado y token válido.', 'Tip: in development, make sure the proxy/API base and a valid token are configured.')}
              </div>
            </div>
          ) : (
            <div
              style={{
                borderRadius: 18,
                background: 'rgba(0,0,0,0.18)',
                border: '1px solid rgba(255,255,255,0.10)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isNarrow ? '40px 1fr 96px' : '44px 1fr 110px',
                  gap: 10,  
                  padding: '10px 12px',
                  fontSize: 11.5,
                  fontWeight: 950,
                  letterSpacing: 0.35,
                  textTransform: 'uppercase',
                  opacity: 0.9,
                  borderBottom: '1px solid rgba(255,255,255,0.10)',
                  background: 'rgba(255,255,255,0.04)',
                }}
              >
                <div>#</div>
                <div>{zcText('Usuario', 'Player')}</div>
                <div style={{ textAlign: 'right' }}>Score</div>
              </div>

              {safeEntries.length === 0 ? (
                <div style={{ padding: 14, opacity: 0.92, fontWeight: 900 }}>
                  {zcText('Todavía no hay scores.', 'No scores yet.')}
                </div>
              ) : (
                safeEntries.map((e, idx) => {
                  const isMe = myUserId && e?.userId && String(e.userId) === myUserId;
                  const name = String(e?.displayName || zcText('Jugador', 'Player'));
                  const signLabel = e?.zodiacSign ? String(e.zodiacSign) : '';
                  const score = Number(e?.score || 0) || 0;

                  const isTop1 = idx === 0;
                  const isTop3 = idx < 3;
                  const medal = crownForRank(idx);

                  return (
                    <div
                      key={String(e?._id || e?.userId || idx)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isNarrow ? '40px 1fr 96px' : '44px 1fr 110px',
                        gap: 10,
                        padding: isNarrow ? '10px 10px' : '11px 12px',
                        alignItems: 'center',
                        borderBottom:
                          idx === safeEntries.length - 1
                            ? 'none'
                            : '1px solid rgba(255,255,255,0.08)',
                        background: isTop1
                          ? 'linear-gradient(135deg, rgba(255,215,0,0.22), rgba(75,0,130,0.14))'
                          : isTop3
                          ? 'rgba(255,215,0,0.06)'
                          : isMe
                          ? 'linear-gradient(135deg, rgba(255,215,0,0.14), rgba(75,0,130,0.10))'
                          : 'transparent',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 950,
                          opacity: 0.95,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          gap: 6,
                        }}
                        title={medal ? 'Top' : 'Puesto'}
                      >
                        <span
                          style={{
                            fontSize: isTop1 ? 16 : 14,
                            filter: isTop1 ? 'drop-shadow(0 6px 14px rgba(255,215,0,0.38))' : 'none',
                          }}
                        >
                          {medal || idx + 1}
                        </span>
                      </div>

                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar entry={e} size={isNarrow ? 34 : 36} />

                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
  style={{
    fontWeight: 950,
    overflow: 'hidden',
    display: isNarrow ? 'block' : 'flex',
    alignItems: isNarrow ? undefined : 'center',
    gap: isNarrow ? undefined : 8,
    lineHeight: 1.12,
    whiteSpace: isNarrow ? 'normal' : 'nowrap',
    textOverflow: isNarrow ? 'clip' : 'ellipsis',
    wordBreak: isNarrow ? 'break-word' : 'normal',
    overflowWrap: isNarrow ? 'anywhere' : 'normal',
  }}
  title={name}
>
  <span
    style={{
      display: 'block',
      overflow: isNarrow ? 'visible' : 'hidden',
      textOverflow: isNarrow ? 'clip' : 'ellipsis',
      whiteSpace: isNarrow ? 'normal' : 'nowrap',
      maxWidth: '100%',
    }}
  >
    {isMe ? '⭐ ' : ''}
    {name}
  </span>

  {isTop1 ? (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3px 8px',
        borderRadius: 999,
        background: 'rgba(255,215,0,0.14)',
        border: '1px solid rgba(255,215,0,0.26)',
        color: '#FFD700',
        fontWeight: 950,
        fontSize: 11,
        whiteSpace: 'nowrap',
        boxShadow: '0 12px 26px rgba(0,0,0,0.30)',
        flex: '0 0 auto',
        marginTop: isNarrow ? 6 : 0,
      }}
      title={zcText('Campeón', 'Champion')}
    >
      {zcText('Campeón', 'Champion')}
    </span>
  ) : null}
</div>

                          <div
                            style={{
                              fontSize: 11.5,
                              opacity: 0.86,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              minWidth: 0,
                            }}
                          >
                            {signLabel ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  padding: '3px 8px',
                                  borderRadius: 999,
                                  background: 'rgba(255,255,255,0.06)',
                                  border: '1px solid rgba(255,255,255,0.10)',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {signEmoji(signLabel)} {signLabel}
                              </span>
                            ) : (
                              <span> </span>
                            )}

                            {isMe ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '3px 8px',
                                  borderRadius: 999,
                                  background: 'linear-gradient(135deg, rgba(255,215,0,0.18), rgba(75,0,130,0.14))',
                                  border: '1px solid rgba(255,215,0,0.26)',
                                  color: '#FFD700',
                                  fontWeight: 950,
                                  fontSize: 11,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Vos
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          textAlign: 'right',
                          fontWeight: 950,
                          fontSize: isTop1 ? (isNarrow ? 15 : 16) : (isNarrow ? 13 : 14),
                          color: '#FFD700',
                          textShadow: isTop1 ? '0 0 14px rgba(255,215,0,0.55)' : 'none',
                        }}
                        title="Score"
                      >
                        {score.toLocaleString('es-AR')}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {my && !loading && !error ? (
            <div style={{ marginTop: 12, fontSize: 12, opacity: 0.9 }}>
              {zcText('Tu puntaje acumulado:', 'Your total score:')}{' '}
              <strong>{Number(my?.score || 0).toLocaleString('es-AR')}</strong>
            </div>
          ) : null}
        </div>

        <div
          style={{
            padding: `${isNarrow ? 12 : 14}px ${isNarrow ? 12 : 16}px`,
            borderTop: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              borderRadius: 999,
              padding: '11px 14px',
              fontWeight: 950,
              background: 'linear-gradient(135deg,#FFD700,#FFB700)',
              color: '#4B0082',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 16px 34px rgba(0,0,0,0.38)',
              width: '100%',
            }}
          >
            {zcText('Listo', 'Done')} ✨
          </button>
        </div>
      </div>
    </div>
  );
}


function ZodiacCrushRunEndModal({
  onClose,
  onReplay,
  onBack,
  score,
  maxCascade,
  allDoneBadge,
  missions,
  bestScoreLocal,
  isNewRecord,
}) {
  const completed = Array.isArray(missions) ? missions.filter((m) => !!m?.done).length : 0;
  const total = Array.isArray(missions) ? missions.length : 0;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  const isNarrow =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 520px)').matches;

  const cardPadX = isNarrow ? 12 : 16;
  const cardPadY = isNarrow ? 12 : 16;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isNarrow ? 12 : 16,
        paddingTop: `calc(${isNarrow ? 12 : 16}px + env(safe-area-inset-top, 0px))`,
        paddingBottom: `calc(${isNarrow ? 12 : 16}px + env(safe-area-inset-bottom, 0px))`,
        background:
          'radial-gradient(circle at 30% 20%, rgba(255,215,0,0.16), transparent 45%), ' +
          'radial-gradient(circle at 70% 30%, rgba(75,0,130,0.45), transparent 55%), ' +
          'rgba(0,0,0,0.62)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        animation: 'zcEndOverlayIn 180ms ease-out',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          width: 'min(640px, 94vw)',
          borderRadius: 22,
          background: 'linear-gradient(180deg, rgba(75,0,130,0.76), rgba(18,0,34,0.96))',
          border: '1px solid rgba(255,215,0,0.28)',
          color: '#FFD700',
          boxShadow: '0 28px 70px rgba(0,0,0,0.62)',
          overflow: 'hidden',
          transform: 'translateY(0)',
          animation: 'zcEndCardIn 260ms cubic-bezier(.2,.9,.2,1)',
          position: 'relative',
          maxHeight: 'calc(92vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* subtle star dust */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.85,
            background:
              'radial-gradient(circle at 18% 22%, rgba(255,215,0,0.12), transparent 42%),' +
              'radial-gradient(circle at 78% 30%, rgba(255,255,255,0.08), transparent 45%),' +
              'radial-gradient(circle at 45% 78%, rgba(255,215,0,0.10), transparent 46%)',
          }}
        />

        <div
          style={{
            padding: `${isNarrow ? 12 : 14}px ${cardPadX}px`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            background:
              'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,255,255,0.04))',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div>
            <div style={{ fontWeight: 950, fontSize: 15, letterSpacing: 0.25 }}>
              ✨ {zcText('Fin de partida', 'Game over')}
            </div>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>
              {zcText('Se terminaron tus movimientos · Guardamos tu score', 'You are out of moves · Your score was saved')}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={zcText('Cerrar', 'Close')}
            style={{
              borderRadius: 999,
              width: 44,
              height: 44,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 950,
              background: 'rgba(255,255,255,0.10)',
              color: '#FFD700',
              border: '1px solid rgba(255,255,255,0.16)',
              cursor: 'pointer',
              lineHeight: 1,
              boxShadow: '0 10px 22px rgba(0,0,0,0.32)',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            padding: `${cardPadY}px ${cardPadX}px`,
            position: 'relative',
            zIndex: 2,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr',
              gap: 12,
            }}
          >
            <div
              style={{
                borderRadius: 18,
                padding: isNarrow ? 12 : 14,
                background: 'rgba(0,0,0,0.18)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.9 }}>{zcText('Score final', 'Final score')}</div>
              <div
                style={{
                  fontSize: isNarrow ? 30 : 34,
                  fontWeight: 950,
                  letterSpacing: 0.3,
                  marginTop: 6,
                  textShadow: '0 10px 26px rgba(0,0,0,0.55)',
                }}
              >
                {Number(score || 0).toLocaleString('es-AR')}
              </div>

              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.92 }}>
                {zcText('Récord local', 'Local record')}: <strong>{Number(bestScoreLocal || 0).toLocaleString()}</strong>
                {isNewRecord ? (
                  <span
                    style={{
                      marginLeft: 8,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 8px',
                      borderRadius: 999,
                      background: 'rgba(255,215,0,0.14)',
                      border: '1px solid rgba(255,215,0,0.28)',
                      fontWeight: 950,
                      color: '#FFD700',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    🥇 {zcText('Nuevo récord', 'New record')}
                  </span>
                ) : null}
              </div>

              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {allDoneBadge && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      borderRadius: 999,
                      background:
                        'linear-gradient(135deg, rgba(218,165,32,0.98), rgba(255,215,0,0.95))',
                      color: '#4B0082',
                      fontWeight: 950,
                      fontSize: 12,
                      boxShadow: '0 14px 30px rgba(0,0,0,0.35)',
                      border: '1px solid rgba(255,255,255,0.18)',
                    }}
                  >
                    🏆 {zcText('Día completado', 'Day complete')}
                  </span>
                )}

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    fontWeight: 900,
                    fontSize: 12,
                  }}
                >
                  🔥 {zcText('Mejor cascada', 'Best cascade')}: <strong>{Number(maxCascade || 0)}</strong>
                </span>
              </div>
            </div>

            <div
              style={{
                borderRadius: 18,
                padding: isNarrow ? 12 : 14,
                background: 'rgba(0,0,0,0.18)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.9 }}>{zcText('Misiones', 'Missions')}</div>

              <div style={{ marginTop: 10 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    opacity: 0.95,
                  }}
                >
                  <span style={{ fontWeight: 850 }}>{zcText('Completadas', 'Completed')}</span>
                  <span style={{ fontWeight: 900 }}>
                    {completed}/{total} · {pct}%
                  </span>
                </div>

                <div
                  style={{
                    height: 12,
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.14)',
                    overflow: 'hidden',
                    marginTop: 8,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, Math.max(0, pct))}%`,
                      background:
                        'linear-gradient(135deg, rgba(255,215,0,0.95), rgba(255,255,255,0.20))',
                      transition: 'width 420ms ease',
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: 12,
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 8,
                    maxHeight: isNarrow ? 170 : 150,
                    overflow: 'auto',
                    paddingRight: 4,
                  }}
                >
                  {(Array.isArray(missions) ? missions : []).map((m, missionIndex) => {
                    const targetNum = Number(m?.target || 0) || 0;
                    const progressNum = Number(m?.progress || 0) || 0;
                    const done = !!m?.done;
                    const label = getLocalizedMissionLabel(m?.type) || m?.type;

                    return (
                      <div
                        key={(m && (m.id || m.type)) || `mission-${missionIndex}`}
                        style={{
                          borderRadius: 14,
                          padding: '8px 10px',
                          background: done ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.06)',
                          border: done
                            ? '1px solid rgba(255,215,0,0.22)'
                            : '1px solid rgba(255,255,255,0.10)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                            alignItems: 'center',
                            fontSize: 12,
                          }}
                        >
                          <span style={{ fontWeight: 850, opacity: done ? 0.92 : 1 }}>
                            {label}
                          </span>
                          <span style={{ opacity: 0.9, fontWeight: 900, whiteSpace: 'nowrap' }}>
                            {progressNum}/{targetNum} {done ? '✅' : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={onReplay}
                style={{
                  borderRadius: 999,
                  padding: '11px 14px',
                  fontWeight: 950,
                  background: 'linear-gradient(135deg,#FFD700,#FFB700)',
                  color: '#4B0082',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 16px 34px rgba(0,0,0,0.38)',
                }}
              >
                🔁 {zcText('Jugar otra vez', 'Play again')}
              </button>

              <button
                type="button"
                onClick={onBack}
                style={{
                  borderRadius: 999,
                  padding: '11px 14px',
                  fontWeight: 900,
                  background: 'rgba(255,255,255,0.08)',
                  color: '#FFD700',
                  border: '1px solid rgba(255,255,255,0.16)',
                  cursor: 'pointer',
                }}
              >
                ← {zcText('Volver', 'Back')}
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                borderRadius: 999,
                padding: '11px 14px',
                fontWeight: 900,
                background: 'rgba(0,0,0,0.18)',
                color: '#FFD700',
                border: '1px solid rgba(255,255,255,0.14)',
                cursor: 'pointer',
              }}
            >
              {zcText('Seguir mirando', 'Keep viewing')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// ---- WebAudio (ultra-light, no assets) ----


let _audioCtx;
let _masterGain;
let _compressor;
let _ambience = null;
let _ambienceGain = null;
void _ambienceGain;
// Audio can ONLY start after a user gesture (Chrome autoplay policy)
let _audioUnlockedByGesture = false;

// ---- Audio mute state ----
const AUDIO_MUTED_KEY = 'zodiacus_zodiaccrush_muted_v1';
let _muted = false;
try { _muted = localStorage.getItem(AUDIO_MUTED_KEY) === '1'; } catch (e) {}
const isMuted = () => !!_muted;
const setMuted = (v) => {
  _muted = !!v;
  try { localStorage.setItem(AUDIO_MUTED_KEY, _muted ? '1' : '0'); } catch (e) {}
};

const getAudioCtx = () => {
  // Prevent creating AudioContext before a user gesture
  if (!_audioUnlockedByGesture) return null;
  if (!_audioCtx) {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();

    // Master chain (audible + consistent across devices)
    _compressor = _audioCtx.createDynamicsCompressor();
    _compressor.threshold.setValueAtTime(-24, _audioCtx.currentTime);
    _compressor.knee.setValueAtTime(30, _audioCtx.currentTime);
    _compressor.ratio.setValueAtTime(12, _audioCtx.currentTime);
    _compressor.attack.setValueAtTime(0.003, _audioCtx.currentTime);
    _compressor.release.setValueAtTime(0.18, _audioCtx.currentTime);

    _masterGain = _audioCtx.createGain();
    _masterGain.gain.setValueAtTime(0.85, _audioCtx.currentTime);

    _compressor.connect(_masterGain);
    _masterGain.connect(_audioCtx.destination);
  }
  return _audioCtx;
};

// iOS/Safari hard unlock: must be called from a user gesture
const primeAudio = () => {
  try {
    _audioUnlockedByGesture = true;
    const ctx = getAudioCtx();
    if (!ctx) return;

    const unlock = () => {
      // play a silent buffer to fully unlock output
      const buffer = ctx.createBuffer(1, 22050, 22050); // 1s silent buffer
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
      if (_masterGain) _masterGain.gain.setValueAtTime(isMuted() ? 0 : 0.85, ctx.currentTime);
    };

    if (ctx.state === 'suspended') {
      ctx.resume().then(unlock).catch(() => {});
    } else {
      unlock();
    }
    // Ambience bed removed for mobile compatibility.
  } catch (e) {
    // ignore
  }
};

const stopAllAudio = () => {
  try {
    // Stop any long-lived nodes (defensive)
    if (_ambience && _ambience.base) {
      try { _ambience.base.stop(); } catch (e) {}
      try { _ambience.lfo.stop(); } catch (e) {}
    }
  } catch (e) {}

  _ambience = null;
  _ambienceGain = null;

  try {
    if (_audioCtx) {
      // Closing releases the hardware output and prevents background hum
      _audioCtx.close().catch(() => {});
    }
  } catch (e) {}

  _audioCtx = null;
  _masterGain = null;
  _compressor = null;
  _audioUnlockedByGesture = false;
};

const playTone = ({ freq = 440, type = 'sine', gain = 0.14, dur = 0.10, sweep = 0 }) => {
  try {
    if (isMuted()) return;
    if (!_audioUnlockedByGesture) return;
    const ctx = getAudioCtx();
    if (!ctx) return;

    const start = () => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();

      o.type = type;
      o.frequency.setValueAtTime(freq, ctx.currentTime);
      if (sweep) {
        o.frequency.linearRampToValueAtTime(freq + sweep, ctx.currentTime + dur);
      }

      // Quick attack + exponential release
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain), ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);

      o.connect(g);
      // Route through compressor/master if available
      if (_compressor) g.connect(_compressor);
      else g.connect(ctx.destination);

      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + dur);
    };

    if (ctx.state === 'suspended') {
      ctx.resume().then(start).catch(() => {});
      return;
    }

    start();
  } catch (e) {
    // ignore
  }
};

const SOUND_BY_ELEMENT = {
  fire: () => playTone({ freq: 620, type: 'triangle', sweep: 420, gain: 0.12, dur: 0.09 }),
  water: () => playTone({ freq: 420, type: 'sine', sweep: -220, gain: 0.11, dur: 0.10 }),
  air: () => playTone({ freq: 980, type: 'sine', sweep: 260, gain: 0.10, dur: 0.08 }),
  earth: () => playTone({ freq: 240, type: 'square', gain: 0.14, dur: 0.09 }),
};

const playInvalidThunk = () =>
  playTone({ freq: 160, type: 'square', gain: 0.16, dur: 0.08 });

const playSwapClick = () =>
  playTone({ freq: 760, type: 'triangle', gain: 0.06, dur: 0.04, sweep: -60 });


const playComboDing = (step = 0, base = 520) => {
  // step: 0..N, raises pitch each time
  const freq = Math.min(1400, base + step * 120);
  playTone({ freq, type: 'sine', gain: 0.08, dur: 0.06, sweep: 80 });
};

const playMatchTick3 = (base = 560) =>
  playTone({ freq: base, type: 'triangle', gain: 0.05, dur: 0.035, sweep: -40 });

const playMatchBell4 = (step = 0, base = 640) => {
  // a slightly longer bell-like accent than the combo ding
  const freq = Math.min(1500, base + step * 90);
  playTone({ freq, type: 'sine', gain: 0.09, dur: 0.085, sweep: 120 });
};

const playCrystalSparkle = (step = 0) => {
  // “crystal” shimmer: three fast high pings with rising pitch
  const a = Math.min(1800, 1180 + step * 70);
  const b = Math.min(2000, 1420 + step * 85);
  const c = Math.min(2200, 1660 + step * 95);

  playTone({ freq: a, type: 'sine', gain: 0.07, dur: 0.06, sweep: 160 });
  window.setTimeout(() => playTone({ freq: b, type: 'triangle', gain: 0.06, dur: 0.06, sweep: 220 }), 55);
  window.setTimeout(() => playTone({ freq: c, type: 'sine', gain: 0.05, dur: 0.05, sweep: 260 }), 105);
};

const playSpecialBoom = () => {
  // Soft “whoom” for stripe/star activation
  playTone({ freq: 210, type: 'sine', gain: 0.12, dur: 0.12, sweep: 90 });
  window.setTimeout(() => playTone({ freq: 420, type: 'triangle', gain: 0.08, dur: 0.10, sweep: 220 }), 40);
};

// Presentation-only special identities. These consume resolver events; they do
// not create or mutate gameplay state.
const playStripeZap = () => {
  playTone({ freq: 980, type: 'sawtooth', gain: 0.07, dur: 0.07, sweep: 520 });
  window.setTimeout(() => playTone({ freq: 1480, type: 'triangle', gain: 0.045, dur: 0.05, sweep: 180 }), 35);
};

const playStarCreationCue = () => {
  playTone({ freq: 760, type: 'sine', gain: 0.06, dur: 0.09, sweep: 240 });
  window.setTimeout(() => playTone({ freq: 1180, type: 'sine', gain: 0.05, dur: 0.08, sweep: 180 }), 55);
};

const playBombCreationCue = () => {
  playTone({ freq: 180, type: 'triangle', gain: 0.09, dur: 0.10, sweep: -45 });
  window.setTimeout(() => playTone({ freq: 520, type: 'sine', gain: 0.045, dur: 0.05, sweep: 90 }), 45);
};

const playStripeActivation = () => {
  playTone({ freq: 1120, type: 'sawtooth', gain: 0.09, dur: 0.12, sweep: 720 });
};

const playStarActivation = () => {
  playTone({ freq: 640, type: 'sine', gain: 0.08, dur: 0.14, sweep: 760 });
  window.setTimeout(() => playTone({ freq: 1320, type: 'triangle', gain: 0.055, dur: 0.11, sweep: 260 }), 65);
};

const playBombActivation = () => {
  playTone({ freq: 120, type: 'sine', gain: 0.13, dur: 0.15, sweep: -35 });
  window.setTimeout(() => playTone({ freq: 720, type: 'triangle', gain: 0.05, dur: 0.06, sweep: -120 }), 55);
};

const playMissionCompleteCue = () => {
  playTone({ freq: 700, type: 'sine', gain: 0.06, dur: 0.08, sweep: 160 });
  window.setTimeout(() => playTone({ freq: 1040, type: 'triangle', gain: 0.055, dur: 0.10, sweep: 220 }), 65);
};

const playObjectiveCompleteCue = () => {
  playTone({ freq: 560, type: 'sine', gain: 0.07, dur: 0.08, sweep: 120 });
  window.setTimeout(() => playTone({ freq: 880, type: 'sine', gain: 0.06, dur: 0.11, sweep: 260 }), 70);
};

const playSpecialComboCue = () => {
  playTone({ freq: 300, type: 'triangle', gain: 0.08, dur: 0.12, sweep: 260 });
  window.setTimeout(() => playTone({ freq: 980, type: 'sine', gain: 0.06, dur: 0.10, sweep: 420 }), 55);
};

const safeVibrate = (pattern) => {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
  } catch (_) {}
};

const playLossCue = () => {
  playTone({ freq: 250, type: 'sine', gain: 0.055, dur: 0.14, sweep: -55 });
  window.setTimeout(() => playTone({ freq: 180, type: 'triangle', gain: 0.04, dur: 0.16, sweep: -35 }), 120);
};



// =========================
// Reusable missions hook
// =========================
export function useZodiacCrushMissions({
  onMissionCompleted,
  onAllCompleted,
} = {}) {
 const [missions, setMissions] = useState([]);
  const [profile, setProfile] = useState(null); // { level, xp, nextLevelXp, ... }
  const [loading, setLoading] = useState(false);
  const [boostersStar, setBoostersStar] = useState(null); // ⭐ backend inventory (hint booster)

  

  const [rewardsUnlocked, setRewardsUnlocked] = useState([]); // [{level,id,kind,title,desc}]

  const _didInitRef = useRef(false);
  
  const _inflightRef = useRef(null);
  const _lastFetchAtRef = useRef(0);
  const _lastWarnAtRef = useRef(0);
  const _refreshTimerRef = useRef(null);
  
  const _missionsRef = useRef([]); // last known missions snapshot (for diff + callbacks)
// Tracks the YYYY-MM-DD key of the missions snapshot currently in UI.
// Prevents mixing progress between days and avoids "bar goes up then resets" when the day changes.
const _missionsDayRef = useRef('');
  // Prevent duplicate / spammy progress POSTs (React 18 strict-mode, fast re-renders)
  

  
  

  // Keep best cascade depth reached during this session so we never overwrite it with 0 later.
  const _bestCascadeDepthRef = useRef(0);


  

  const warnOnce = (...args) => {
    const now = Date.now();
    if (now - (_lastWarnAtRef.current || 0) < 4000) return;
    _lastWarnAtRef.current = now;
    // eslint-disable-next-line no-console
    console.warn(...args);
  };

  // Normalize backend variations (older/newer payload shapes)
  const pickHintBoosterCount = (obj) => {
    // Prefer explicit `boosters.hint` or `boosters.star`, otherwise profile.boostersInventory.hint, otherwise boostersInventory.hint
    const fromBoostersHint = (obj?.boosters && typeof obj.boosters.hint === 'number') ? Number(obj.boosters.hint) : null;
    if (fromBoostersHint != null) return fromBoostersHint;

    const fromBoostersStar = (obj?.boosters && typeof obj.boosters.star === 'number') ? Number(obj.boosters.star) : null;
    if (fromBoostersStar != null) return fromBoostersStar;

    const fromProfile = (typeof obj?.profile?.boostersInventory?.hint === 'number') ? Number(obj.profile.boostersInventory.hint) : null;
    if (fromProfile != null) return fromProfile;

    const fromInv = (typeof obj?.boostersInventory?.hint === 'number') ? Number(obj.boostersInventory.hint) : null;
    if (fromInv != null) return fromInv;

    return null;
  };

  const pickRewardsUnlocked = (obj) => {
    if (Array.isArray(obj?.rewardsUnlocked) && obj.rewardsUnlocked.length) return obj.rewardsUnlocked;
    if (Array.isArray(obj?.profile?.rewardsUnlocked) && obj.profile.rewardsUnlocked.length) return obj.profile.rewardsUnlocked;
    return null;
  };

 const allDone = useMemo(() => {
  return (
    Array.isArray(missions) &&
    missions.length >= 3 &&                 // ← CLAVE DEL FIX
    missions.every((m) => !!(m && m.done))
  );
}, [missions]);

  const fetchToday = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();

    // Avoid hammering the endpoint (dev strict-mode / fast refresh)
    if (!force && now - (_lastFetchAtRef.current || 0) < 2500) {
      return null;
    }

    // Deduplicate concurrent calls
    if (_inflightRef.current) {
      return _inflightRef.current;
    }

    setLoading(true);
    _lastFetchAtRef.current = now;

    const p = (async () => {
      try {
        const data = await apiGetZodiacCrushMissionsToday({ force });
        // If backend includes profile snapshot, keep it in sync
        if (data && typeof data === 'object' && data.profile) {
          setProfile(data.profile);
        }

        const nextStar = pickHintBoosterCount(data);
        if (nextStar != null) setBoostersStar(Number(nextStar) || 0);

        // Level rewards unlocks (optional)
        const nextRewards = pickRewardsUnlocked(data);
        if (nextRewards) setRewardsUnlocked(nextRewards);
        // Fallback: if missions endpoint doesn't include profile, fetch it once.
        if (!data?.profile) {
          try {
            const p = await apiGetZodiacCrushProfile();
            if (p && typeof p === 'object' && p.profile) {
              setProfile(p.profile);
            }
            // ⭐ fallback: profile snapshot may include boostersInventory / boosters
            const nextStar = pickHintBoosterCount(p);
            if (nextStar != null) setBoostersStar(Number(nextStar) || 0);

            const nextRewards = pickRewardsUnlocked(p);
            if (nextRewards) setRewardsUnlocked(nextRewards);
          } catch (e) {
            // ignore
          }
        }
        const nextList = Array.isArray(data?.missions) ? data.missions : [];

// Si el backend rota el día (nuevas misiones diarias), limpiamos snapshot viejo para que NO aparezca "combo" viejo al entrar
const nextDay = data?.day ? String(data.day) : null;
if (nextDay && _missionsDayRef.current && _missionsDayRef.current !== nextDay) {
  _missionsRef.current = [];
  setMissions([]);
  _bestCascadeDepthRef.current = 0;
}
if (nextDay) _missionsDayRef.current = nextDay;

        // Diff vs previous snapshot to detect newly completed missions
        const prevList = Array.isArray(_missionsRef.current) ? _missionsRef.current : [];
        const prevByKey = new Map(prevList.filter(Boolean).map((m) => [m.id || m.type, m]));

        const newlyCompletedIds = [];
        for (const m of nextList) {
          if (!m) continue;
          const key = m.id || m.type;
          const prev = prevByKey.get(key);
          const wasDone = !!prev?.done;
          const isDone = !!m.done;
          if (!wasDone && isDone) newlyCompletedIds.push(m.id || m.type);
        }

        const mergedServer = mergeMissionsPreferMaxProgress(nextList, _missionsRef.current);
const deduped = dedupeMissionsByType(mergedServer);
_missionsRef.current = deduped;
setMissions(deduped);

        if (newlyCompletedIds.length && typeof onMissionCompleted === 'function') {
          const byKey = new Map(nextList.filter(Boolean).map((m) => [m.id || m.type, m]));
          const completedMissions = newlyCompletedIds.map((k) => byKey.get(k)).filter(Boolean);
          const xpGained = completedMissions.reduce((acc, m) => acc + (Number(m?.reward?.xp) || 0), 0);
          onMissionCompleted({ completedIds: newlyCompletedIds, completedMissions, xpGained, missions: nextList });
        }

        const isAllDone = Array.isArray(nextList) && nextList.length > 0 && nextList.every((m) => !!m?.done);
        if (isAllDone && typeof onAllCompleted === 'function') {
          onAllCompleted({ missions: nextList });
        }

        return data;
      } catch (e) {
        warnOnce('[ZodiacCrush] missions/today unauthorized or failed', e);
        return null;
      } finally {
        setLoading(false);
        _inflightRef.current = null;
      }
    })();

    _inflightRef.current = p;
    return p;
  }, [onMissionCompleted, onAllCompleted]);

  

  useEffect(() => {
    // React 18 StrictMode mounts/unmounts twice in dev; prevent double-fetch spam
    if (_didInitRef.current) return;
    _didInitRef.current = true;

    let alive = true;
    (async () => {
      const r = await fetchToday();
      if (!alive) return;

      const alreadyAllDone =
        Array.isArray(r?.missions) && r.missions.length > 0 && r.missions.every((m) => !!m.done);

      if (alreadyAllDone && typeof onAllCompleted === 'function') {
        onAllCompleted({ missions: r.missions });
      }
    })();

    return () => {
      alive = false;
      if (_refreshTimerRef.current) {
        window.clearTimeout(_refreshTimerRef.current);
        _refreshTimerRef.current = null;
      }
    };
  }, [fetchToday, onAllCompleted]);

useEffect(() => {
  // Rewards are currently surfaced via toast + HUD.
  // Booster consumption / sync is handled locally per run.
  // This hook is intentionally a no-op to avoid state coupling.
}, [rewardsUnlocked]);

  return {
    missions,
    loading,
    allDone,
    fetchToday,
    profile,
    rewardsUnlocked,
    boostersStar,
  };
}



// =========================
// Pure helpers (module-scope)
// =========================




function zcFindFirstValidSwap(b) {
  if (!b || !Array.isArray(b.grid)) return null;
  const rows = Number(b.rows || 0) || 0;
  const cols = Number(b.cols || 0) || 0;
  if (rows <= 0 || cols <= 0) return null;

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const a = { r, c };

      if (c + 1 < cols) {
        const b2 = { r, c: c + 1 };
        const res = swapWithEvents(b, a, b2);
        if (res?.valid) return { a, b: b2 };
      }

      if (r + 1 < rows) {
        const b2 = { r: r + 1, c };
        const res = swapWithEvents(b, a, b2);
        if (res?.valid) return { a, b: b2 };
      }
    }
  }

  return null;
}

function zcApplyRowBomb(b, rowIndex) {
  if (!b || !Array.isArray(b.grid) || !Number.isFinite(rowIndex)) return b;
  const rows = Number(b.rows || 0) || 0;
  const cols = Number(b.cols || 0) || 0;
  if (rowIndex < 0 || rowIndex >= rows || cols <= 0) return b;

  const nextGrid = b.grid.map((r) => (Array.isArray(r) ? r.slice() : r));
  for (let c = 0; c < cols; c += 1) {
    if (nextGrid[rowIndex]) nextGrid[rowIndex][c] = null;
  }
  return { ...b, grid: nextGrid };
}

function ZodiacCrushScreenInner() {
  const operationOwnerId = String(getAuthUserSnapshot()?.id || '').trim();
  useEffect(() => {
    syncZodiacCrushOperationOwner(operationOwnerId);
  }, [operationOwnerId]);
  // Detect route changes even when this screen is kept mounted (some parent layouts keep it alive)
  const location = useLocation();
  const navigate = useNavigate();
const routeKey = `${location.pathname}|${location.search}`;
const lastRouteKeyRef = useRef(routeKey);

  
  const getQueryLevel = (search) => {
  try {
    const sp = new URLSearchParams(String(search || ''));
    const v = Number(sp.get('level') || 0) || 0;
    return v > 0 ? v : 0;
  } catch {
    return 0;
  }
};


  
const [levelProgress, setLevelProgress] = useState(() => ({ ...EMPTY_LEVEL_PROGRESS }));

const levelProgressRef = useRef(EMPTY_LEVEL_PROGRESS);
// Tracks which level the current `levelProgress` belongs to (prevents stale carry-over on next level)
const levelProgressLevelRef = useRef(0);

useEffect(() => {
  levelProgressRef.current = levelProgress;
}, [levelProgress]);

// 🧼 Cuando salís del juego (a mapa/mundos), limpiá el progreso del nivel
// para que no se “cachee” visualmente el objetivo/progreso anterior.

  

  const ZC_LEVELS_CACHE_KEY = 'zc_map_levels_cache_v1';

  // --- Auth token helper (keeps this screen self-contained) ---
  // Inject premium UI CSS once (keeps the file self-contained)
  useEffect(() => {
    const id = 'zc_ui_gamer_css_v1';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.type = 'text/css';
    style.setAttribute('data-zc', '1');
    style.appendChild(document.createTextNode(ZC_UI_GAMER_CSS));
    document.head.appendChild(style);
    // keep it mounted across navigations (no cleanup)
  }, []);
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, []);
  // Mantener RNG estable durante la sesión para que el refill sea consistente.
    const seed = useMemo(() => `local_${Date.now()}`, []);
    const runStartMsRef = useRef(Number(seed.split('_')[1] || Date.now()));
  const rngRef = useRef(createRng(seed));
  const replayRecorderRef = useRef(createProtocolV1RunRecorder());
  const replaySelectionAttemptRef = useRef(0);

  const boardWrapRef = useRef(null);
  const [particles, setParticles] = useState([]); // {id,x,y,dx,dy,rot,scale,kind,element,colorA,colorB}
  // Start on map to avoid rendering lvl 1 goals before the profile/mapProgress arrives.
  const [zcView, setZcView] = useState('map'); // 'game' | 'map' | 'world'
  
  
  const [board, setBoard] = useState(() => createInitialBoard(rngRef.current));
  const [selected, setSelected] = useState(null);
  const [movesLeft, setMovesLeft] = useState(20);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  // Local boosters state for UI (⭐ hint)
  // Local boosters state for UI
// - hint: 💡 pista
// - rowBomb: 💣 explota una fila (elegís una ficha)
  const [boosters, setBoosters] = useState({ hint: 0, rowBomb: 0 });

  
  

// ⭐ Hint (pista) persistente: swap sugerido en el tablero
// shape: { a: {r,c}, b: {r,c} } | null
const [hintSwap, setHintSwap] = useState(null);   // { a:{r,c}, b:{r,c} }
const hintSourceRef = useRef(null); // 'explicit' | 'idle' | null
const idleHintTimerRef = useRef(null);


  

  const [mapWorld, setMapWorld] = useState({ worldIndex: 0, signKey: 'aries' });
  const [selectedLevelFromMap, setSelectedLevelFromMap] = useState(null);

  const [missions, setMissions] = useState([]);

  // Forces a fresh run even when the user re-selects the SAME level from map/world
  const [playNonce, setPlayNonce] = useState(0);
  // Forces a fresh remount of the LevelGoals UI (prevents stale cached goals when navigating Home -> Worlds -> Game)
  const [goalsNonce, setGoalsNonce] = useState(0);

  // ✅ Track if the player actually played at least 1 valid move in this run
  // (avoid win modal firing due to cached/initial goal state)
  const _hasPlayedMoveRef = useRef(false);

  // ✅ Cuando salís del juego a mapa/mundos, limpiá el progreso local del nivel
  // para que no quede “cache visual” del objetivo/progreso del nivel anterior.
  useLayoutEffect(() => {
    if (zcView === 'game') return;

    setLevelProgress(() => ({ ...EMPTY_LEVEL_PROGRESS }));
    setSelected(null);
    setHintSwap(null);
    setPendingBooster(null);
  }, [zcView]);

  // -------------------------
// Helpers (safe resets)
// -------------------------


// =========================
// World/level tuning (difficulty + element bias)
// =========================
  const LEVELS_PER_WORLD = 20; // 12 worlds * 20 = 240
  
  useEffect(() => {
    // Default is 'map'. But if we deep-link into /zodiac-crush/play?level=2
    // we must NOT force map on mount (it causes the infamous "rebote").
    try {
      const path = String(location?.pathname || '').toLowerCase();
      const isPlayRoute = path.includes('zodiac-crush') && path.includes('/play');
      if (isPlayRoute) return;
    } catch (e) {
      // ignore
    }

    setZcView((v) => (v === 'game' ? v : 'map'));
  }, [location?.pathname]);

  // 🔥 Hard reset when navigating away to Home and back, even if React keeps this screen mounted.
  // This fixes the “cached level goal/progress (e.g. 250 score)” bug.
  useEffect(() => {
    const prevKey = lastRouteKeyRef.current;
    if (prevKey === routeKey) return;
    lastRouteKeyRef.current = routeKey;

    // Only act when we're (re)entering the ZodiacCrush route.
    const isZodiacCrushRoute = String(location.pathname || '').toLowerCase().includes('zodiac');
    if (!isZodiacCrushRoute) return;

    // Force a fresh UI + run state.
    try {
      setGoalsNonce(Date.now());
      setPlayNonce(Date.now());

      // Clear any stale progress immediately (prevents goal widgets flashing old values)
      setLevelProgress(() => ({ ...EMPTY_LEVEL_PROGRESS }));
      scoreRef.current = 0;
      setScore(0);
      setMovesLeft(
        getMovesForLevel(
          (typeof normalizeAbsoluteLevel === 'function')
            ? normalizeAbsoluteLevel(levelToPlayNum, activeSignKey)
            : levelToPlayNum
        )
      );
      setLevelProgress(EMPTY_LEVEL_PROGRESS);
_hasPlayedMoveRef.current = false;
      setRunOver(false);
      runSubmittedRef.current = false;
      runSubmissionPromiseRef.current = null;
      setLevelWin(null);
      setSelected(null);
      setHintSwap(null);
      setPendingBooster(null);

      // Full run reset (fresh board + counters)
      resetRun({
        toast: false,
        seedPrefix: `${activeSignKey}_w${activeWorldIndex}_lvl${levelToPlayNum}`,
      });
    } catch (e) {
      // ultra-safe: never crash on navigation
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  const enteringGameRef = useRef(false);
  

  // Tracks the *current* view without relying on stale closures (pageshow handlers).
const zcViewRef = useRef('map');
useEffect(() => {
  zcViewRef.current = zcView;
}, [zcView]);

// Timer to keep the entering-game guard alive long enough for Safari/iOS pageshow quirks.
const _enteringGameTimerRef = useRef(null);

// Raw world index coming from map navigation state (may be stale / desynced)
const mapWorldIndexRaw = useMemo(() => {
  return Number(mapWorld?.worldIndex || 0);
}, [mapWorld?.worldIndex]);

// Bias curve by level-in-world:
// - early: stronger theme (teaches the world element)
// - mid: balanced
// - late: subtle (avoids feeling "all the same")
const getBiasStrengthForLevel = useCallback((levelNumber) => {
  const lvl = Number(levelNumber || 1) || 1;
  const inWorld = ((lvl - 1) % LEVELS_PER_WORLD) + 1;

  let s;
  if (inWorld <= 5) s = 0.70;
  else if (inWorld <= 12) s = 0.62;
  else s = 0.55;

  return Math.max(0, Math.min(0.85, s));
}, [LEVELS_PER_WORLD]);

  // Active world/sign context (used to bias board generation + stable seeding)
  // ✅ Derivar world/sign desde el nivel absoluto (1..240) cuando sea posible.
  // Evita desync: abrir Tauro pero seguir con sign=aries.
  // (Moved below after levelToPlayNum)

// Map navigation (kept local to this screen)
  const openMap = useCallback(() => setZcView('map'), []);

const openWorld = useCallback(({ worldIndex, signKey } = {}) => {
  setMapWorld({
    worldIndex: Number(worldIndex) || 0,
    signKey: String(signKey || 'aries'),
  });
  setZcView('world');
}, []);

const playLevelFromMap = useCallback((payload) => {
  const n =
    typeof payload === 'number'
      ? Number(payload)
      : Number(payload?.level);

  if (!Number.isFinite(n) || n <= 0) return;
  // ✅ Mantener mapWorld sincronizado con el nivel elegido (n es absoluto)
try {
  const wi = Math.max(0, Math.min(11, Math.floor((n - 1) / 20)));
  const sk = WORLD_ORDER[wi] || 'aries';
  setMapWorld((prev) => {
    const curW = Number(prev?.worldIndex || 0) || 0;
    const curS = String(prev?.signKey || '').trim().toLowerCase();
    if (curW === wi && curS === sk) return prev;
    return { worldIndex: wi, signKey: sk };
  });
} catch {}

  // ✅ IMPORTANTÍSIMO: si el mapa manda signKey/worldIndex, persistilos ANTES de entrar al game
  try {
    const nextSign =
      (typeof payload === 'object' && payload)
        ? String(payload.signKey || '')
        : '';

    const nextWorldIndex =
      (typeof payload === 'object' && payload)
        ? Number(payload.worldIndex)
        : NaN;

    if (nextSign) {
      setMapWorld((prev) => ({
        worldIndex: Number.isFinite(nextWorldIndex) ? nextWorldIndex : (Number(prev?.worldIndex || 0) || 0),
        signKey: String(nextSign || prev?.signKey || 'aries'),
      }));
    } else if (Number.isFinite(nextWorldIndex)) {
      setMapWorld((prev) => ({
        worldIndex: nextWorldIndex,
        signKey: String(prev?.signKey || 'aries'),
      }));
    }
  } catch (_) {}

  // 🛡️ MARCAMOS ENTRADA INTENCIONAL AL GAME
  enteringGameRef.current = true;
  if (_enteringGameTimerRef.current) window.clearTimeout(_enteringGameTimerRef.current);
  _enteringGameTimerRef.current = window.setTimeout(() => {
    enteringGameRef.current = false;
    _enteringGameTimerRef.current = null;
  }, 1200);

  try {
    flushSync(() => {
      setSelectedLevelFromMap(n);
      setZcView('game');
    });
  } catch {
    setSelectedLevelFromMap(n);
    setZcView('game');
  }

  setPlayNonce(Date.now());
  setGoalsNonce(Date.now());
}, []);

  // 🎮 Deep-link support: /zodiac-crush/play?level=2
  // This prevents bouncing back to map on mobile/Safari when opening play URLs.
  useEffect(() => {
    try {
      const path = String(location?.pathname || '').toLowerCase();
      const isPlayRoute = path.includes('zodiac-crush') && path.includes('/play');
      if (!isPlayRoute) return;

      const qs = new URLSearchParams(String(location?.search || ''));
      const raw = qs.get('level');
      const n = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isFinite(n) || n <= 0) return;

      // If we're already there, do nothing.
      if (zcView === 'game' && Number(selectedLevelFromMap) === n) return;

      // Mark intentional enter to protect against iOS pageshow quirks.
      enteringGameRef.current = true;
      if (_enteringGameTimerRef.current) window.clearTimeout(_enteringGameTimerRef.current);
      _enteringGameTimerRef.current = window.setTimeout(() => {
        enteringGameRef.current = false;
        _enteringGameTimerRef.current = null;
      }, 1200);

      // Enter game for that level.
      setSelectedLevelFromMap(n);
      setZcView('game');
      setPlayNonce(Date.now());
      setGoalsNonce(Date.now());
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, location?.pathname, location?.search]);
  
  
  const {
    missions: hookMissions,
    allDone,
    fetchToday,
    profile,
    rewardsUnlocked,
    boostersStar,
  } = useZodiacCrushMissions({
    onMissionCompleted: ({ completedIds, xpGained }) => {
      pulseMissionIds(completedIds);
      if (xpGained > 0) showXpFly(xpGained);

      playCrystalSparkle(comboStepRef.current);
      playMissionCompleteCue();
      safeVibrate([10, 22, 10]);
      showToast(zcText('✨ Misión completada', '✨ Mission complete'));
    },
    onAllCompleted: () => {
      setAllDoneBadge(true);
      playCrystalSparkle(6);
      playMissionCompleteCue();
      safeVibrate([14, 28, 14]);
      window.setTimeout(() => playCrystalSparkle(9), 120);
      showToast(zcText('🏆 ¡Misiones completadas!', '🏆 Missions complete!'));
    },
  });

  // Keep local missions state in sync with backend/hook snapshot
  useEffect(() => {
  if (Array.isArray(hookMissions) && hookMissions.length > 0) {
    setMissions((prev) =>
      mergeMissionsPreferMaxProgress(hookMissions, prev)
    );
  }
}, [hookMissions]);

  // ------------------------------------------------------------
  // Bridge: keep *level goal* progress in sync with mission deltas
  // ------------------------------------------------------------
  // The engine reports per-move deltas (score, stars, stripes, clears, cascades).
  // Level-goal completion + win banner are driven by `levelProgress`/`mergedLevelProgress`.
  // If we don't sync those counters here, UI can show "LISTO" while `levelGoalsFrac.done` stays 0.
  // ------------------------------------------------------------
  // Bridge: keep *level goal* progress in sync with mission deltas
  // ------------------------------------------------------------
  // The engine reports per-move deltas (score, stars, stripes, clears, cascades).
  // Level-goal completion + win banner are driven by `levelProgress`/`mergedLevelProgress`.
  // If we don't sync those counters here, UI can show "LISTO" while `levelGoalsFrac.done` stays 0.

  const levelToPlay = useMemo(() => {
    // 1️⃣ Prioridad absoluta: nivel elegido desde el mapa
    if (selectedLevelFromMap != null) {
      const n = Number(selectedLevelFromMap);
      return Number.isFinite(n) && n > 0 ? n : null;
    }

    // 2️⃣ Preferimos el nivel “actual” (lo lógico para jugar) por sobre el máximo desbloqueado.
    // Esto evita el bug de “me manda al 240” cuando el backend tiene maxUnlockedLevel alto.
    const currentFromProfile =
      Number(profile?.zodiacCrushMapProgress?.currentLevel) ||
      Number(profile?.mapProgress?.currentLevel) ||
      Number(profile?.profile?.zodiacCrushMapProgress?.currentLevel) ||
      Number(profile?.profile?.mapProgress?.currentLevel) ||
      Number(profile?.currentLevel) ||
      null;

    if (Number.isFinite(currentFromProfile) && currentFromProfile > 0) {
      return currentFromProfile;
    }

    // 3️⃣ Fallback: si no hay currentLevel, recién ahí usamos el máximo desbloqueado.
    const maxFromProfile =
      Number(profile?.zodiacCrushMapProgress?.maxUnlockedLevel) ||
      Number(profile?.mapProgress?.maxUnlockedLevel) ||
      Number(profile?.profile?.zodiacCrushMapProgress?.maxUnlockedLevel) ||
      Number(profile?.profile?.mapProgress?.maxUnlockedLevel) ||
      Number(profile?.maxUnlockedLevel) ||
      null;

    return Number.isFinite(maxFromProfile) && maxFromProfile > 0 ? maxFromProfile : null;
  }, [selectedLevelFromMap, profile]);

  const levelToPlayNum = useMemo(() => {
    try {
      if (levelToPlay == null) return null;

      // Most of the time `levelToPlay` is already a number.
      const direct = Number(levelToPlay);
      if (Number.isFinite(direct) && direct > 0) {
        const v = Math.floor(direct);
        return Math.max(1, Math.min(240, v));
      }

      // Ultra-safe fallback: extract the first number from any string-like input.
      const raw = String(levelToPlay);
      const m = raw.match(/\d+/);
      const n = m ? parseInt(m[0], 10) : NaN;

      if (!Number.isFinite(n) || n <= 0) return null;
      return Math.max(1, Math.min(240, Math.floor(n)));
    } catch {
      return null;
    }
  }, [levelToPlay]);

  // Active world/sign context (used to bias board generation + stable seeding)
  // ✅ Derivar world/sign desde el nivel absoluto (1..240) cuando sea posible.
  // Evita desync: abrir Tauro pero seguir con sign=aries.

  const absLevelForContext = useMemo(() => {
    const n = Number(levelToPlayNum || selectedLevelFromMap || 0) || 0;
    return n > 0 ? Math.floor(n) : 0;
  }, [levelToPlayNum, selectedLevelFromMap]);

  const activeWorldIndex = useMemo(() => {
    if (absLevelForContext > 0) return Math.max(0, Math.min(11, Math.floor((absLevelForContext - 1) / 20)));
    return Math.max(0, Math.min(11, Number(mapWorldIndexRaw || 0) || 0));
  }, [absLevelForContext, mapWorldIndexRaw]);

  const activeSignKey = useMemo(() => {
    if (absLevelForContext > 0) return WORLD_ORDER[activeWorldIndex] || 'aries';

    // fallback ultra-safe
    let s = String(mapWorld?.signKey || 'aries').trim().toLowerCase();
    if (s === 'geminis') s = 'gemini';
    if (s === 'capricornio') s = 'capricorn';
    return s || 'aries';
  }, [absLevelForContext, activeWorldIndex, mapWorld?.signKey]);

  const activeElementKey = useMemo(() => {
    try {
      // UI world keys are English while engine SIGN_ELEMENT uses canonical
      // Spanish keys for several signs. Use the shared world profile alias
      // normalization instead of silently falling back to neutral generation.
      const raw = SIGN_ELEMENT?.[activeSignKey] || getZcWorldProfile(activeSignKey)?.biasElement;
      return normalizeElementKey(raw) || null;
    } catch {
      return null;
    }
  }, [activeSignKey]);

  const worldVisualProfile = useMemo(
    () => getWorldVisualProfile(activeSignKey),
    [activeSignKey]
  );

  // Gameplay back returns to the level list for the world being played.
  // Keep the selected world synchronized even when the game was opened by a
  // deep link or an absolute level number.
  const openCurrentWorldLevelList = useCallback(() => {
    const level = Number(levelToPlayNum || selectedLevelFromMap || 0) || 0;
    const worldIndex = level > 0
      ? Math.max(0, Math.min(11, Math.floor((level - 1) / LEVELS_PER_WORLD)))
      : activeWorldIndex;
    const signKey = WORLD_ORDER[worldIndex] || activeSignKey || 'aries';

    setMapWorld((prev) => ({
      ...(prev || {}),
      worldIndex,
      signKey,
    }));
    setZcView('world');
  }, [activeSignKey, activeWorldIndex, levelToPlayNum, selectedLevelFromMap]);

  useEffect(() => {
    if (zcView !== 'game') return undefined;
    return registerBackHandler(() => {
      openCurrentWorldLevelList();
      return true;
    });
  }, [openCurrentWorldLevelList, zcView]);
  
  useEffect(() => {
  try {
    // Solo cuando estamos jugando un level con deep-link
    if (String(location?.pathname || '') !== '/zodiac-crush/play') return;

    const curUrlLevel = getQueryLevel(location?.search);
    const curLevel = Number(levelToPlayNum || 0) || 0;
    if (!curLevel) return;

    // Si el state dice otro nivel, actualizamos la URL (replace para no llenar el historial)
    if (curUrlLevel !== curLevel) {
      navigate(
        `/zodiac-crush/play?level=${encodeURIComponent(String(curLevel))}`,
        { replace: true }
      );
    }
  } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location?.pathname, location?.search, levelToPlayNum]);

  // Keep URL in sync with the level actually being played.
  // Without this, refreshing reloads whatever stale `?level=` is in the address bar.
  useEffect(() => {
    try {
      if (String(zcView || '').toLowerCase() !== 'game') return;

      const lvl = Math.max(1, Math.floor(Number(levelToPlayNum || 0) || 0));
      if (!lvl) return;

      const sp = new URLSearchParams(String(location?.search || ''));
      const cur = Math.max(1, Math.floor(Number(sp.get('level') || 0) || 0));

      if (cur === lvl) return;

      sp.set('level', String(lvl));
      const next = `${String(location?.pathname || '/zodiac-crush/play')}?${sp.toString()}`;
      navigate(next, { replace: true });
    } catch (_) {
      // ignore
    }
  }, [zcView, levelToPlayNum, location?.pathname, location?.search, navigate]);

// --- API base helper ---
// In dev, relative fetch('/api/...') hits the CRA dev server (localhost:3000) unless a proxy is configured.
// This helper forces requests to the backend origin when running locally.
const zcApiBase = useMemo(() => REST_API_ORIGIN, []);

const zcApiUrl = useCallback((path) => {
  const p = String(path || '');
  if (!p) return p;
  if (!zcApiBase) return p; // prod / proxy case

  const cleanBase = String(zcApiBase).replace(/\/$/, '');
  const cleanPath = p.startsWith('/') ? p : `/${p}`;

  return `${cleanBase}${cleanPath}`;
}, [zcApiBase]);

// -------------------------
  // Carry (persist progress across retries)
  // -------------------------
  const [levelCarry, setLevelCarry] = useState(null);

  const carryGoals =
  (levelCarry?.goals && typeof levelCarry.goals === 'object')
    ? levelCarry.goals
    : null;

const mergedLevelProgress = useMemo(() => {
  const curLvl = Number(levelToPlayNum || 0) || 0;
const runRaw =
  (curLvl && levelProgressLevelRef.current === curLvl && levelProgress && typeof levelProgress === 'object')
    ? levelProgress
    : {};

  const carryRaw =
    (carryGoals && typeof carryGoals === 'object')
      ? carryGoals
      : null;

  // Normalize keys so goal checks always see the camelCase fields.
  // This prevents situations where UI shows 1/1 ✅ but levelGoalsFrac reads 0.
  const normalizeKey = (k) => {
  // Resistente a casing/separadores/legacy
  let key = String(k || '').trim();
  if (!key) return key;

  key = key
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

  switch (key) {
    // score
    case 'score_reach':
    case 'reach_score':
    case 'final_score':
    case 'finalscore':
    case 'best_score':
    case 'bestscore':
    case 'score':
      return 'score';

    // stars
    case 'stars_created':
    case 'starscreated':
    case 'make_star':
    case 'make_stars':
    case 'created_stars':
    case 'createdstars':
    case 'star':
    case 'stars':
      return 'starsCreated';

    // stripes
    case 'stripes_activated':
    case 'stripesactivated':
    case 'activate_stripe':
    case 'activated_stripes':
    case 'activatedstripes':
    case 'stripe':
    case 'stripes':
      return 'stripesActivated';

    // cascades (todo mapea a 'cascades' para goal-checking)
    case 'cascades':
    case 'cascade':
    case 'cascade_count':
    case 'cascadecount':
    case 'cascades_reach':
    case 'cascade_depth':
    case 'cascadedepth':
    case 'max_cascade':
    case 'maxcascade':
    case 'best_cascade':
    case 'bestcascade':
    case 'cascades_best':
    case 'cascadesbest':
      return 'cascades';

    // clears by element
    case 'cleared_fire':
    case 'clear_element_fire':
    case 'clearedfire':
      return 'clearedFire';

    case 'cleared_water':
    case 'clear_element_water':
    case 'clearedwater':
      return 'clearedWater';

    case 'cleared_air':
    case 'clear_element_air':
    case 'clearedair':
      return 'clearedAir';

    case 'cleared_earth':
    case 'clear_element_earth':
    case 'clearedearth':
      return 'clearedEarth';

    // generic clears
    case 'cleared_cells':
    case 'clearedcells':
    case 'cleared':
    case 'clear':
      return 'clearedCells';

    default:
      return key; // devolvemos normalizado (por si querés loguearlo)
  }
};

  // Start from a normalized run snapshot
  const out = {};
  for (const [k, v] of Object.entries(runRaw)) {
    out[normalizeKey(k)] = v;
  }

  // If no carry, return normalized run
  if (!carryRaw) return out;

  // Merge carry by ADDING onto the run snapshot (carry is cumulative across retries)
  for (const [k, v] of Object.entries(carryRaw)) {
    const kk = normalizeKey(k);
    const base = Number(v);
    if (!Number.isFinite(base)) continue;

    const cur = Number(out[kk]);
    out[kk] = (Number.isFinite(cur) ? cur : 0) + base;
  }

  return out;
}, [levelProgress, carryGoals, levelToPlayNum]);

  const fetchCarryForLevel = useCallback(async (lv) => {
    try {
      const t = getCentralAuthToken();
      const url = zcApiUrl(
        `/api/games/zodiac-crush/map/progress?includeCarry=1&carryLevel=${Number(lv)}`
      );

      const resp = await authenticatedRequest(url, {
        rawResponse: true,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        // If url is cross-origin (dev backend), use CORS + omit cookies; otherwise include.
        ...(String(url).startsWith('http') && !String(url).startsWith(window.location.origin)
          ? { credentials: 'omit', mode: 'cors' }
          : { credentials: 'include' }),
        diagSource: 'ZodiacCrushScreen.mapProgress',
      });

      const data = await resp.json().catch(() => null);
      setLevelCarry(data?.ok ? (data?.carry || null) : null);
    } catch {
      setLevelCarry(null);
    }
  }, [zcApiUrl]);

  useEffect(() => {
    const lv = Number(levelToPlayNum || 0) || 0;
    if (!lv) {
      setLevelCarry(null);
      return;
    }
    void fetchCarryForLevel(lv);
  }, [levelToPlayNum, fetchCarryForLevel]);
  




const upsertCachedLevelMeta = useCallback((levelMeta) => {
  try {
    const lvl = Number(levelMeta?.level || 0) || 0;
    if (!lvl) return;

    const raw = localStorage.getItem(ZC_LEVELS_CACHE_KEY) || '';
    let parsed = null;
    try {
      parsed = raw ? JSON.parse(raw) : null;
    } catch {
      parsed = null;
    }

    const curLevels = Array.isArray(parsed?.levels) ? parsed.levels : [];
    const nextLevels = curLevels.slice();

    const idx = nextLevels.findIndex((x) => Number(x?.level || 0) === lvl);
    if (idx >= 0) nextLevels[idx] = { ...nextLevels[idx], ...levelMeta };
    else nextLevels.push(levelMeta);

    localStorage.setItem(
      ZC_LEVELS_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), levels: nextLevels })
    );

    // ✅ Bust goal computation so `levelGoals` re-reads cached objectives immediately.
    // (Without this, React may keep the old memo result and the level can look "stuck".)
    try { setGoalsNonce(Date.now()); } catch (_) {}
  } catch {
    // ignore
  }
}, [setGoalsNonce]);


const levelGoals = useMemo(() => {
  if (zcView !== 'game') return [];

  // 👇 referencia explícita para invalidar cache cuando el nivel/meta cambia
  void goalsNonce;

  try {
    const lvlRaw = Number(levelToPlayNum) || 1;

// ✅ CLAVE: convertir “nivel dentro del mundo” (1..20) + sign -> nivel absoluto (1..240)
const lvl = (typeof normalizeAbsoluteLevel === 'function')
  ? normalizeAbsoluteLevel(lvlRaw, activeSignKey)
  : lvlRaw;

if (typeof normalizeAbsoluteLevel !== 'function') {
  // eslint-disable-next-line no-console
  console.warn('[ZC levelGoals] normalizeAbsoluteLevel missing (import bug)');
}

if (!lvl) return [];

    // Prefer backend level meta (authoritative targets) when cached
    const CACHE_KEY = 'zc_map_levels_cache_v1';

    const normalizeSignSlug = (raw) => {
  let s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  try { s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (_) {}
  return s;
};

const readCachedLevelMeta = (level, signKey) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY) || '';
    const parsed = raw ? JSON.parse(raw) : null;
    const levels = Array.isArray(parsed?.levels) ? parsed.levels : [];

    const lvlNum = Number(level);
    const wantSign = normalizeSignSlug(signKey);

    // Prefer exact match by (level + sign)
    if (wantSign) {
      const hit = levels.find(
        (x) => Number(x?.level || 0) === lvlNum && normalizeSignSlug(x?.sign) === wantSign
      );

      // 🔥 Si pedimos signKey, NO podemos caer a otro signo.
      return hit || null;
    }

    // No signKey provided: safe fallback by level only
    return levels.find((x) => Number(x?.level || 0) === lvlNum) || null;
  } catch {
    return null;
  }
};

    const buildGoalsFromMeta = (levelMeta) => {
      const objectives = Array.isArray(levelMeta?.objectives) ? levelMeta.objectives : [];
      if (!objectives.length) {
        // ...
        return getGoalsForLevel({ level: lvl, signKey: activeSignKey, worldIdentityVersion: WORLD_IDENTITY_V2.version });
      }

      const norm = (t) => String(t || '').trim().toLowerCase();

      const goals = objectives
        .map((o, idx) => {
          const t = norm(o?.type);
          const target = Math.max(1, Number(o?.target || 0) || 1);

          if (t === 'reach_score' || t === 'score_reach' || t === 'score') {
            return {
              id: `${lvl}_score_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'score',
              target,
              label: zcText('Sumá puntos', 'Score points'),
              icon: '🎯',
            };
          }

          if (t === 'make_star' || t === 'make_stars' || t === 'star' || t === 'stars_created') {
            return {
              id: `${lvl}_starsCreated_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'starsCreated',
              target,
              label: zcText('Creá estrellas', 'Create stars'),
              icon: '🌟',
            };
          }

          if (t === 'activate_stripe' || t === 'stripes_activated') {
            return {
              id: `${lvl}_stripesActivated_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'stripesActivated',
              target,
              label: zcText('Activá rayos', 'Activate lightning tiles'),
              icon: '⚡',
            };
          }

          if (t === 'cascades' || t === 'cascade' || t === 'max_cascade' || t === 'max_cascades') {
            return {
              id: `${lvl}_cascades_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'cascades',
              target,
              label: zcText('Lográ cascadas', 'Create cascades'),
              icon: '🌊',
            };
          }

          if (t === 'clear_element_fire' || t === 'clear_fire') {
            return {
              id: `${lvl}_clear_element_fire_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'clear_element_fire',
              target,
              label: zcText('Limpiá fuego', 'Clear fire'),
              icon: '🔥',
            };
          }
          if (t === 'clear_element_water' || t === 'clear_water') {
            return {
             id: `${lvl}_clear_element_water_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'clear_element_water',
              target,
              label: zcText('Limpiá agua', 'Clear water'),
              icon: '💧',
            };
          }
          if (t === 'clear_element_air' || t === 'clear_air') {
            return {
              id: `${lvl}_clear_element_air_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'clear_element_air',
              target,
              label: zcText('Limpiá aire', 'Clear air'),
              icon: '🌬️',
            };
          }
          if (t === 'clear_element_earth' || t === 'clear_earth') {
            return {
             id: `${lvl}_clear_element_earth_${idx}${activeSignKey ? `_${activeSignKey}` : ''}`,
              type: 'clear_element_earth',
              target,
              label: zcText('Limpiá tierra', 'Clear earth'),
              icon: '🪨',
            };
          }

          // Unknown objective -> skip (do NOT invent targets)
          return null;
        })
        .filter(Boolean);

      // If all objectives were unknown (or filtered out), NEVER return null.
      // Fallback to legacy/static goals so `levelGoalsFrac.total > 0` and completion can trigger.
      return goals.length
        ? goals
        : getGoalsForLevel({ level: lvl, signKey: activeSignKey, worldIdentityVersion: WORLD_IDENTITY_V2.version });
    };

    // ✅ Fuente de verdad: goals locales (zcLevelGoals.js)
// El meta cache del backend puede estar stale y mostrar objetivos distintos.
const localGoals = getGoalsForLevel({ level: lvl, signKey: activeSignKey, worldIdentityVersion: WORLD_IDENTITY_V2.version });
if (Array.isArray(localGoals) && localGoals.length) {
  return localGoals;
}

// Si por alguna razón localGoals viniera vacío, recién ahí probamos meta cache.
const meta = readCachedLevelMeta(lvl, activeSignKey);
const metaGoals = meta ? buildGoalsFromMeta(meta) : null;

if (Array.isArray(metaGoals) && metaGoals.length) {
  return metaGoals;
}

// Final fallback (legacy/static)
const fallbackGoals = getGoalsForLevel({ level: lvl, signKey: activeSignKey, worldIdentityVersion: WORLD_IDENTITY_V2.version });
    if (!Array.isArray(fallbackGoals) || fallbackGoals.length === 0) {
      return [];
    }

    return fallbackGoals;
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.warn('[ZC levelGoals] memo error; using fallback', { err: e?.message || String(e) });
    } catch (_) {}

    try {
      const lvlSafe = Number(levelToPlayNum) || 1;
      const fb = getGoalsForLevel({ level: lvlSafe, signKey: activeSignKey, worldIdentityVersion: WORLD_IDENTITY_V2.version });
      return Array.isArray(fb) ? fb : [];
    } catch {
      return [];
    }
  }
}, [zcView, levelToPlayNum, activeSignKey, goalsNonce]);

// Canonical goal snapshot shared by the panel and lifecycle completion checks.
// It deliberately uses the live resolver score so an async progress payload
// cannot regress a visibly completed score goal back to zero.
const canonicalGoalState = useMemo(
  () => deriveCanonicalGoalState({
    goals: levelGoals,
    score,
    runProgress: mergedLevelProgress,
  }),
  [levelGoals, score, mergedLevelProgress]
);

useEffect(() => {
  logCompletionTrace('canonicalGoalState updated', {
    score: Number(score || 0) || 0,
    canonicalGoalState,
    done: Number(canonicalGoalState?.done || 0) || 0,
    total: Number(canonicalGoalState?.total || 0) || 0,
    operationId: getZodiacCrushRunOperationId(operationOwnerId),
    levelId: Number(levelToPlayNum || 0) || 0,
  });
}, [canonicalGoalState, operationOwnerId, score, levelToPlayNum]);

/* Legacy diagnostic implementation retained below for historical context.
// Compute done/total for *level goals* using the local per-run counters.
// NOTE: `goalsDoneFraction` from the goals module expects a progress-array with `.done` flags,
// so we compute the fraction explicitly here.
const legacyLevelGoalsFrac = useMemo(() => {
  try {
    const goals = Array.isArray(levelGoals) ? levelGoals : [];

    // `mergedLevelProgress` puede venir stale (snapshot backend / async),
    // `levelProgress` es el contador por-run que actualiza `reportProgress`.
    // Mezclamos con MAX para que los goals nunca “queden atrás”.
    const pServer =
      (mergedLevelProgress && typeof mergedLevelProgress === 'object')
        ? mergedLevelProgress
        : {};
    const curLvl = Number(levelToPlayNum || 0) || 0;
const pRun =
  (curLvl && levelProgressLevelRef.current === curLvl && levelProgress && typeof levelProgress === 'object')
    ? canonicalLevelGoalProgress
    : {};

    const n = (x) => {
      const v = Number(x);
      return Number.isFinite(v) ? v : 0;
    };

    const p = {
      score: Math.max(n(pServer.score), n(pRun.score), n(score)),
      stripesActivated: Math.max(n(pServer.stripesActivated), n(pRun.stripesActivated)),
      starsCreated: Math.max(n(pServer.starsCreated), n(pRun.starsCreated)),
      cascades: Math.max(n(pServer.cascades), n(pRun.cascades)),
      clearedFire: Math.max(n(pServer.clearedFire), n(pRun.clearedFire)),
      clearedWater: Math.max(n(pServer.clearedWater), n(pRun.clearedWater)),
      clearedAir: Math.max(n(pServer.clearedAir), n(pRun.clearedAir)),
      clearedEarth: Math.max(n(pServer.clearedEarth), n(pRun.clearedEarth)),
      // opcional si existe
      clearedCells: Math.max(n(pServer.clearedCells), n(pRun.clearedCells)),
    };

    // Normaliza cualquier goal-type (snake/camel/legacy/meta) a una métrica canónica en `p`
    const normalizeGoalType = (raw) => {
      const t0 = String(raw || '').trim();
      // Lowercase + strip accents/diacritics
      let t = t0.toLowerCase();
      try {
        t = t.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      } catch (_) {}

      // Unificar separadores a underscores (score reach -> score_reach, etc.)
      t = t.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

      // score
      if (
        t === 'score' ||
        t === 'reach_score' ||
        t === 'score_reach' ||
        t === 'final_score' ||
        t.includes('score')
      ) return 'score';

      // stars
      if (
        t === 'starscreated' ||
        t === 'stars_created' ||
        t === 'make_star' ||
        t === 'make_stars' ||
        t.includes('star') ||
        t.includes('estrella')
      ) return 'starsCreated';

      // stripes
      if (
        t === 'stripesactivated' ||
        t === 'stripes_activated' ||
        t === 'activate_stripe' ||
        t.includes('stripe') ||
        t.includes('rayo')
      ) return 'stripesActivated';

      // cascades (depth/best/max)
      if (t === 'cascadedepth' || t === 'cascade_depth' || t.includes('cascadedepth')) return 'cascadeDepth';

      if (
        t === 'cascadesbest' ||
        t === 'cascades_best' ||
        t === 'best_cascade' ||
        t === 'bestcascade' ||
        t.includes('cascadesbest') ||
        t.includes('bestcascade')
      ) return 'cascadesBest';

      if (t === 'maxcascade' || t === 'max_cascade' || t.includes('maxcascade')) return 'maxCascade';

      // cascades count (generic)
      if (
        t === 'cascades' ||
        t === 'cascades_reach' ||
        (t.includes('cascade') &&
          !t.includes('cascadedepth') &&
          !t.includes('maxcascade') &&
          !t.includes('bestcascade') &&
          !t.includes('cascadesbest')) ||
        t.includes('cascada')
      ) return 'cascades';

      // element clears
      if (t === 'clear_element_fire' || (t.includes('clear') && t.includes('fire'))) return 'clearedFire';
      if (t === 'clear_element_water' || (t.includes('clear') && t.includes('water'))) return 'clearedWater';
      if (t === 'clear_element_air' || (t.includes('clear') && t.includes('air'))) return 'clearedAir';
      if (t === 'clear_element_earth' || (t.includes('clear') && t.includes('earth'))) return 'clearedEarth';

      // generic clears
      if (t.includes('clear')) return 'clearedCells';

      // fallback: si alguien ya manda el nombre canónico
      if (
        t0 === 'score' ||
        t0 === 'starsCreated' ||
        t0 === 'stripesActivated' ||
        t0 === 'cascades' ||
        t0 === 'cascadeDepth' ||
        t0 === 'maxCascade' ||
        t0 === 'cascadesBest' ||
        t0 === 'clearedFire' ||
        t0 === 'clearedWater' ||
        t0 === 'clearedAir' ||
        t0 === 'clearedEarth' ||
        t0 === 'clearedCells'
      ) {
        return t0;
      }

      // canonical lower (por si viene “starscreated” etc como “type”)
      if (t === 'starscreated') return 'starsCreated';
      if (t === 'stripesactivated') return 'stripesActivated';
      if (t === 'clearedfire') return 'clearedFire';
      if (t === 'clearedwater') return 'clearedWater';
      if (t === 'clearedair') return 'clearedAir';
      if (t === 'clearedearth') return 'clearedEarth';
      if (t === 'clearedcells') return 'clearedCells';

      return null;
    };

    const getValForType = (typeRaw) => {
      const key = normalizeGoalType(typeRaw);
      if (!key) return 0;

      // si no hubo move válido, no cuenta score (evita wins por flash/estado viejo)
      if (key === 'score' && !_hasPlayedMoveRef?.current) return 0;

      return n(p[key]);
    };

    const total = goals.length;

    // Build a single authoritative per-goal snapshot used for BOTH done-count and debug.
    // This prevents mismatches where `done` is computed with one logic and `perGoal` with another.
    const perGoal = (goals || []).map((g) => {
      // Tolerante a shapes distintos (cache/meta/backend)
      const rawType =
        g?.type ??
        g?.goalType ??
        g?.kind ??
        g?.objective ??
        g?.metric ??
        g?.key ??
        g?.name ??
        g?.id ??
        '';

      const type = String(rawType || '').trim();

      const rawTarget =
        g?.target ??
        g?.value ??
        g?.amount ??
        g?.count ??
        g?.need ??
        g?.required ??
        g?.goal ??
        g?.min ??
        0;

      const tNum = Number(rawTarget);
      const target = Math.max(1, Number.isFinite(tNum) ? tNum : 1);

      const key = normalizeGoalType(type);
      const v = getValForType(type); // uses normalizeGoalType + hasPlayedMove guard for score

      const ok = isGoalSatisfied(v, target);

      return {
        type,
        key,
        target,
        v,
        ok,
      };
    });

    const done = perGoal.reduce((acc, g) => acc + (g?.ok ? 1 : 0), 0);

    return { done, total };
  } catch {
    return { done: 0, total: 0 };
  }
}, [canonicalGoalState, levelGoals, mergedLevelProgress, levelProgress, levelToPlayNum, score, canonicalLevelGoalProgress]);
*/
  

  const [levelWin, setLevelWin] = useState(null); // { level, stars }
const [showLevelWinConfetti, setShowLevelWinConfetti] = useState(false);
const [levelWinBanner, setLevelWinBanner] = useState(null); // { key, level, stars, unlockedNext } | null

// IMPORTANT: banner + confetti must NOT share the same timer (they cancel each other)
const _levelWinBannerTimerRef = useRef(null);
  const _levelWinConfettiTimerRef = useRef(null);
  // ✅ Guard: prevent setState after unmount (timers/fetches)
const _isMountedRef = useRef(true);
  // Show BOTH the small banner FX and the modal with stars (single entry-point)
const showLevelWinUI = useCallback(({ level, stars = 3, unlockedNext = false } = {}) => {
  try {
    const lvl = Math.max(1, Math.floor(Number(level || 1) || 1));
    const st = Math.max(1, Math.min(3, Math.floor(Number(stars || 3) || 3)));

    // Modal (persistent until user action)
    logCompletionTrace('setWon modal state', { levelId: lvl, stars: st, operationId: getZodiacCrushRunOperationId(operationOwnerId) });
    setLevelWin({ level: lvl, stars: st });

    // Confetti burst (auto-hide) — keep separate from banner timer
    try {
      setShowLevelWinConfetti(true);

      if (_levelWinConfettiTimerRef.current) window.clearTimeout(_levelWinConfettiTimerRef.current);
      _levelWinConfettiTimerRef.current = window.setTimeout(() => {
  if (!_isMountedRef.current) return;
  setShowLevelWinConfetti(false);
  _levelWinConfettiTimerRef.current = null;
}, 2100);
    } catch (_) {}

    // Banner FX (auto-hide)
    setLevelWinBanner({
      key: `${lvl}_${Date.now()}`,
      level: lvl,
      stars: st,
      unlockedNext: !!unlockedNext,
    });

    try {
      if (_levelWinBannerTimerRef.current) window.clearTimeout(_levelWinBannerTimerRef.current);
      _levelWinBannerTimerRef.current = window.setTimeout(() => {
  if (!_isMountedRef.current) return;
  setLevelWinBanner(null);
  _levelWinBannerTimerRef.current = null;
}, 2400);
    } catch (_) {}
  } catch (e) {
    // ignore
  }
}, [operationOwnerId]);



const ZcConfettiBurst = ({ show, count = 26, zIndex = 1000002 }) => {
  const piecesRef = useRef(null);

  if (!piecesRef.current || piecesRef.current.length !== count) {
    piecesRef.current = Array.from({ length: count }).map((_, i) => {
      const left = (i * 3.7 + (i % 4) * 6 + (Math.random() * 6)) % 100;
      const delay = (i % 8) * 35;
      const x = (Math.random() - 0.5) * 160;
      const glyph = i % 5 === 0 ? '💎' : i % 3 === 0 ? '⭐' : '✨';
      const size = i % 6 === 0 ? 18 : i % 2 === 0 ? 14 : 16;
      return { id: i, left, delay, x, glyph, size };
    });
  }

  const pieces = piecesRef.current;

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex,
      }}
      aria-hidden="true"
    >
      {pieces.map((p) => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            fontSize: p.size,
            opacity: 0,
            filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.35))',
            animation: `zcConfettiFall 900ms ease-out ${p.delay}ms forwards`,
            '--x': `${p.x}px`,
          }}
        >
          {p.glyph}
        </span>
      ))}
    </div>
  );
}

// Legacy helper: keep for callers, but delegate to the single win entry-point.
// IMPORTANT: avoid duplicating timers/state with showLevelWinUI.
const fireLevelWinFx = useCallback((lvl, opts = {}) => {
  try {
    const level = Math.max(1, Math.floor(Number(lvl || 1) || 1));
    const advanced = !!(opts && opts.advanced);
    const stars = Math.max(1, Math.min(3, Math.floor(Number(opts?.stars ?? 3) || 3)));

    // Delegate to the canonical win UI (modal + banner + confetti)
    if (typeof showLevelWinUI === 'function') {
      showLevelWinUI({ level, stars, unlockedNext: advanced });
      return;
    }

    // Fallback (should rarely happen): keep minimal FX without sharing timers.
    setShowLevelWinConfetti(true);
    setLevelWinBanner({
      key: `${level}_${Date.now()}`,
      level,
      stars,
      unlockedNext: advanced,
    });

    if (_levelWinConfettiTimerRef.current) window.clearTimeout(_levelWinConfettiTimerRef.current);
    _levelWinConfettiTimerRef.current = window.setTimeout(() => {
      setShowLevelWinConfetti(false);
      _levelWinConfettiTimerRef.current = null;
    }, 2100);

    if (_levelWinBannerTimerRef.current) window.clearTimeout(_levelWinBannerTimerRef.current);
    _levelWinBannerTimerRef.current = window.setTimeout(() => {
      setLevelWinBanner(null);
      _levelWinBannerTimerRef.current = null;
    }, 2400);
  } catch {
    // ignore
  }
}, [showLevelWinUI]);

useEffect(() => {
  if (!levelWin) return;
  logCompletionTrace('win modal render', {
    levelId: levelWin.level,
    operationId: getZodiacCrushRunOperationId(operationOwnerId),
  });
}, [levelWin, operationOwnerId]);
  
  useEffect(() => {
  _isMountedRef.current = true;

  return () => {
    // mark unmounted first
    _isMountedRef.current = false;

    // clear win timers
    try {
      if (_levelWinBannerTimerRef.current) window.clearTimeout(_levelWinBannerTimerRef.current);
    } catch (_) {}
    try {
      if (_levelWinConfettiTimerRef.current) window.clearTimeout(_levelWinConfettiTimerRef.current);
    } catch (_) {}
    _levelWinBannerTimerRef.current = null;
    _levelWinConfettiTimerRef.current = null;

    // clear toast timer (avoid setToast on unmounted)
    try {
      if (toastTimerRef?.current) window.clearTimeout(toastTimerRef.current);
    } catch (_) {}
    try {
      if (toastTimerRef) toastTimerRef.current = null;
    } catch (_) {}
  };
}, []);
  
  
// Pending booster mode (user armed a booster and must pick a tile)
const [pendingBooster, setPendingBooster] = useState(null); // { type: 'rowBomb' } | null

const [busy, setBusy] = useState(false);
  const clearIdleHintTimer = useCallback(() => {
    if (idleHintTimerRef.current) {
      window.clearTimeout(idleHintTimerRef.current);
      idleHintTimerRef.current = null;
    }
  }, []);

  const clearHintForActivity = useCallback(() => {
    clearIdleHintTimer();
    hintSourceRef.current = null;
    setHintSwap(null);
  }, [clearIdleHintTimer]);

  const [toast, setToast] = useState('');
  const toastTimerRef = useRef(null);
  
  const [showLeaderboard, setShowLeaderboard] = useState(false);
// Default a GLOBAL para que no abra vacío
const [lbScope, setLbScope] = useState('global');
const [lbState, setLbState] = useState({ loading: false, error: null, entries: [], my: null });


const showToast = useCallback((msg) => {
  setToast(msg);

  try {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
  } catch (_) {}

  toastTimerRef.current = window.setTimeout(() => {
  if (!_isMountedRef.current) return;
  setToast('');
  toastTimerRef.current = null;
}, 1100);
}, []);

  // 🎯 Dedup toasts for level goals (prevents double-fire / StrictMode re-renders)
  const goalToastRef = useRef({ level: null, done: 0, total: 0 });

  const handleGoalCompletedToast = useCallback(({ done, total }) => {
    const d = Number(done || 0) || 0;
    const t = Number(total || 0) || 0;
    const lvl = levelToPlayNum;

    const prev = goalToastRef.current || { level: null, done: 0, total: 0 };

    // Reset tracking when level changes
    if (prev.level !== lvl) {
      goalToastRef.current = { level: lvl, done: d, total: t };
      // Don't toast on initial mount/render; only when progress increases.
      return;
    }

    // Only toast when "done" increases
    if (d > Number(prev.done || 0)) {
      if (d === t) {
        playObjectiveCompleteCue();
        safeVibrate([12, 24, 12]);
      }
      showToast(
        d === t
          ? zcText('🏆 ¡Objetivos del nivel completados!', '🏆 Level objectives complete!')
          : zcText(`🎯 Objetivo completado (${d}/${t})`, `🎯 Objective complete (${d}/${t})`)
      );
    }

    goalToastRef.current = { level: lvl, done: d, total: t };
}, [showToast, levelToPlayNum]);

  // --- Mobile perf: trim heavy FX on low-end devices (keeps gameplay fluid) ---
  const lowFxRef = useRef(false);
  useEffect(() => {
    try {
      const isSmall =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 520px)').matches;
      const mem = Number(navigator?.deviceMemory || 0) || 0; // Chrome-only (ok if 0)
      // Consider "low" when on small screens OR low memory devices.
      lowFxRef.current = !!isSmall || (mem > 0 && mem <= 4);
    } catch {
      lowFxRef.current = false;
    }
  }, []);

  useEffect(() => {
    scoreRef.current = Number(score || 0);
  }, [score]);
  

const handleUseHintBooster = async () => {
  primeAudio();
  clearIdleHintTimer();
  if (busy) return;

  const stock = Number(boosters?.hint || 0) || 0;
  if (stock <= 0) {
    showToast(zcText('No tenés pistas disponibles', 'No hints available'));
    return;
  }

  // 1) Intentar hint del engine
  let swap = null;
  try {
    const res = getHint?.(board);
    if (res?.a && res?.b) swap = res;
  } catch (e) {}

  // 2) Fallback robusto: buscar swap válido
  if (!swap) {
    try {
      const found = zcFindFirstValidSwap(board);
      if (!found) {
        showToast('No hay pistas disponibles');
        return;
      }
      swap = found;
    } catch (e) {
      showToast('No hay pistas disponibles');
      return;
    }
  }

  // 3) Guardar pista de forma PERSISTENTE (NO timeout)
  hintSourceRef.current = 'explicit';
  setHintSwap({
    a: { r: swap.a.r, c: swap.a.c },
    b: { r: swap.b.r, c: swap.b.c },
  });

  // 4) Consumir booster en backend
  try {
    const resp = await boosterOnServer('hint');
    if (!resp?.ok || resp?.used !== true) {
      hintSourceRef.current = null;
      setHintSwap(null);
      if (resp?.reason === 'no_stock') showToast(zcText('No tenés pistas disponibles', 'No hints available'));
      else showToast(zcText('No se pudo usar la pista', 'The hint could not be used'));
      return;
    }

    // Sync contadores desde backend
    // ✅ Sync contadores desde backend (preferir shape normalizado)
const bn = resp?.boostersNormalized;
if (bn && typeof bn === 'object') {
  setBoosters((prev) => ({
    ...prev,
    hint: Number.isFinite(Number(bn.hint)) ? Number(bn.hint) : prev.hint,
    rowBomb: Number.isFinite(Number(bn.rowBomb)) ? Number(bn.rowBomb) : prev.rowBomb,
  }));
} else if (resp?.boosters && typeof resp.boosters === 'object') {
  // Back-compat (si algún backend viejo manda "star")
  setBoosters((prev) => ({
    ...prev,
    hint:
      typeof resp.boosters.hint === 'number'
        ? Number(resp.boosters.hint)
        : (typeof resp.boosters.star === 'number' ? Number(resp.boosters.star) : prev.hint),
    rowBomb:
      typeof resp.boosters.rowBomb === 'number'
        ? Number(resp.boosters.rowBomb)
        : (typeof resp.boosters.row_bomb === 'number' ? Number(resp.boosters.row_bomb) : prev.rowBomb),
  }));
} else {
  // fallback ultra defensivo (si el server no trae inventario)
  setBoosters((prev) => ({ ...prev, hint: Math.max(0, (Number(prev.hint || 0) || 0) - 1) }));
}

    showToast(zcText('💡 Pista activada', '💡 Hint activated'));
  } catch (e) {
    hintSourceRef.current = null;
    setHintSwap(null);
    showToast(zcText('No se pudo usar la pista', 'The hint could not be used'));
  }
};

  
const handleUseRowBombBooster = useCallback(() => {
  // Toggle: if already armed, tapping again cancels.
  if (pendingBooster?.type === 'rowBomb') {
    setPendingBooster(null);
    showToast(zcText('Bomba cancelada', 'Bomb canceled'));
    return;
  }

  const stock = Number(boosters?.rowBomb || 0) || 0;
  if (stock <= 0) {
    showToast(zcText('No tenés bombas disponibles', 'No bombs available'));
    return;
  }

  setPendingBooster({ type: 'rowBomb' });
  showToast(zcText('💣 Elegí una ficha para explotar la fila', '💣 Choose a tile to clear its row'));
}, [boosters?.rowBomb, pendingBooster?.type, showToast]);

    const [clearingIds, setClearingIds] = useState(null);
    
  const [allDoneBadge, setAllDoneBadge] = useState(false);

  const [showHelp, setShowHelp] = useState(false);



// Keep the latest level in a ref to avoid stale closure / mismatched completion
const levelToPlayNumRef = useRef(levelToPlayNum);
useEffect(() => {
  levelToPlayNumRef.current = levelToPlayNum;
}, [levelToPlayNum]);


// ✅ Ensure the in-game screen always has authoritative objectives (backend -> localStorage cache)
useEffect(() => {
  if (zcView !== 'game') return;
  const lvl = Number(levelToPlayNum || 0) || 0;
  if (!lvl) return;

  let cancelled = false;

  (async () => {
    try {
      const t = getCentralAuthToken();

      // IMPORTANT: in dev, force the backend origin (avoids CRA/proxy returning index.html)
      const url = zcApiUrl(`/api/games/zodiac-crush/map/levels?from=${lvl}&to=${lvl}`);

      const resp = await authenticatedRequest(url, {
        rawResponse: true,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        // If url is same-origin we can include cookies; otherwise omit.
        ...(String(url).startsWith('http') && !String(url).startsWith(window.location.origin)
          ? { credentials: 'omit', mode: 'cors' }
          : { credentials: 'include' }),
        diagSource: 'ZodiacCrushScreen.mapLevels',
      });

      const data = await resp.json().catch(() => null);
      if (cancelled) return;
      if (!resp.ok || !data?.ok) return;

      const meta = Array.isArray(data?.levels) ? data.levels[0] : null;
      if (meta && Number(meta?.level || 0) === lvl) {
        // Writes into `zc_map_levels_cache_v1` so `zcLevelGoals.js` can read objectives.
        upsertCachedLevelMeta(meta);
      }
    } catch {
      // ignore
    }
  })();

  return () => {
    cancelled = true;
  };
}, [zcView, levelToPlayNum, zcApiUrl, upsertCachedLevelMeta]);

  const [runOver, setRunOver] = useState(false);
  // Keep the lifecycle in React state as well as the ref.  The ref is used by
  // the async completion pipeline, while the state value is the render-time
  // trigger that lets the completion effect re-evaluate after RESOLVING ->
  // READY without relying on a mutable ref change.
  const [runLifecycle, setRunLifecycle] = useState(RUN_LIFECYCLE.PLAYING);
  const runLifecycleRef = useRef(RUN_LIFECYCLE.PLAYING);
  const activeRunIdRef = useRef(null);
  const transitionRunLifecycle = useCallback((next) => {
    logCompletionTrace('runLifecycle transition', {
      from: runLifecycleRef.current,
      to: next,
      operationId: getZodiacCrushRunOperationId(operationOwnerId),
      levelId: Number(levelToPlayNumRef.current || levelToPlayNum || 0) || 0,
    });
    if (next === RUN_LIFECYCLE.WON) {
      logCompletionTrace('setWon', { operationId: getZodiacCrushRunOperationId(operationOwnerId) });
    }
    runLifecycleRef.current = next;
    setRunLifecycle(next);
  }, [operationOwnerId, levelToPlayNum]);
  const runSubmittedRef = useRef(false);
  const runSubmittedRunIdRef = useRef(null);
  const runSubmissionPromiseRef = useRef(null);
  const runSubmissionRunIdRef = useRef(null);
  const confirmedCompletionRef = useRef(null);

  // A level can be completed before the move budget is exhausted. The map
  // completion endpoint requires the replay-backed GameRun to exist first,
  // so serialize the run submission and reuse it from either completion path.
  const submitRunIfNeeded = useCallback((outcome = 'loss') => {
    const runId = activeRunIdRef.current;
    const runOperationId = getZodiacCrushRunOperationId(operationOwnerId);
    logCompletionTrace('submitRunIfNeeded called', {
      outcome,
      operationId: runOperationId,
      levelId: Number(levelToPlayNumRef.current || levelToPlayNum || 0) || 0,
      runLifecycle: runLifecycleRef.current,
    });
    if (runSubmissionPromiseRef.current && runSubmissionRunIdRef.current === runId) {
      return runSubmissionPromiseRef.current;
    }
    if (runSubmittedRef.current && runSubmittedRunIdRef.current === runId) {
      return Promise.resolve({ ok: true, alreadySubmitted: true });
    }

    runSubmittedRef.current = true;
    runSubmittedRunIdRef.current = runId;
    transitionRunLifecycle(RUN_LIFECYCLE.SUBMITTING_RUN);
    const promise = (async () => {
      try {
        const replay = getReplayPayloadForRunSubmission(replayRecorderRef.current);
        if (!replay) throw new Error('replay_unavailable');
        // Freeze all authoritative claims from the finalized replay contract.
        // UI counters remain presentation state and cannot drift independently.
        const finalRunSnapshot = buildFinalRunSnapshot(replay);
        logCompletionTrace('RUN_SUBMIT_START', {
          operationId: runOperationId,
          levelId: Number(levelToPlayNumRef.current || levelToPlayNum || 0) || 0,
          replayPresent: replay ? 'YES' : 'NO',
          replayProtocol: replay?.protocolVersion || replay?.version || 'none',
          actionCount: Array.isArray(replay?.actions) ? replay.actions.length : 0,
          scoreClaim: finalRunSnapshot.score,
          movesClaim: finalRunSnapshot.acceptedMoves,
          maxCascadeClaim: finalRunSnapshot.maxCascade,
        });
        const result = await apiSubmitZodiacCrushRun({
          finalScore: finalRunSnapshot.score,
          moves: finalRunSnapshot.acceptedMoves,
          durationMs: Date.now() - Number(runStartMsRef.current || Date.now()),
          maxCascade: finalRunSnapshot.maxCascade,
          cascadeDepth: finalRunSnapshot.maxCascade,
          cascades: finalRunSnapshot.maxCascade,
          replay: finalRunSnapshot.replay,
        }, {
          runOperationId,
        });
        if (!result?.ok) throw new Error('run_submission_failed');
        logCompletionTrace('submitRun resolved', {
          ok: true,
          operationId: runOperationId,
          levelId: Number(levelToPlayNumRef.current || levelToPlayNum || 0) || 0,
        });
        if (runId !== activeRunIdRef.current) return { ok: false, stale: true };
        let missionRefreshSucceeded = true;
        try {
          const refreshed = await fetchToday({ force: true });
          missionRefreshSucceeded = !!refreshed && refreshed?.ok !== false;
        } catch (_) {
          missionRefreshSucceeded = false;
        }
        logCompletionTrace('RUN_SUBMIT_MISSION_REFRESH', {
          operationId: runOperationId,
          succeeded: missionRefreshSucceeded,
        });
        if (runId !== activeRunIdRef.current) return { ok: false, stale: true };
        if (outcome === 'loss') transitionRunLifecycle(RUN_LIFECYCLE.LOST);
        return result;
      } catch (error) {
        const failure = classifyRunSubmitFailure(error);
        logCompletionTrace('submitRun resolved', {
          ok: false,
          reason: failure.category,
          httpStatus: failure.status,
          safeErrorCode: failure.code,
          safeErrorReason: failure.category,
          operationId: runOperationId,
          levelId: Number(levelToPlayNumRef.current || levelToPlayNum || 0) || 0,
        });
        runSubmittedRef.current = false;
        if (runId === activeRunIdRef.current) transitionRunLifecycle(RUN_LIFECYCLE.ERROR);
        throw error;
      } finally {
        if (runSubmissionRunIdRef.current === runId) {
          runSubmissionPromiseRef.current = null;
          runSubmissionRunIdRef.current = null;
        }
      }
    })();
    runSubmissionPromiseRef.current = promise;
    return promise;
  }, [fetchToday, operationOwnerId, transitionRunLifecycle, levelToPlayNum]);

  const scheduleIdleHint = useCallback(() => {
    clearIdleHintTimer();
    if (zcView !== 'game' || busy || selected || runOver || levelWin || !board || Number(movesLeft || 0) <= 0) return;

    idleHintTimerRef.current = window.setTimeout(() => {
      idleHintTimerRef.current = null;
      if (busy || selected || runOver || levelWin || zcView !== 'game' || Number(movesLeft || 0) <= 0) return;
      const nextHint = getHint?.(board);
      if (nextHint?.a && nextHint?.b) {
        hintSourceRef.current = 'idle';
        setHintSwap({
          a: { r: nextHint.a.r, c: nextHint.a.c },
          b: { r: nextHint.b.r, c: nextHint.b.c },
        });
      }
    }, ZC_IDLE_HINT_DELAY_MS);
  }, [board, busy, clearIdleHintTimer, levelWin, movesLeft, runOver, selected, zcView]);

  // A board identity change invalidates coordinate-only hints. The new board
  // gets a fresh idle window only after all resolver work has settled.
  useEffect(() => {
    clearIdleHintTimer();
    hintSourceRef.current = null;
    setHintSwap(null);
    if (!busy && !runOver && !levelWin && zcView === 'game') scheduleIdleHint();
    return clearIdleHintTimer;
  }, [board, busy, clearIdleHintTimer, levelWin, runOver, scheduleIdleHint, zcView]);

  useEffect(() => () => clearIdleHintTimer(), [clearIdleHintTimer]);

  const visibleHintSwap = useMemo(() => {
    if (!hintSwap || busy || runOver || levelWin || !board?.grid) return null;
    const a = hintSwap.a;
    const b = hintSwap.b;
    if (!a || !b || !isAdjacent(a, b)) return null;
    if (!board.grid?.[a.r]?.[a.c] || !board.grid?.[b.r]?.[b.c]) return null;
    try {
      return isHintSwapValid(board, a, b) ? hintSwap : null;
    } catch (_) {
      return null;
    }
  }, [board, busy, hintSwap, levelWin, runOver]);

  const [bestScoreLocal, setBestScoreLocal] = useState(0);
  const [nuevoRecordBadge, setNuevoRecordBadge] = useState(false);
  const [multPop, setMultPop] = useState(null); // { mult, key }

  

  // UI candy: pulse missions on completion + XP fly sticker
  const [missionPulse, setMissionPulse] = useState({});
  const [xpFly, setXpFly] = useState(null); // { value, key }

  const closeHelp = useCallback(() => {
    setShowHelp(false);
    try { localStorage.setItem(ZC_HELP_SEEN_KEY, '1'); } catch {}
  }, []);

  const toggleHelp = useCallback(() => {
    setShowHelp((v) => !v);
  }, []);

  useEffect(() => {
    // Show help on first visit (per device)
    try {
      const seen = localStorage.getItem(ZC_HELP_SEEN_KEY) === '1';
      if (!seen) setShowHelp(true);
    } catch {
      // If storage fails, still show once
      setShowHelp(true);
    }
  }, []);
    
    useEffect(() => {
  try {
    const v = Number(localStorage.getItem(ZC_BEST_SCORE_KEY) || 0) || 0;
    setBestScoreLocal(v);
  } catch (e) {
    setBestScoreLocal(0);
  }
}, []);

  useEffect(() => {
    if (!showHelp) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeHelp();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showHelp, closeHelp]);

  

  // ✅ Si el usuario entra sin haber elegido un nivel del mapa todavía,
  // arrancamos en el mejor nivel desbloqueado según el profile (evita caer en lvl 1).
  useEffect(() => {
    if (selectedLevelFromMap != null) return;

    const maxFromProfile =
      Number(profile?.zodiacCrushMapProgress?.maxUnlockedLevel || 0) ||
      Number(profile?.mapProgress?.maxUnlockedLevel || 0) ||
      Number(profile?.profile?.zodiacCrushMapProgress?.maxUnlockedLevel || 0) ||
      Number(profile?.profile?.mapProgress?.maxUnlockedLevel || 0) ||
      Number(profile?.maxUnlockedLevel || 0) ||
      Number(profile?.currentLevel || 0) ||
      0;

    if (maxFromProfile > 1) {
      setSelectedLevelFromMap(maxFromProfile);
    }
  }, [profile, selectedLevelFromMap]);

  useEffect(() => {
    if (allDone) setAllDoneBadge(true);
  }, [allDone]);


useEffect(() => {
  let cancelled = false;
  if (!showLeaderboard) return;

  setLbState((prev) => ({ ...prev, loading: true, error: null }));

  apiGetZodiacCrushLeaderboard({ scope: lbScope, limit: 50 })
    .then((data) => {
      if (cancelled) return;
      setLbState({
        loading: false,
        error: null,
        entries: Array.isArray(data?.entries) ? data.entries : [],
        my: data?.my || null,
      });
    })
    .catch((err) => {
      if (cancelled) return;
      setLbState({
        loading: false,
        error: err?.message || zcText('No se pudo cargar el ranking', 'The leaderboard could not be loaded'),
        entries: [],
        my: null,
      });
    });

  return () => {
    cancelled = true;
  };
}, [showLeaderboard, lbScope]);


      useEffect(() => {
  if (!runOver) return;
  try {
    playLossCue();
    safeVibrate([12, 34, 12]);
  } catch (e) {}

  // Local best score (visual + retention). Does NOT affect backend.
  try {
    const current = Number(scoreRef.current || 0) || 0;
    const prev = Number(localStorage.getItem(ZC_BEST_SCORE_KEY) || 0) || 0;
    if (current > prev) {
      localStorage.setItem(ZC_BEST_SCORE_KEY, String(current));
      setBestScoreLocal(current);
      setNuevoRecordBadge(true);
    } else {
      setBestScoreLocal(prev);
      setNuevoRecordBadge(false);
    }
  } catch (e) {
    setNuevoRecordBadge(false);
  }
      }, [runOver]);
  
    
  const [flash, setFlash] = useState(null); // { color }
  const [scorePop, setScorePop] = useState(null); // { value, key }
  const [swapPresentation, setSwapPresentation] = useState(null);
  const [presentationMotion, setPresentationMotion] = useState(null);
  const [matchPresentation, setMatchPresentation] = useState(3);
  const [specialFx, setSpecialFx] = useState(null);

  const [shake, setShake] = useState({ on: false, axis: 'x' }); // axis: 'x' | 'y'
  const shakeTimerRef = useRef(null);

  const [punchOn, setPunchOn] = useState(false);
  const punchTimerRef = useRef(null);

  const comboStepRef = useRef(0); // increases on consecutive clears/cascades
  const maxCascadeRunRef = useRef(0); // max clear-phases (cascades) reached in a single move during this run

// Keep latest cascades in a ref so callbacks don't depend on state (avoids exhaustive-deps warnings)
const _cascadesRef = useRef(0);

useEffect(() => {
  _cascadesRef.current = Math.max(0, Number(levelProgress?.cascades ?? 0) || 0);
}, [levelProgress?.cascades]);

  const triggerInvalidFeedback = useCallback((axis = 'x') => {
    // directional shake
    setShake({ on: false, axis });
    if (shakeTimerRef.current) window.clearTimeout(shakeTimerRef.current);

    // micro "thunk" punch
    setPunchOn(false);
    if (punchTimerRef.current) window.clearTimeout(punchTimerRef.current);

    window.requestAnimationFrame(() => {
      setShake({ on: true, axis });
        setPunchOn(true);
        try { if (navigator.vibrate) navigator.vibrate(18); } catch (e) {}
      shakeTimerRef.current = window.setTimeout(() => setShake({ on: false, axis }), 280);
      punchTimerRef.current = window.setTimeout(() => setPunchOn(false), 190);
    });
  }, []);

  useEffect(() => {
    if (!particles.length) return;
    const t = window.setTimeout(() => {
      setParticles((p) => p.slice(-96));
    }, 1200);
    return () => window.clearTimeout(t);
  }, [particles]);


  useEffect(() => {
    try {
      if (!Array.isArray(rewardsUnlocked) || rewardsUnlocked.length === 0) return;

      const raw = localStorage.getItem(ZC_SEEN_LEVEL_REWARDS_KEY) || '[]';
      const seenArr = JSON.parse(raw);
      const seen = new Set(Array.isArray(seenArr) ? seenArr : []);

      const newly = [];
     let hintGained = 0; // 💡
let rowBombGained = 0; // 💣

      for (const r of rewardsUnlocked) {
        const key = String(r?.id || r?.title || '');
        if (!key) continue;

        if (!seen.has(key)) {
          newly.push(r);
          seen.add(key);

          if (r.kind === 'booster' && r.id === 'hint') {
  hintGained += 1;
}
if (r.kind === 'booster' && (r.id === 'row_bomb' || r.id === 'rowBomb')) {
  rowBombGained += 1;
}
        }
      }

      if (newly.length) {
        localStorage.setItem(
          ZC_SEEN_LEVEL_REWARDS_KEY,
          JSON.stringify(Array.from(seen).slice(-200))
        );

        for (const r of newly) {
          const label = String(r?.title || r?.id || 'Desbloqueo');
          showToast(`✨ Desbloqueaste: ${label}`);
          try { playCrystalSparkle(8); } catch {}
        }
      }

      if (hintGained > 0 || rowBombGained > 0) {
  setBoosters((prev) => ({
    ...prev,
    hint: (Number(prev?.hint || 0) || 0) + (Number(hintGained || 0) || 0),
    rowBomb: (Number(prev?.rowBomb || 0) || 0) + (Number(rowBombGained || 0) || 0),
  }));
}
    } catch {
      // ignore
    }
  }, [rewardsUnlocked, showToast, setBoosters]);


  // Sync ⭐ booster inventory from backend (only apply increases)
  useEffect(() => {
    if (typeof boostersStar !== 'number') return;
    setBoosters((prev) => {
      const cur = Number(prev?.hint || 0) || 0;
      const next = Number(boostersStar || 0) || 0;
      if (next > cur) return { ...prev, hint: next };
      return prev;
    });
  }, [boostersStar]);

  // Sync 💣 row bomb stock from backend profile (source of truth)
useEffect(() => {
  const nextRowBomb = Number(
    profile?.boostersInventory?.row_bomb ??
    profile?.boostersInventory?.rowBomb ??
    0
  );

  if (!Number.isFinite(nextRowBomb)) return;

  setBoosters((prev) => {
    const curRowBomb = Number(prev?.rowBomb || 0) || 0;

    // Row bombs are consumable, so allow decreases too
    if (nextRowBomb === curRowBomb) return prev;

    return { ...prev, rowBomb: Math.max(0, nextRowBomb) };
  });
}, [
  profile?.boostersInventory?.row_bomb,
  profile?.boostersInventory?.rowBomb,
]);

  const sleep = useCallback((ms) => new Promise((res) => window.setTimeout(res, ms)), []);

  // Defer heavy/non-UI work by at least one frame (mobile smoothness)
  const deferAfterFrame = useCallback((fn) => {
    try {
      window.requestAnimationFrame(() => {
        Promise.resolve()
          .then(fn)
          .catch(() => {});
      });
    } catch (e) {
      // fallback
      Promise.resolve().then(fn).catch(() => {});
    }
  }, []);



  // --- Missions: local projection only during a run ---

const queueMissionSync = useCallback((agg) => {
  try {
    if (!_hasPlayedMoveRef?.current) return;
  } catch {}
    // ✅ No hagas nada si no hay deltas reales
    if (!hasAnyMissionAggDelta(agg)) return;

    // ✅ Also keep per-run LEVEL GOALS progress in sync (local + instant).
    // IMPORTANT: Do NOT update *delta* metrics here (clearedFire/Water/Air/Earth, starsCreated, stripesActivated).
    // Those are accumulated in the move loop (SUM). Updating them here with MAX can overwrite the accumulated value
    // because React batches state updates in the same tick.
    // We only keep absolute/max-like metrics in sync: score (best) and cascades (EXTRA cascades, depth-1).
    try {
      const a = (agg && typeof agg === 'object') ? agg : {};

      // Any real agg implies the player made a meaningful move in this run.
      _hasPlayedMoveRef.current = true;

      const n = (v) => (Number(v || 0) || 0);

      setLevelProgress((prev) => {
        const p = (prev && typeof prev === 'object') ? prev : { ...EMPTY_LEVEL_PROGRESS };
        const next = { ...p };

        // score = best-of
        next.score = Math.max(
          n(next.score),
          n(a.score ?? a.finalScore ?? a.scoreReach ?? a.score_reach)
        );

        // cascades = EXTRA cascades (depth-1)
        const cascSrc = a.cascades;
        const cascFromDepth = n(a.maxCascade ?? a.bestCascade ?? a.cascadeDepth ?? a.cascade ?? a.cascadeCount);
        const cascExtra = (cascSrc != null)
          ? n(cascSrc)
          : Math.max(0, cascFromDepth - 1);

        next.cascades = Math.max(n(next.cascades), cascExtra);

        return next;
      });
    } catch (e) {
      // ignore
    }

  }, []);

// (removed duplicate mergeMissionAgg definition)


  // Wait for a paint before doing CPU-heavy work (keeps mobile input/render smooth)
  const nextFrame = useCallback(() => {
    return new Promise((res) => {
      try {
        window.requestAnimationFrame(() => res());
      } catch {
        window.setTimeout(res, 0);
      }
    });
  }, []);

    const pulseMissionIds = useCallback((ids = []) => {
  const now = Date.now();
  setMissionPulse((prev) => {
    const next = { ...prev };
    ids.forEach((id) => { next[id] = now; });
    return next;
  });

  window.setTimeout(() => {
    setMissionPulse((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { delete next[id]; });
      return next;
    });
  }, 850);
}, []);

const showXpFly = useCallback((xp) => {
  if (!xp) return;
  setXpFly({ value: xp, key: Date.now() });
  window.setTimeout(() => setXpFly(null), 1150);
}, []);

// ✅ Optimistic UI: update daily missions locally as the player plays (no need to wait end-of-run)

// 🔎 Stripe (rayo) activation detector
// Some resolver versions don't include the stripe tile in clearedCells.
// Prefer explicit resolver counters / breakdowns, then fallback to activated/events arrays.
const countStripeActivationsFromState = (st) => {
  try {
    // 1) Direct counters
    const direct =
      Number(
        st?.stripesActivated ??
          st?.stripeActivated ??
          st?.stripeActivations ??
          st?.activatedStripes ??
          st?.stripeCount ??
          st?.raysActivated ??
          st?.rayosActivated ??
          st?.lightningActivated ??
          0
      ) || 0;
    if (direct > 0) return direct;

    // 2) Nested counters (common in resolvers)
    const nestedCandidates = [
      st?.specialsActivated,
      st?.specials,
      st?.counters,
      st?.stats,
      st?.progress,
    ].filter(Boolean);

    for (const obj of nestedCandidates) {
      const v =
        Number(
          obj?.stripesActivated ??
            obj?.stripe ??
            obj?.stripes ??
            obj?.rayo ??
            obj?.rayos ??
            obj?.bolt ??
            obj?.bolts ??
            obj?.lightning ??
            obj?.lightnings ??
            obj?.zap ??
            obj?.zaps ??
            0
        ) || 0;
      if (v > 0) return v;
    }

    // 3) Breakdown objects
    const bd =
      st?.stripesActivationBreakdown ||
      st?.stripeBreakdown ||
      st?.specialBreakdown;
    const bdTotal =
      Number(
        bd?.total ?? bd?.count ?? bd?.stripe ?? bd?.stripes ?? bd?.rayos ?? 0
      ) || 0;
    if (bdTotal > 0) return bdTotal;

    // 4) Arrays of activated specials / events
    const arrays = [
      st?.activated,
      st?.activatedSpecials,
      st?.triggeredSpecials,
      st?.specialsTriggered,
      st?.events,
      st?.eventLog,
      st?.actions,
    ].filter((x) => Array.isArray(x));

    if (arrays.length) {
      let n = 0;
      for (const arr of arrays) {
        for (const a of arr) {
          const token = String(
            a?.type ?? a?.kind ?? a?.special ?? a?.name ?? a?.id ?? a?.key ?? ""
          ).toLowerCase();

          if (
            token.includes("stripe") ||
            token.includes("rayo") ||
            token.includes("ray") ||
            token.includes("bolt") ||
            token.includes("lightning") ||
            token.includes("zap") ||
            token.includes("line")
          ) {
            n += 1;
          }
        }
      }
      if (n > 0) return n;
    }

    return 0;
  } catch {
    return 0;
  }
};

// Resolver activation metadata is the only source for special-activation
// missions. Creation (for example, making a star) is not activation.
const countStarActivationsFromState = (st) => {
  try {
    const direct = Number(
      st?.starsActivated ?? st?.starActivated ?? st?.starActivations ?? 0
    ) || 0;
    if (direct > 0) return direct;

    const arrays = [
      st?.activated,
      st?.activatedSpecials,
      st?.triggeredSpecials,
      st?.specialsTriggered,
      st?.events,
      st?.eventLog,
      st?.actions,
    ].filter((value) => Array.isArray(value));

    let count = 0;
    for (const arr of arrays) {
      for (const activation of arr) {
        const token = String(
          activation?.type ?? activation?.kind ?? activation?.special ??
          activation?.name ?? activation?.id ?? activation?.key ?? ''
        ).toLowerCase();
        if (token.includes('star') || token.includes('color_bomb') || token.includes('colorbomb')) {
          count += 1;
        }
      }
    }
    return count;
  } catch {
    return 0;
  }
};

const applyAggToMissions = useCallback((prevMissions, agg) => {
  return projectMissionProgress(prevMissions, agg);
/*
  const list = Array.isArray(prevMissions) ? prevMissions : [];
  const a = (agg && typeof agg === 'object') ? agg : {};

  const nNum = (v) => (Number(v || 0) || 0);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  // Normalize agg metrics
  const clearedFire = nNum(a.clearedFire ?? a.clear_element_fire ?? a.fire ?? a.cleared_fire);
  const clearedWater = nNum(a.clearedWater ?? a.clear_element_water ?? a.water ?? a.cleared_water);
  const clearedAir = nNum(a.clearedAir ?? a.clear_element_air ?? a.air ?? a.cleared_air);
  const clearedEarth = nNum(a.clearedEarth ?? a.clear_element_earth ?? a.earth ?? a.cleared_earth);

  const starsCreated = nNum(a.starsCreated ?? a.make_star ?? a.stars ?? a.createdStars);
  const stripesActivated = nNum(a.stripesActivated ?? a.activate_stripe ?? a.stripes ?? a.activatedStripes);
  const starsActivated = nNum(a.starsActivated ?? a.activateStar ?? a.activate_star ?? a.activatedStars);
  const specialActivations = nNum(a.specialActivations ?? a.activate_any_special ?? (starsActivated + stripesActivated));
  // ✅ Cascades: treat `cascades` as DEPTH (number of clear phases).
  // Producers may send one of these keys; normalize everything to DEPTH.
  const cascades = Math.max(
    0,
    nNum(
      a.cascades ??
        a.cascadeDepth ??
        a.maxCascade ??
        a.bestCascade ??
        a.best_cascade ??
        a.cascade ??
        a.cascadeCount ??
        0
    )
  );

  // combos: prefer explicit fields; also treat any combo_4/5 as 3+
  const combo3 = nNum(a.combo_3 ?? a.combo3);
  const combo4 = nNum(a.combo_4 ?? a.combo4);
  const combo5 = nNum(a.combo_5 ?? a.combo5);
  const combos3Plus = nNum(a.combos_3plus ?? a.combos3Plus ?? a.combo_3plus ?? a.combo3plus ?? (combo3 + combo4 + combo5));

  // score reach (some missions care about final score; keep best score seen)
  const score = nNum(a.score ?? a.finalScore ?? a.scoreReach ?? a.score_reach);

  const next = list.map((m) => {
    const type = String(m?.type || '').toLowerCase();
    const target = nNum(m?.target);
    const prevProgress = nNum(m?.progress);

    let add = 0;
    let nextProgress = prevProgress;

    // Element clears
    if (type.includes('fire')) add = clearedFire;
    else if (type.includes('water')) add = clearedWater;
    else if (type.includes('air')) add = clearedAir;
    else if (type.includes('earth')) add = clearedEarth;

    // Stars / stripes / cascades
    if (type.includes('star') || type.includes('estrella') || type.includes('make_star')) add = starsCreated;
    if (type.includes('stripe') || type.includes('rayo') || type.includes('activate_stripe')) add = stripesActivated;
    if (type === 'clear_element_any') {
      add = clearedFire + clearedWater + clearedAir + clearedEarth;
    }
    if (type === 'activate_any_special') add = specialActivations;
    if (type.includes('cascade') || type.includes('cascada')) add = cascades;

    // Combos 3+
    if (type.includes('combo')) {
      // If mission is explicitly 4+ or 5+, honor that; otherwise treat as 3+
      if (type.includes('5')) add = combo5 || combos3Plus;
      else if (type.includes('4')) add = combo4 || combos3Plus;
      else add = combos3Plus;
    }

    // Score reach: use max score (not additive)
    if (type.includes('score') || type.includes('reach')) {
      nextProgress = Math.max(prevProgress, score);
    } else {
      nextProgress = prevProgress + add;
    }

    if (target > 0) nextProgress = clamp(nextProgress, 0, target);

    const wasDone = !!m?.done;
    const done = target > 0 ? (wasDone || nextProgress >= target) : wasDone;

    // Return updated mission
    return {
      ...m,
      progress: nextProgress,
      done,
      completed: done,
      current: done ? Math.max(target, nextProgress) : nextProgress,
    };
  });

  return next;
*/
}, []);
  
  // Helper: stable mission ids for pulse without capturing outer `missions`
const getMissionIds = (list) => {
  try {
    return (Array.isArray(list) ? list : [])
      .map((m) => (m?.id || m?.type))
      .filter(Boolean);
  } catch {
    return [];
  }
};

  // ✅ Delta guard for mission-aggregation payloads (moveMissionAgg / boosterAgg)
  // NOTE: `hasAnyMissionDelta` above compares mission LISTS.
  // This one checks if an AGG object contains any meaningful progress.
  
  const hasAnyMissionAggDelta = (agg) => {
  try {
    const a = (agg && typeof agg === 'object') ? agg : {};
    const n = (v) => (Number(v || 0) || 0);

    return (
      n(a.clearedFire ?? a.clear_element_fire ?? a.fire ?? a.cleared_fire) > 0 ||
      n(a.clearedWater ?? a.clear_element_water ?? a.water ?? a.cleared_water) > 0 ||
      n(a.clearedAir ?? a.clear_element_air ?? a.air ?? a.cleared_air) > 0 ||
      n(a.clearedEarth ?? a.clear_element_earth ?? a.earth ?? a.cleared_earth) > 0 ||

      n(a.starsCreated ?? a.make_star ?? a.stars ?? a.createdStars) > 0 ||
      n(a.stripesActivated ?? a.activate_stripe ?? a.stripes ?? a.activatedStripes) > 0 ||
      n(a.starsActivated ?? a.activateStar ?? a.activate_star ?? a.activatedStars) > 0 ||
      n(a.specialActivations ?? a.activate_any_special) > 0 ||

      // ✅ IMPORTANT: include cascades so cascade missions persist (e.g., cascade_2p)
      n(a.cascades ?? a.maxCascade ?? a.bestCascade ?? a.cascadeDepth ?? a.cascade ?? a.cascadeCount) > 0 ||

      n(a.combo_3 ?? a.combo3) > 0 ||
      n(a.combo_4 ?? a.combo4) > 0 ||
      n(a.combo_5 ?? a.combo5) > 0 ||

      // Score can be absolute (reach)
      n(a.score ?? a.finalScore ?? a.scoreReach ?? a.score_reach) > 0
    );
  } catch {
    return false;
  }
};

    
  const movesTotal = useMemo(
  () => (levelToPlayNum ? getMovesForLevel(levelToPlayNum) : 0),
  [levelToPlayNum]
);

const movesTotalRef = useRef(movesTotal);
useEffect(() => {
  movesTotalRef.current = movesTotal;
}, [movesTotal]);
  
  // Keep latest profile without forcing useCallback deps
const profileRef = useRef(profile);
useEffect(() => {
  profileRef.current = profile;
}, [profile]);
  
  
const resetRun = useCallback((opts = {}) => {
  const { toast: shouldToast = true, seedPrefix = 'local' } = (opts && typeof opts === 'object') ? opts : {};

  const seedSuffix = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const newSeed = `${String(seedPrefix || 'local')}_${seedSuffix}`;

  rngRef.current = createRng(newSeed);
  runStartMsRef.current = Date.now();
  const newRunId = beginZodiacCrushRun(operationOwnerId);
  activeRunIdRef.current = newRunId;
  transitionRunLifecycle(RUN_LIFECYCLE.PLAYING);
  confirmedCompletionRef.current = null;

  // ✅ Freeze the level this run started on (authoritative for completion/unlock)
  try {
    _levelStartedRef.current = Math.max(1, Math.floor(Number(levelToPlayNum || 1)));

    // Also reset per-level completion guards when starting a fresh run
    const sign = String(activeSignKey || '').trim().toLowerCase();
    const key = `${_levelStartedRef.current}:${sign}`;
    _levelCompletionFireRef.current = { key, fired: false };
    _levelWinShownRef.current = { key, shown: false };
  } catch (_) {}

  // Generate a fresh board, biased by the current world sign's element when available.
  // (If the engine version doesn't support options, fall back gracefully.)
  let initialRunBoard;
  try {
    // Preferred: engine supports (rng, rows, cols, opts)
    const biasStrength = activeElementKey ? getBiasStrengthForLevel(levelToPlayNum) : undefined;
    initialRunBoard = createInitialBoard(rngRef.current, undefined, undefined, {
      worldKey: activeSignKey,
      level: levelToPlayNum,
      worldIdentityVersion: WORLD_IDENTITY_V2.version,
      biasElement: activeElementKey,
      ...(typeof biasStrength === 'number' ? { biasStrength } : {}),
    });
  } catch (e) {
    if (WORLD_IDENTITY_V2.version === 2) throw e;
    try {
      // Fallback: old engine signature (rng, rows, cols)
      initialRunBoard = createInitialBoard(rngRef.current);
    } catch {
      // Last resort
      initialRunBoard = createInitialBoard(rngRef.current);
    }
  }
  setBoard(initialRunBoard);
  replaySelectionAttemptRef.current = 0;
  try {
    replayRecorderRef.current.startRun({
      seed: newSeed,
      levelConfig: normalizeProtocolV1LevelConfig(levelToPlayNum, {
        worldIdentityVersion: WORLD_IDENTITY_V2.version,
      }),
      initialBoard: protocolV1BoardSnapshot(initialRunBoard),
      initialMeta: {
        hole: initialRunBoard?.meta?.hole || null,
        charge: Number(initialRunBoard?.meta?.charge || 0),
        chargeTarget: Number(initialRunBoard?.meta?.chargeTarget || 3),
      },
    });
  } catch (_) {
    // Replay capture is non-enforcing and must never interrupt production gameplay.
    replayRecorderRef.current.reset();
  }
  setSelected(null);
  setMovesLeft(
    getMovesForLevel(
      (typeof normalizeAbsoluteLevel === 'function')
        ? normalizeAbsoluteLevel(levelToPlayNum, activeSignKey)
        : levelToPlayNum
    )
  );

  scoreRef.current = 0;
  setScore(0);

  // 🎯 Reset progreso del objetivo del nivel (onboarding por nivel)
  setLevelProgress(() => ({ ...EMPTY_LEVEL_PROGRESS }));
  // Bind fresh per-run progress to the current started level (prevents next-level stale UI on first render)
try {
  levelProgressLevelRef.current = Number(_levelStartedRef.current || levelToPlayNum || 0) || 0;
} catch (_) {
  levelProgressLevelRef.current = Number(levelToPlayNum || 0) || 0;
}

  // Clear per-run UI/FX state
  _hasPlayedMoveRef.current = false;
  // Ensure completion can’t run on a stale “started level”
  try { _levelStartedRef.current = Math.max(1, Math.floor(Number(levelToPlayNum || 1))); } catch (_) {}
  setBusy(false);
  setClearingIds(null);
  comboStepRef.current = 0;
  maxCascadeRunRef.current = 0;
  setPendingBooster(null);
  setHintSwap(null);

  setAllDoneBadge(false);
  setRunOver(false);
  runSubmittedRef.current = false;
  runSubmittedRunIdRef.current = null;
  // Do not cancel/replace an older in-flight promise here; its run-id guard
  // prevents stale completion from mutating this new run.
  setLevelWin(null);
  setNuevoRecordBadge(false);

  if (shouldToast) {
    showToast('✨ Nuevo tablero');
  }
}, [
  showToast,
  activeElementKey,
  getBiasStrengthForLevel,
  levelToPlayNum,
  activeSignKey,
  operationOwnerId,
  transitionRunLifecycle,
]);

useEffect(() => {
  // Don’t reset/run until we actually know the level AND we are in the game view.
  // This prevents the “lvl 1 goals/progress flash/cache” when profile/mapProgress is still loading.
  if (zcView !== 'game') return;
  if (!levelToPlayNum) return;

  setLevelWin(null);
  setGoalsNonce(Date.now());

  resetRun({
    toast: false,
    seedPrefix: `${activeSignKey}_w${activeWorldIndex}_lvl${levelToPlayNum}`,
  });
}, [zcView, levelToPlayNum, activeSignKey, activeWorldIndex, playNonce, resetRun]);
  
  // 🔁 Bust goals cache when returning to the game view (Home -> ZodiacCrush sin reload)
useEffect(() => {
  if (zcView !== 'game') return;
  setGoalsNonce(Date.now());
}, [zcView]);
  
  useEffect(() => {
  // Si el navegador restaura desde bfcache (Back/Forward cache),
  // forzamos un reset limpio para que no quede estado viejo.
  const onPageShow = (e) => {
   if (e?.persisted) {
  try {
    // Always bust caches on BFCache restore
    setGoalsNonce(Date.now());
    setPlayNonce(Date.now());

    // If we are currently in-game OR we just intentionally entered the game,
    // DO NOT bounce back to map.
    const viewNow = zcViewRef.current;
    if (enteringGameRef.current || viewNow === 'game') {
      return;
    }

    // Otherwise, restore to a clean map state
    setSelectedLevelFromMap(null);
    setZcView('map');
  } catch {}
}
  };

  window.addEventListener('pageshow', onPageShow);
  return () => window.removeEventListener('pageshow', onPageShow);
}, [setGoalsNonce, setPlayNonce, setSelectedLevelFromMap, setZcView]);

// 🔁 Bust when the tab/app becomes visible again (iOS/Android resume)
useEffect(() => {
  const onVis = () => {
    try {
      if (document.visibilityState === 'visible' && zcView === 'game') {
        setGoalsNonce(Date.now());
      }
    } catch {}
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}, [zcView]);
  
  const triggerClearFX = useCallback((state, cascadeDepth = 1, matchTier = 3) => {
    const clearedLen = Array.isArray(state?.clearedCells) ? state.clearedCells.length : 0;

    // On mobile/low devices, the full-screen flash can cause a tiny frame hitch.
    // Keep score pop, but skip the flash for simple 3-matches.
    const allowFlash = !(lowFxRef.current && clearedLen <= 3);

    if (allowFlash) {
      const cell = state?.clearedCells?.[0];
      const rawEl =
        cell?.tile?.element ??
        (cell?.tile?.key ? (SIGN_ELEMENT?.[cell.tile.key] ?? null) : null);

      const element = normalizeElementKey(rawEl) || 'neutral';
      const color = ELEMENT_FLASH[element] || 'rgba(255,215,0,0.22)';
      setFlash({ color });
      window.setTimeout(() => setFlash(null), 180);
    }

    if (state?.scoreDelta) {
      const delta = Math.max(0, Number(state.scoreDelta || 0) || 0);
      const intensity = Math.min(1.45, 0.92 + Math.min(0.32, delta / 2200) + Math.min(0.22, Math.max(0, cascadeDepth - 1) * 0.07) + (matchTier >= 5 ? 0.08 : matchTier === 4 ? 0.04 : 0));
      const lifetime = Math.min(860, 520 + Math.max(0, cascadeDepth - 1) * 55 + (matchTier >= 5 ? 90 : matchTier === 4 ? 45 : 0));
      setScorePop({ value: state.scoreDelta, key: Date.now(), scale: intensity, tier: matchTier, lifetime });
      window.setTimeout(() => setScorePop(null), lifetime);
    }
  }, []);

  
// --- Boosters API (local helper) ---
const boosterOnServer = useCallback(async (kind) => {
  // kind: "hint" | "rowBomb"
  const type =
    kind === 'rowBomb' ? 'row_bomb' :
    kind === 'hint' ? 'hint' :
    String(kind || '');

  const t = getCentralAuthToken();
  const resp = await authenticatedRequest(zcApiUrl('/api/games/zodiac-crush/boosters/use'), {
    rawResponse: true,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: JSON.stringify({ type }),
    diagSource: 'ZodiacCrushScreen.useBooster',
  });

  const data = await resp.json().catch(() => null);

  if (!resp.ok) {
    const msg = localizedServerContent(data?.message || data?.error, zcText('No se pudo usar el booster', 'The booster could not be used'));
    throw new Error(msg);
  }

  // Normalizar shape de contadores si vienen
  const b = data?.boosters && typeof data.boosters === 'object' ? data.boosters : null;
  const normalizedBoosters = b ? {
    hint: Number(b.hint ?? b.star ?? 0) || 0,
    rowBomb: Number(b.rowBomb ?? b.row_bomb ?? 0) || 0,
  } : null;

  return { ...data, boostersNormalized: normalizedBoosters };
}, [zcApiUrl]);
  

const zcComputeDerivedMaxUnlocked = useCallback((starsByLevel, fallback = 1) => {
  try {
    const s = starsByLevel && typeof starsByLevel === 'object' ? starsByLevel : {};
    let maxCompleted = 0;

    for (const k of Object.keys(s)) {
      const lv = Number(k);
      const st = Number(s[k] || 0) || 0;
      if (Number.isFinite(lv) && lv > 0 && st > 0) {
        if (lv > maxCompleted) maxCompleted = lv;
      }
    }

    return Math.max(Number(fallback || 1) || 1, maxCompleted + 1);
  } catch {
    return Math.max(Number(fallback || 1) || 1, 1);
  }
}, []);

const zcSanitizeProgressLevels = useCallback(({
  serverCurrentLevel,
  serverMaxUnlockedLevel,
  localWantMax,
  starsByLevel,
  prevMaxBefore,
}) => {
  const want = Math.max(Number(localWantMax || 1) || 1, 1);
  const prevMax = Math.max(Number(prevMaxBefore || 1) || 1, 1);

  const derived = Math.max(want, zcComputeDerivedMaxUnlocked(starsByLevel, want));

  const sMax = Number(serverMaxUnlockedLevel || 0) || 0;
  const sCur = Number(serverCurrentLevel || 0) || 0;

  const allowedCeil = Math.max(derived, prevMax) + 1;

  const maxUnlockedLevel =
    (sMax > 0 && sMax <= allowedCeil)
      ? sMax
      : Math.max(derived, prevMax);

  const currentLevel =
    (sCur > 0 && sCur <= maxUnlockedLevel)
      ? sCur
      : maxUnlockedLevel;

  return { currentLevel, maxUnlockedLevel, derived, allowedCeil };
}, [zcComputeDerivedMaxUnlocked]);



const persistLevelCompletion = useCallback(async ({ level, stars }) => {
  if (DEBUG_ZC_LOGS) console.log('[persistLevelCompletion ENTER]', { level, stars, ts: new Date().toISOString() });
const lvRaw = Math.max(1, Math.floor(Number(level || 1)));
let lv = lvRaw; // authoritative level to persist (may be clamped below)


// ⭐ Prefer LOCAL star computation from score + cached level meta.
// This makes completion deterministic even if callers pass undefined/bad `stars`.
const computeStarsLocal = (levelNum, scoreNum) => {
  try {
    const lvl = Math.max(1, Math.floor(Number(levelNum || 1)));
    const score = Math.max(0, Number(scoreNum || 0) || 0);

    // Read cached level meta (populated by map/levels fetch + upsertCachedLevelMeta)
    const raw = localStorage.getItem('zc_map_levels_cache_v1') || '';
    let parsed = null;
    try { parsed = raw ? JSON.parse(raw) : null; } catch { parsed = null; }
    const levels = Array.isArray(parsed?.levels) ? parsed.levels : [];
    const meta = levels.find((x) => Number(x?.level || 0) === lvl) || null;

    const s1 = Number(meta?.star1 ?? meta?.star_1 ?? meta?.stars?.[0] ?? 0) || 0;
    const s2 = Number(meta?.star2 ?? meta?.star_2 ?? meta?.stars?.[1] ?? 0) || 0;
    const s3 = Number(meta?.star3 ?? meta?.star_3 ?? meta?.stars?.[2] ?? 0) || 0;

    // If we don't have thresholds, fall back to caller-provided stars.
    if (!s1 && !s2 && !s3) return 0;

    let stLocal = 0;
    if (s1 > 0 && score >= s1) stLocal = 1;
    if (s2 > 0 && score >= s2) stLocal = 2;
    if (s3 > 0 && score >= s3) stLocal = 3;

    return Math.max(0, Math.min(3, stLocal));
  } catch {
    return 0;
  }
};

const scoreLocal = Math.max(0, Number(scoreRef.current || 0) || 0);
const starsFromCache = computeStarsLocal(lv, scoreLocal);
const starsFromArg = Math.max(1, Math.min(3, Math.floor(Number(stars || 3))));
const st = Math.max(1, Math.min(3, (starsFromCache || 0) > 0 ? starsFromCache : starsFromArg));
  if (DEBUG_ZC_LOGS) console.log('[ZC complete] START', {
    levelArg: level,
    starsArg: stars,
    lvRaw,
  lv,
    st,
    zcView: String(zcViewRef?.current || ''),
    startedRef: Number(_levelStartedRef?.current || 0) || 0,
    levelToPlayRef: Number(levelToPlayNumRef?.current || 0) || 0,
    cacheBefore: (() => {
      try { return JSON.parse(localStorage.getItem(getCurrentZodiacCrushProgressCacheKey()) || '{}'); }
      catch { return null; }
    })(),
  });
  if (DEBUG_ZC_LOGS) console.log('[persistLevelCompletion ENTER]', {
    levelArg: level,
    starsArg: stars,
    lv,
    st,
    zcView: String(zcViewRef?.current || ''),
    started: Number(_levelStartedRef?.current || 0) || 0,
    ref: Number(levelToPlayNumRef?.current || 0) || 0,
    cache: (() => { try { return JSON.parse(localStorage.getItem(getCurrentZodiacCrushProgressCacheKey()) || '{}'); } catch { return null; } })(),
  });

  try {
    // Use the best available "current" level signal.
    // `levelToPlayNumRef` can lag 1 render behind; `_levelStartedRef` is frozen on game-entry and
    // is often the most reliable for completion.
    // Avoid depending on state in this callback; prefer the ref (kept in sync via useEffect)
    const curFromRef = Number(levelToPlayNumRef?.current || 0) || 0;
    const curFromStarted = Number(_levelStartedRef?.current || 0) || 0;

    // Prefer the frozen “started level” as the source of truth to avoid picking a future level
    // when the UI advances early (e.g., optimistic unlock / map selection updates).
    const curLvl = curFromStarted || curFromRef;
    if (DEBUG_ZC_LOGS) console.log('[ZC complete] curLvl computed', { curFromStarted, curFromRef, curLvl, lv, st });
// ✅ Authoritative level selection to avoid persisting a FUTURE level
// If we have a frozen started level, that's the source of truth.
if (curFromStarted > 0) {
  if (lv !== curFromStarted) {
    console.warn('[ZodiacCrush] persistLevelCompletion overriding level with startedRef', {
      lvArg: lv,
      started: curFromStarted,
      curFromRef,
    });
  }
  lv = curFromStarted;
} else if (curLvl > 0 && lv === (curLvl + 1)) {
  // Common desync: UI already advanced; completion belongs to curLvl.
  console.warn('[ZodiacCrush] persistLevelCompletion clamping future level to curLvl', {
    lvArg: lv,
    curLvl,
    curFromRef,
    curFromStarted,
  });
  lv = curLvl;
}
    if (curLvl > 0) {
      // ✅ Allowed (robust against 1-render lag / ref lag):
      // - lv === curLvl       (normal)
      // - lv === curLvl - 1   (UI already advanced to next level)
      // - lv === curLvl + 1   (refs lagged behind; still must persist)
      const diff = lv - curLvl;
      const okLevelWindow = (diff === 0 || diff === -1 || diff === 1);

      if (!okLevelWindow) {
        const reason = lv > curLvl ? 'level_mismatch_future' : 'level_mismatch_stale';
        console.warn('[ZodiacCrush] persistLevelCompletion skipped (level mismatch)', {
          lv,
          curLvl,
          diff,
          reason,
          curFromStarted,
          curFromRef,
        });
        if (DEBUG_ZC_LOGS) console.log('[persistLevelCompletion MISMATCH]', {
          reason,
          lv,
          curLvl,
          diff,
          curFromStarted,
          curFromRef,
        });
        if (DEBUG_ZC_LOGS) console.log('[ZC complete] BLOCKED mismatch', { lv, curLvl, diff, reason, curFromStarted, curFromRef });
        return { ok: false, error: reason, level: lv, currentLevel: curLvl, unlockedNext: false };
      }

      // If refs lagged behind the finished level, advance the frozen ref so next completions don't get blocked.
      try {
        const prevStarted = Number(_levelStartedRef?.current || 0) || 0;
        if (lv > prevStarted) _levelStartedRef.current = lv;
      } catch (_) {}
    }
  } catch (_) {}

const CACHE_KEY = getCurrentZodiacCrushProgressCacheKey();

  const readCache = () => {
    if (!CACHE_KEY) {
      return { cache: {}, starsByLevel: {}, maxUnlockedLevel: 1, currentLevel: 1 };
    }
    try {
      const raw = localStorage.getItem(CACHE_KEY) || '{}';
      const parsed = JSON.parse(raw);
      const cache = (parsed && typeof parsed === 'object') ? parsed : {};

      const starsByLevel =
        (cache.starsByLevel && typeof cache.starsByLevel === 'object')
          ? cache.starsByLevel
          : {};

      const maxUnlockedLevel = Math.max(1, Number(cache?.maxUnlockedLevel ?? 1) || 1);
      // `currentLevel` is treated as the NEXT playable level
      const currentLevel = Math.max(1, Number(cache?.currentLevel ?? maxUnlockedLevel ?? 1) || 1);

      return { cache, starsByLevel, maxUnlockedLevel, currentLevel };
    } catch {
      return { cache: {}, starsByLevel: {}, maxUnlockedLevel: 1, currentLevel: 1 };
    }
  };

  const writeCache = ({ base, currentLevel, starsByLevel, maxUnlockedLevel } = {}) => {
    if (!CACHE_KEY) return;
    try {
      const b = (base && typeof base === 'object') ? base : {};

      // Preserve existing starsByLevel unless explicitly provided.
      const prevStars = (b.starsByLevel && typeof b.starsByLevel === 'object') ? b.starsByLevel : {};
      const sb = (starsByLevel && typeof starsByLevel === 'object') ? starsByLevel : prevStars;

      const curLv = Math.max(1, Number(currentLevel ?? b.currentLevel ?? 1) || 1);
      const mxRaw = Math.max(1, Number(maxUnlockedLevel ?? b.maxUnlockedLevel ?? curLv ?? 1) || 1);

      // Never allow maxUnlockedLevel to be lower than currentLevel
      const mx = Math.max(mxRaw, curLv);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          ...b,
          ts: Date.now(),
          currentLevel: curLv,
          maxUnlockedLevel: mx,
          starsByLevel: sb,
        })
      );
    } catch {
      // ignore
    }
  };

const { starsByLevel: curStarsByLevel, maxUnlockedLevel: curMax } = readCache();
// Hoist so backend sanitize can read the same starsByLevel we just wrote optimistically
let nextStarsByLevel = null;
  const prevMaxBefore = Number(curMax || 1) || 1;

  // Compute the expected server-side progression, but do not write it locally
  // until the authoritative map mutation succeeds.
  const prevStars = Number(curStarsByLevel[String(lv)] || 0) || 0;

  // IMPORTANT:
  // - currentLevel = NEXT playable level pointer (what you should play next)
  // - maxUnlockedLevel = furthest unlocked level
  // If curMax is already 240, we must NOT set currentLevel to 240 when completing a low level.
  nextStarsByLevel = { ...(curStarsByLevel || {}) };
  if (st > prevStars) nextStarsByLevel[String(lv)] = st;

  // If we already applied the same outcome locally, DO NOT skip backend.
  // Reason: local cache can be ahead of the server (e.g., optimistic writes / older bugs / manual cache changes).
  // If we skip the backend, the server may stay behind and the next level will remain locked on refresh.
  // Authoritative backend sync.
  try {
  const completionLogicalKey = `run:${getZodiacCrushRunOperationId(operationOwnerId)}:level:${lv}`;
  const completionOperationId = getZodiacCrushCompletionOperationId(operationOwnerId, completionLogicalKey);
  const t = getCentralAuthToken();

  // ✅ log separado (sin await)
  if (DEBUG_ZC_LOGS) console.log('[ZC complete-level] sending', {
    level: lv,
    stars: st,
    prevMaxBefore,
    cacheBefore: (() => {
      try { return JSON.parse(localStorage.getItem(getCurrentZodiacCrushProgressCacheKey()) || '{}'); }
      catch { return null; }
    })(),
  });

  logCompletionTrace('mapComplete called', {
    operationId: completionOperationId,
    levelId: lv,
    runOperationId: getZodiacCrushRunOperationId(operationOwnerId),
  });
  // ✅ fetch real
 const resp = await authenticatedRequest(zcApiUrl('/api/games/zodiac-crush/map/complete-level'), {
  rawResponse: true,
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  },
    body: JSON.stringify({
      level: lv,
      runOperationId: getZodiacCrushRunOperationId(operationOwnerId),
    // ✅ CLAVE: sin score, en prod no te actualiza estrella/bestScore correctamente
    score: Math.max(0, Number(scoreRef.current || 0) || 0),
    stars: (Number(st || 0) > 0 ? Math.max(0, Math.min(3, Number(st) || 0)) : undefined),
    stats: {
      maxCascade: Number(maxCascadeRunRef.current || 0) || 0,
    },
    ...(completionOperationId ? { completionOperationId } : {}),
  }),
  diagSource: 'ZodiacCrushScreen.completeLevel',
});

    const data = await resp.json().catch(() => null);
    logCompletionTrace('mapComplete resolved', {
      ok: resp.ok,
      status: resp.status,
      operationId: completionOperationId,
      levelId: lv,
    });
    
    if (DEBUG_ZC_LOGS) console.log('[persistLevelCompletion BACKEND_RESP]', {
  status: resp.status,
  ok: resp.ok,
  dataKeys: Object.keys(data || {}),
  maxUnlockedLevel: data?.maxUnlockedLevel,
  progressMax: data?.progress?.maxUnlockedLevel,
});

    // Robust extraction of server current/max levels (for sanitize/merge)
    const serverCurrent = Number(
      data?.currentLevel ??
      data?.progress?.currentLevel ??
      data?.mapProgress?.currentLevel ??
      data?.profile?.zodiacCrushMapProgress?.currentLevel ??
      data?.profile?.mapProgress?.currentLevel ??
      0
    ) || 0;
    const serverMax = Number(
      data?.maxUnlockedLevel ??
      data?.progress?.maxUnlockedLevel ??
      data?.mapProgress?.maxUnlockedLevel ??
      data?.profile?.zodiacCrushMapProgress?.maxUnlockedLevel ??
      data?.profile?.mapProgress?.maxUnlockedLevel ??
      0
    ) || 0;

    const sanitized = zcSanitizeProgressLevels({
      serverCurrentLevel: serverCurrent,
      serverMaxUnlockedLevel: serverMax,
      localWantMax: lv + 1,
      starsByLevel: nextStarsByLevel || curStarsByLevel,
      prevMaxBefore,
    });

    if (DEBUG_ZC_LOGS) console.log('[ZC sanitize]', sanitized);

    // If server says locked, clamp cache back to authoritative maxUnlockedLevel.
    // Be tolerant: backend may vary message/error shape.
    const lockedMsg = String(data?.error || data?.message || '').toLowerCase();
    const serverMaxFromAny = Number(
      data?.maxUnlockedLevel ??
      data?.progress?.maxUnlockedLevel ??
      data?.mapProgress?.maxUnlockedLevel ??
      data?.profile?.zodiacCrushMapProgress?.maxUnlockedLevel ??
      data?.profile?.mapProgress?.maxUnlockedLevel ??
      0
    ) || 0;

    if (!resp.ok && resp.status === 403 && (lockedMsg === 'level is locked' || lockedMsg.includes('locked'))) {
      try {
        const { cache: c3, starsByLevel: s3 } = readCache();
        const serverMax = Math.max(1, serverMaxFromAny || 1);

        // ServerMax is the highest unlocked / next playable pointer on the server.
        // Clamp local cache back to it so UI never shows unreachable worlds.
        writeCache({
          base: c3,
          currentLevel: serverMax,
          maxUnlockedLevel: serverMax,
          starsByLevel: s3,
        });

        try {
          window.dispatchEvent(
            new CustomEvent('zc_map_progress_updated', {
              detail: {
                currentLevel: serverMax,
                maxUnlockedLevel: serverMax,
                starsByLevel: s3,
                authoritative: true,
              },
            })
          );
          // localStorage.removeItem('zc_map_levels_cache_v1'); // disabled: breaks objectives cache
        } catch (_) {}
      } catch {}

      // eslint-disable-next-line no-console
      console.warn('[ZodiacCrush] map/complete blocked by server (locked)', resp.status, data);
      // Treat as "ok-ish" for UX (show win UI), but do NOT claim unlock if the server rejected it.
      // We keep `cached: true` so the caller can still show the victory UI.
      return {
        ok: false,
        error: 'Level is locked',
        level: lv,
        stars: st,
        cached: true,
        unlockedNext: false,
        maxUnlockedLevel: serverMaxFromAny || data?.maxUnlockedLevel,
      };
    }

    // Merge backend snapshot (if any) into local cache (source of truth).
    const snap =
      data?.progress ||
      data?.mapProgress ||
      data?.profile?.zodiacCrushMapProgress ||
      data?.profile?.mapProgress ||
      data;

    if (snap && typeof snap === 'object') {
      const nextStarsByLevel =
        (snap.starsByLevel && typeof snap.starsByLevel === 'object')
          ? snap.starsByLevel
          : null;
      const nextMax = Number(snap.maxUnlockedLevel || 0) || 0;

      if (nextStarsByLevel || nextMax) {
        const { cache: c2, starsByLevel: s2, maxUnlockedLevel: m2, currentLevel: c2Cur } = readCache();
        const mergedStars = { ...(s2 || {}), ...(nextStarsByLevel || {}) };
        const mergedMax = Math.max(Number(m2 || 1) || 1, nextMax || 1);
        const snapCur = Number(snap.currentLevel || 0) || 0;
        const mergedCur = Math.max(Number(c2Cur || 1) || 1, snapCur || 1, mergedMax || 1);

        writeCache({
  base: c2,
  currentLevel: sanitized.currentLevel,
  maxUnlockedLevel: sanitized.maxUnlockedLevel,
  starsByLevel: mergedStars,
});

        // ✅ Keep this screen in sync (avoid needing a profile refetch to unlock next level)
        try {
          setSelectedLevelFromMap((prev) => {
            const curSel = Number(prev || 0) || 0;
            return sanitized.maxUnlockedLevel > curSel ? sanitized.maxUnlockedLevel : prev;
          });
        } catch (_) {}

        // 🔔 Notify map hook/UI immediately
        try {
          window.dispatchEvent(
            new CustomEvent('zc_map_progress_updated', {
              detail: {
                currentLevel: mergedCur,
                maxUnlockedLevel: mergedMax,
                starsByLevel: mergedStars,
                authoritative: true,
              },
            })
          );
          // localStorage.removeItem('zc_map_levels_cache_v1'); // disabled: breaks objectives cache
        } catch (_) {}
      }
    }

    // Unlock state comes only from the successful server response. Never infer
    // it from a possibly stale local cache.
    const authoritativeMaxUnlocked = Number(
      data?.maxUnlockedLevel ??
      data?.progress?.maxUnlockedLevel ??
      data?.mapProgress?.maxUnlockedLevel ??
      data?.profile?.zodiacCrushMapProgress?.maxUnlockedLevel ??
      data?.profile?.mapProgress?.maxUnlockedLevel ??
      0
    ) || 0;
    const unlockedNext = authoritativeMaxUnlocked >= (lv + 1);

    if (!resp.ok) {
      // eslint-disable-next-line no-console
      console.warn('[ZodiacCrush] map/complete failed', resp.status, data);
      return { ok: false, status: resp.status, data, unlockedNext: false, cached: false, stars: st, level: lv };
    }
    completeZodiacCrushCompletionOperation(operationOwnerId, completionLogicalKey);

    if (DEBUG_ZC_LOGS) console.log('[persistLevelCompletion RESULT]', {
  lv,
  st,
  unlockedNext,
  cacheFinal: (() => { try { return JSON.parse(localStorage.getItem(getCurrentZodiacCrushProgressCacheKey()) || '{}'); } catch { return null; } })(),
});
    return { ok: true, data, unlockedNext, cached: true, stars: st, level: lv };
  } catch (e) {
    return { ok: false, error: 'network', unlockedNext: false, cached: false, stars: st, level: lv };
  }
}, [operationOwnerId, zcApiUrl, zcSanitizeProgressLevels]);
  
  
  // Fire level completion exactly once per (level, sign) while in game view.
  // Prevents double/unordered unlock calls which can lead to server "locked" and no map advance.
  // Fire level completion exactly once per (level, sign) while in game view.
// Prevents double/unordered unlock calls which can lead to server "locked" and no map advance.
const _levelCompletionFireRef = useRef({ key: '', fired: false });
  const _levelWinShownRef = useRef({ key: '', shown: false });
  
  // ✅ Freeze the level this run started on (prevents desync if UI advances early)
  const _levelStartedRef = useRef(0);
  
const _fireLevelCompletionOnce = useCallback(() => {
  try {
    const done = Number(canonicalGoalState?.done || 0) || 0;
    const total = Number(canonicalGoalState?.total || 0) || 0;
    logCompletionTrace('completion effect entered', {
      score: Number(scoreRef.current || score || 0) || 0,
      canonicalGoalState,
      done,
      total,
      busy,
      runLifecycle: runLifecycleRef.current,
      firedRef: _levelCompletionFireRef.current,
      operationId: getZodiacCrushRunOperationId(operationOwnerId),
      levelId: Number(_levelStartedRef.current || levelToPlayNumRef.current || levelToPlayNum || 0) || 0,
    });

    // ✅ Completion must be driven ONLY by level goals.
    // If goals are not loaded/configured, do NOT auto-complete.
    if (total <= 0) {
      logCompletionAbort('goal_not_complete', { done, total });
      if (DEBUG_ZC_LOGS) console.log('[ZC completion check SKIP:no-goals]', {
        zcView,
        done,
        total,
        hasPlayedMove: !!_hasPlayedMoveRef?.current,
        reason: 'no-goals',
      });
      return;
    }

    const completed = done >= total;
    if (!completed) {
      logCompletionAbort('goal_not_complete', { done, total });
      if (DEBUG_ZC_LOGS) console.log('[ZC completion check SKIP:not-completed]', {
        zcView,
        done,
        total,
        hasPlayedMove: !!_hasPlayedMoveRef?.current,
      });
      return;
    }

    if (DEBUG_ZC_LOGS) console.log('[ZC completion check ENTER]', {
      zcView,
      done,
      total,
      hasPlayedMove: _hasPlayedMoveRef?.current,
    });

    const view = String(zcView || '').toLowerCase();
    if (runOver) {
      logCompletionAbort('unknown', { reason: 'run_over' });
      return;
    }

    const isCompletionView =
      view === 'game' ||
      view === 'completion' ||
      view === 'complete' ||
      view === 'completed' ||
      view === 'end' ||
      view === 'win' ||
      view === 'level_end' ||
      view === 'levelend' ||
      view === 'level-end';

    if (!isCompletionView) {
      logCompletionAbort('unknown', { reason: 'not_completion_view', zcView: view });
      if (DEBUG_ZC_LOGS) console.log('[ZC completion check SKIP:not-completion-view]', { zcView: view });
      return;
    }

    const lvl = Math.max(
      1,
      Math.floor(Number(_levelStartedRef.current || levelToPlayNumRef.current || levelToPlayNum || 1))
    );

    if (!_hasPlayedMoveRef?.current) {
      logCompletionAbort('unknown', { reason: 'no_move' });
      if (DEBUG_ZC_LOGS) console.log('[ZC completion check SKIP:no-move]', {
        lvl,
        done,
        total,
        hasPlayedMove: _hasPlayedMoveRef?.current,
      });
      return;
    }

    const sign = String(activeSignKey || '').trim().toLowerCase() || 'unknown';
    if (!lvl) return;

    const key = `${lvl}:${sign}`;

    if (_levelCompletionFireRef.current.key !== key) {
      _levelCompletionFireRef.current = { key, fired: false };
      _levelWinShownRef.current = { key, shown: false };
    }

    if (DEBUG_ZC_LOGS) console.log('[ZC completion check]', {
      zcView,
      done,
      total,
      hasPlayedMove: !!_hasPlayedMoveRef.current,
      lvlStarted: _levelStartedRef.current,
      lvlRef: levelToPlayNumRef.current,
      lvlComputed: lvl,
      sign,
      key,
      fired: _levelCompletionFireRef.current,
      shown: _levelWinShownRef.current,
    });

    if (_levelCompletionFireRef.current.fired) {
      logCompletionAbort('already_fired', { key });
      if (DEBUG_ZC_LOGS) console.log('[ZC completion check SKIP:already-fired]', { key });
      return;
    }
    if (_levelWinShownRef.current.shown) {
      logCompletionAbort('already_fired', { key, reason: 'win_already_shown' });
      if (DEBUG_ZC_LOGS) console.log('[ZC completion check SKIP:already-shown]', { key });
      return;
    }

    if (runLifecycleRef.current !== RUN_LIFECYCLE.READY) {
      logCompletionAbort('lifecycle_not_ready', { lifecycle: runLifecycleRef.current });
      return;
    }
    const completionRunId = activeRunIdRef.current;

    // in-flight (evita doble disparo), pero NO marques shown todavía
    _levelCompletionFireRef.current.fired = true;

    // ⭐ Compute stars locally from score + cached level meta (best-effort)
    const computeStarsLocal = (levelNum, scoreNum) => {
      try {
        const lvlN = Math.max(1, Math.floor(Number(levelNum || 1)));
        const scoreN = Math.max(0, Number(scoreNum || 0) || 0);

        const raw = localStorage.getItem('zc_map_levels_cache_v1') || '';
        let parsed = null;
        try { parsed = raw ? JSON.parse(raw) : null; } catch { parsed = null; }
        const levels = Array.isArray(parsed?.levels) ? parsed.levels : [];
        const meta = levels.find((x) => Number(x?.level || 0) === lvlN) || null;

        const s1 = Number(meta?.star1 ?? meta?.star_1 ?? meta?.stars?.[0] ?? 0) || 0;
        const s2 = Number(meta?.star2 ?? meta?.star_2 ?? meta?.stars?.[1] ?? 0) || 0;
        const s3 = Number(meta?.star3 ?? meta?.star_3 ?? meta?.stars?.[2] ?? 0) || 0;

        if (!s1 && !s2 && !s3) return 0;

        let stLocal = 0;
        if (s1 > 0 && scoreN >= s1) stLocal = 1;
        if (s2 > 0 && scoreN >= s2) stLocal = 2;
        if (s3 > 0 && scoreN >= s3) stLocal = 3;

        return Math.max(0, Math.min(3, stLocal));
      } catch {
        return 0;
      }
    };

    const starsLocal = Math.max(
      1,
      Math.min(3, computeStarsLocal(lvl, Number(scoreRef?.current || 0) || 0) || 3)
    );

    if (DEBUG_ZC_LOGS) console.log('[ZodiacCrush] persistLevelCompletion firing', {
      level: lvl,
      stars: starsLocal,
      done,
      total,
      sign,
      key,
    });

    Promise.resolve()
      .then(() => submitRunIfNeeded('win'))
      .then((runResult) => {
        if (!runResult?.ok || runResult?.stale || completionRunId !== activeRunIdRef.current) {
          logCompletionAbort(runResult?.stale || completionRunId !== activeRunIdRef.current ? 'stale_run' : 'submit_failed', {
            runResult,
            completionRunId,
            activeRunId: activeRunIdRef.current,
          });
          throw new Error('stale_or_failed_run');
        }
        transitionRunLifecycle(RUN_LIFECYCLE.COMPLETING_LEVEL);
        return persistLevelCompletion({ level: lvl, stars: starsLocal });
      })
      .then((r) => {
        if (DEBUG_ZC_LOGS) console.log('[ZC complete] persistLevelCompletion resolved', { r, lvl, ts: new Date().toISOString() });

        if (
          r &&
          r.ok === false &&
          (r.error === 'Level is locked' || String(r.error || '').startsWith('level_mismatch'))
        ) {
          console.warn('[ZodiacCrush] level completion skipped', r);
          try { _levelCompletionFireRef.current.fired = false; } catch (_) {}
          try { _levelWinShownRef.current.shown = false; } catch (_) {}

          try {
            if (String(r.error || '').startsWith('level_mismatch')) {
              showToast(zcText('⚠️ No se pudo guardar el nivel (desync). Volvé al mapa y reintentá.', '⚠️ The level could not be saved. Return to the map and retry.'));
            }
          } catch (_) {}
          return;
        }

        // A cached optimistic response is not authoritative completion. Only
        // the successful map mutation may unlock the next level or show win UI.
        const okish = !!(r && r.ok === true);
        if (!okish) {
          logCompletionAbort('map_failed', { result: r });
          try { _levelCompletionFireRef.current.fired = false; } catch (_) {}
          try { _levelWinShownRef.current.shown = false; } catch (_) {}
          return;
        }

        if (completionRunId !== activeRunIdRef.current) {
          logCompletionAbort('stale_run', { completionRunId, activeRunId: activeRunIdRef.current });
          return;
        }
        confirmedCompletionRef.current = {
          runId: completionRunId,
          level: lvl,
          unlockedNext: !!r.unlockedNext,
          nextLevel: Number(r?.data?.currentLevel ?? r?.data?.progress?.currentLevel ?? r?.data?.mapProgress?.currentLevel ?? 0) || 0,
        };
        transitionRunLifecycle(RUN_LIFECYCLE.WON);

        try {
          if (typeof showLevelWinUI === 'function') {
            showLevelWinUI({
              level: lvl,
              stars: Number(r?.stars || starsLocal || 3) || 3,
              unlockedNext: !!r?.unlockedNext,
            });
          }
        } catch (_) {}

        try { _levelWinShownRef.current.shown = true; } catch (_) {}

        const unlockedNext = !!(r && r.unlockedNext);

        try {
          fireLevelWinFx(lvl, { advanced: unlockedNext });
        } catch (_) {}

        if (DEBUG_ZC_LOGS) console.log('[ZodiacCrush] completion result', {
          okish,
          unlockedNext,
          shouldShowNextLevelUnlockedBanner: okish && unlockedNext,
          lvl,
          sign,
        });
      })
      .catch((err) => {
        logCompletionAbort('unknown', { reason: err?.message || 'completion_pipeline_error' });
        console.error('[ZC complete] persistLevelCompletion rejected', { err, lvl, ts: new Date().toISOString() });
        // allow retry on unexpected errors
        try { _levelCompletionFireRef.current.fired = false; } catch (_) {}
        try { _levelWinShownRef.current.shown = false; } catch (_) {}
        if (completionRunId === activeRunIdRef.current) transitionRunLifecycle(RUN_LIFECYCLE.ERROR);
      });
  } catch (_) {}
}, [
   zcView,
   runOver,
   canonicalGoalState,
   activeSignKey,
   levelToPlayNum,
   persistLevelCompletion,
   submitRunIfNeeded,
   busy,
   score,
   operationOwnerId,
   fireLevelWinFx,
   showToast,
   showLevelWinUI,
   transitionRunLifecycle,
]);
  
useEffect(() => {
  try {
    logCompletionTrace('completion effect execution', {
      score: Number(score || 0) || 0,
      canonicalGoalState,
      done: Number(canonicalGoalState?.done || 0) || 0,
      total: Number(canonicalGoalState?.total || 0) || 0,
      busy,
      runLifecycle,
      firedRef: _levelCompletionFireRef.current,
      operationId: getZodiacCrushRunOperationId(operationOwnerId),
      levelId: Number(_levelStartedRef.current || levelToPlayNum || 0) || 0,
    });
    // ✅ El nivel se completa SOLO por objetivos del NIVEL (stars), nunca por misiones del día.
    if (String(zcView || '') !== 'game') {
      logCompletionAbort('unknown', { reason: 'not_game_view' });
      return;
    }
    if (runOver) {
      logCompletionAbort('unknown', { reason: 'run_over' });
      return;
    }

    if (runLifecycle === RUN_LIFECYCLE.ERROR) {
      logCompletionAbort('submit_failed', { reason: 'run_lifecycle_error' });
      return;
    }

    const done = Number(canonicalGoalState?.done || 0) || 0;
    const total = Number(canonicalGoalState?.total || 0) || 0;

    // Score/objective progress can cross its target during a cascade. Defer
    // the authoritative pipeline until the resolver has released the busy
    // state, then re-run this effect because `busy` is an explicit dependency.
    if (busy) {
      logCompletionAbort('busy');
      return;
    }

    // Si no hay objetivos configurados, NO auto-completar.
    if (total <= 0) {
      logCompletionAbort('goal_not_complete', { done, total });
      return;
    }

    if (done >= total) {
      _fireLevelCompletionOnce();
    }
  } catch {
    logCompletionAbort('unknown');
    // ignore
  }
}, [
  canonicalGoalState,
  canonicalGoalState?.done,
  canonicalGoalState?.total,
  busy,
  runLifecycle,
  zcView,
  runOver,
  score,
  operationOwnerId,
  levelToPlayNum,
  allDone,
  _fireLevelCompletionOnce,
]);

useEffect(() => {
  try {
    // ✅ Freeze the level ONLY when entering the game view.
    // Do NOT keep updating it on levelToPlayNum changes while already in-game,
    // because the UI may advance early and would overwrite the true “started level”.
    const wasGame = zcViewRef?.current === 'game';

    if (zcView === 'game' && !wasGame) {
      const v = Number(levelToPlayNumRef.current || levelToPlayNum || 1) || 1;
      _levelStartedRef.current = Math.max(1, Math.floor(v));
    }

    // ❌ NO lo resetees a 0 al salir, porque el completion puede dispararse 1 tick después.
  } catch (_) {}
}, [zcView, levelToPlayNum]);

useEffect(() => {
  try {
    if (zcViewRef) zcViewRef.current = zcView;
  } catch (_) {}
}, [zcView]);



  
  
  const spawnClearParticles = useCallback((state) => {
    const cleared = state?.clearedCells || [];
    if (!cleared.length) return;

    // In low-FX mode on mobile, particles on simple 3-matches can cause micro-lag.
    if (lowFxRef.current && cleared.length <= 3) return;

    const rows = board?.rows || 0;
    const cols = board?.cols || 0;

    // derive element: use the first cleared tile's element (coherent burst)
    const rawEl =
      cleared?.[0]?.tile?.element ??
      (cleared?.[0]?.tile?.key ? (SIGN_ELEMENT?.[cleared[0].tile.key] ?? null) : null);

    const el = normalizeElementKey(rawEl) || 'neutral';
    const palette = ELEMENT_PARTICLE[el] || ELEMENT_PARTICLE.neutral;

    const now = Date.now();
    const burstCountBase = lowFxRef.current
      ? Math.min(6, Math.max(3, Math.round(cleared.length * 0.65)))
      : Math.min(12, Math.max(6, Math.round(cleared.length * 0.9)));

    // Convert cell coords -> percentage within board, so it works responsively.
    const seeds = cleared.slice(0, lowFxRef.current ? 3 : 6);
    const pts = seeds.map((c) => {
      const x = cols ? (c.c + 0.5) / cols : 0.5;
      const y = rows ? (c.r + 0.5) / rows : 0.5;
      return { x, y };
    });

    const makeOne = (i) => {
      const base = pts[i % pts.length] || { x: 0.5, y: 0.5 };
      const jitterX = (Math.random() - 0.5) * 0.06;
      const jitterY = (Math.random() - 0.5) * 0.06;

      // Motion tuned by element
      const mag = el === 'air' ? 140 : el === 'water' ? 110 : el === 'earth' ? 85 : 125;
      const ang = Math.random() * Math.PI * 2;
      const dx = Math.cos(ang) * mag;
      const dy = (Math.sin(ang) * mag) - (el === 'earth' ? 10 : 35);

      const kind = worldVisualProfile.particle === 'embers'
        ? (Math.random() < 0.4 ? 'ember' : 'spark')
        : worldVisualProfile.particle === 'bubbles'
          ? (Math.random() < 0.45 ? 'drop' : 'mist')
          : worldVisualProfile.particle === 'wisps'
            ? (Math.random() < 0.6 ? 'streak' : 'glint')
            : worldVisualProfile.particle === 'dust'
              ? (Math.random() < 0.65 ? 'dust' : 'chip')
              : el === 'fire'
        ? (Math.random() < 0.25 ? 'ember' : 'spark')
        : el === 'water'
          ? (Math.random() < 0.35 ? 'drop' : 'mist')
          : el === 'air'
            ? (Math.random() < 0.55 ? 'streak' : 'glint')
            : el === 'earth'
              ? (Math.random() < 0.6 ? 'dust' : 'chip')
              : (Math.random() < 0.5 ? 'glint' : 'spark');

      const scale = el === 'air'
        ? 0.7 + Math.random() * 0.8
        : 0.75 + Math.random() * 1.0;

      return {
        id: `${now}_${i}_${Math.random().toString(16).slice(2)}`,
        x: Math.min(0.98, Math.max(0.02, base.x + jitterX)),
        y: Math.min(0.98, Math.max(0.02, base.y + jitterY)),
        dx,
        dy,
        rot: (Math.random() - 0.5) * 120,
        scale,
        kind,
        element: el,
        colorA: palette.a,
        colorB: palette.b,
      };
    };

    const newParts = Array.from({ length: burstCountBase }, (_, i) => makeOne(i));
    setParticles((prev) => prev.concat(newParts).slice(-96));

    // remove these particles after animation
    window.setTimeout(() => {
      setParticles((prev) => prev.filter((p) => !p.id.startsWith(String(now))));
    }, 920);
  }, [board?.rows, board?.cols, worldVisualProfile.particle]);


  const onCellClick = useCallback(async (cell) => {
    clearHintForActivity();
    scheduleIdleHint();
    primeAudio();
    if (busy) return;

    if (movesLeft <= 0) {
      showToast(zcText('Sin movimientos', 'Out of moves'));
      return;
    }
   // 💡 Pista: NO la limpies al primer toque.
// Mantenerla visible mientras el usuario elige, y limpiarla sólo cuando intenta un swap (2do toque adyacente).
// ...

if (hintSwap && selected && isAdjacent(selected, cell)) {
  setHintSwap(null);
}

    // 💣 Pending booster: Row bomb (clear an entire row) — NO consume move
    if (pendingBooster?.type === 'rowBomb') {
      const localStock = Number(boosters?.rowBomb || 0) || 0;
      if (localStock <= 0) {
        setPendingBooster(null);
        showToast(zcText('No tenés bombas disponibles', 'No bombs available'));
        return;
      }

      setBusy(true);
      setSelected(null);

      // Consume on backend first (source of truth)
      try {
        // boosterOnServer normaliza `type` internamente. Para row bomb pasamos el alias del UI.
        const resp = await boosterOnServer('rowBomb');
        if (!resp?.ok || resp?.used !== true) {
          setPendingBooster(null);
          if (resp?.reason === 'no_stock') showToast(zcText('Sin bombas 💣', 'No bombs available 💣'));
          else showToast(zcText('No se pudo usar la bomba', 'The bomb could not be used'));
          setBusy(false);
          return;
        }
        replayRecorderRef.current.markIneligible('row_bomb_booster');

        // Sync local UI counts from backend response when available
        // Prefer the normalized shape returned by boosterOnServer.
        const bn = resp?.boostersNormalized;
        if (bn && typeof bn === 'object') {
          setBoosters((prev) => ({
            ...prev,
            hint: Number.isFinite(Number(bn.hint)) ? Number(bn.hint) : prev.hint,
            rowBomb: Number.isFinite(Number(bn.rowBomb)) ? Number(bn.rowBomb) : prev.rowBomb,
          }));
        } else if (resp?.boosters && typeof resp.boosters === 'object') {
          // Back-compat with older backend shapes
          setBoosters((prev) => ({
            ...prev,
            hint:
              typeof resp.boosters.star === 'number'
                ? Number(resp.boosters.star)
                : (typeof resp.boosters.hint === 'number' ? Number(resp.boosters.hint) : prev.hint),
            rowBomb:
              typeof resp.boosters.rowBomb === 'number'
                ? Number(resp.boosters.rowBomb)
                : (typeof resp.boosters.row_bomb === 'number' ? Number(resp.boosters.row_bomb) : prev.rowBomb),
          }));
        }
      } catch (e) {
        setPendingBooster(null);
        showToast(zcText('No se pudo usar la bomba', 'The bomb could not be used'));
        setBusy(false);
        return;
      }

      setPendingBooster(null);

      try { playSpecialBoom(); } catch (e) {}
      try { if (navigator.vibrate) navigator.vibrate([12, 26, 12]); } catch (e) {}

      const bombed = zcApplyRowBomb(board, Number(cell?.r || 0));
      setBoard(bombed);

      await nextFrame();

      try {
        const after = resolveUntilStable(bombed, rngRef.current, 30, { cause: 'booster_row_bomb', at: cell });

        // Apply final board if resolver changed it
        if (after?.board) setBoard(after.board);

        // --- Aggregate mission + level goal deltas for this booster action ---
        let phaseBoard = bombed;
        const boosterAgg = {
          starsCreated: 0,
          starsActivated: 0,
          specialActivations: 0,
          stripesActivated: 0,
          cascades: 0,
          score: 0,
          clearedFire: 0,
          clearedWater: 0,
          clearedAir: 0,
          clearedEarth: 0,
        };

        for (const st of (after?.states || [])) {
          const derivedCleared = getClearedCellsForState(phaseBoard, st);
          const clearedCells = Array.isArray(derivedCleared) ? derivedCleared : [];
          
          if (String(st?.phase || '').toLowerCase().includes('clear') && clearedCells.length > 0) {
            boosterAgg.cascades += 1;
          }

          // Count element clears
          if (clearedCells.length > 0) {
            for (const cc of clearedCells) {
              const raw =
                cc?.tile?.element ??
                (cc?.tile?.key ? (SIGN_ELEMENT?.[cc.tile.key] ?? null) : null);

              const elNorm = normalizeElementKey(raw);
              if (elNorm === 'fire') boosterAgg.clearedFire += 1;
              else if (elNorm === 'water') boosterAgg.clearedWater += 1;
              else if (elNorm === 'air') boosterAgg.clearedAir += 1;
              else if (elNorm === 'earth') boosterAgg.clearedEarth += 1;
            }
          }

          // Stars created (simple heuristic consistent with the main move loop)
          if (String(st?.phase || '').toLowerCase().includes('clear') && clearedCells.length >= 5) {
            boosterAgg.starsCreated += 1;
          }

          // Stripe activations: prefer explicit resolver signals (some versions don't include stripe tile in clearedCells)
          if (String(st?.phase || '').toLowerCase().includes('clear') && clearedCells.length > 0) {
            const stripesActivatedEffective = countStripeActivationsFromState(st);
            if (stripesActivatedEffective > 0) boosterAgg.stripesActivated += stripesActivatedEffective;
            const starsActivatedEffective = countStarActivationsFromState(st);
            if (starsActivatedEffective > 0) boosterAgg.starsActivated += starsActivatedEffective;
          }

          // Advance phase board so derived clears are correct for the next state
          if (st?.board) phaseBoard = st.board;
        }

        // ✅ Missions expect cascade DEPTH (not depth-1). Keep it as-is.
        boosterAgg.cascades = Math.max(0, Number(boosterAgg.cascades || 0) || 0);
        boosterAgg.specialActivations =
          Math.max(0, Number(boosterAgg.starsActivated || 0) || 0) +
          Math.max(0, Number(boosterAgg.stripesActivated || 0) || 0);
        boosterAgg.maxCascade = Math.max(0, Number((boosterAgg.maxCascade ?? boosterAgg.cascades) || 0) || 0);
        boosterAgg.cascadeDepth = Math.max(0, Number((boosterAgg.cascadeDepth ?? boosterAgg.cascades) || 0) || 0);

        // Score delta (booster action does NOT consume a move)
        const d = Number(after?.scoreDelta || 0) || 0;
        if (d) {
          const nextScore = (Number(scoreRef.current || 0) || 0) + d;
          scoreRef.current = nextScore;
          setScore(nextScore);
          setScorePop({ value: d, key: Date.now() });
          window.setTimeout(() => setScorePop(null), 520);
        }

        boosterAgg.score = Number(scoreRef.current || 0) || 0;

    // ✅ Sync daily missions for booster actions (don’t block UI)
    if (typeof hasAnyMissionAggDelta === 'function' && hasAnyMissionAggDelta(boosterAgg)) {
      // ✅ Optimistic missions UI (booster)
      try {
        if (typeof setMissions === 'function') {
          setMissions((prev) => {
            const next = applyAggToMissions(prev, boosterAgg);
            try { pulseMissionIds(getMissionIds(next)); } catch {}
            return next;
          });
        }
      } catch {}

      // ✅ Single sync path
      queueMissionSync(boosterAgg);
    }
        // Update local level-goal progress (UI only)
        setLevelProgress((prev0) => {
          const prev =
            prev0 && typeof prev0 === 'object'
              ? prev0
              : EMPTY_LEVEL_PROGRESS;

          return {
            ...prev,
            score: Math.max(
              Number(prev?.score || 0) || 0,
              Number(boosterAgg?.score || 0) || 0
            ),

            stripesActivated:
              (Number(prev?.stripesActivated || 0) || 0) +
              (Number(boosterAgg?.stripesActivated || 0) || 0),

            starsCreated:
              (Number(prev?.starsCreated || 0) || 0) +
              (Number(boosterAgg?.starsCreated || 0) || 0),

            cascades:
              (Number(prev?.cascades || 0) || 0) +
              (Number(boosterAgg?.cascades || 0) || 0),

            clearedFire:
              (Number(prev?.clearedFire || 0) || 0) +
              (Number(boosterAgg?.clearedFire || 0) || 0),

            clearedWater:
              (Number(prev?.clearedWater || 0) || 0) +
              (Number(boosterAgg?.clearedWater || 0) || 0),

            clearedAir:
              (Number(prev?.clearedAir || 0) || 0) +
              (Number(boosterAgg?.clearedAir || 0) || 0),

            clearedEarth:
              (Number(prev?.clearedEarth || 0) || 0) +
              (Number(boosterAgg?.clearedEarth || 0) || 0),
          };
        });
      } catch (e) {
        // if resolve fails, we still keep the blast applied
      }

      try { showToast(zcText('💥 ¡Fila explotada!', '💥 Row cleared!')); } catch (e) {}
      setBusy(false);
      return;
    }

    if (!selected) {
      replaySelectionAttemptRef.current += 1;
      setSelected({ r: cell.r, c: cell.c });
      return;
    }
      


    // Click en la misma celda => deseleccionar
    if (selected.r === cell.r && selected.c === cell.c) {
      setSelected(null);
      return;
    }

    // Si no es adyacente, solo movemos la selección
    if (!isAdjacent(selected, cell)) {
      replaySelectionAttemptRef.current += 1;
      setSelected({ r: cell.r, c: cell.c });
      return;
    }

    // Swap con animación real (swap -> (si no hay match) swap-back + shake)
    setBusy(true);
    transitionRunLifecycle(RUN_LIFECYCLE.RESOLVING);

    // Snapshot swap positions before setSelected(null)
    const swapFrom = selected;
    const swapTo = cell;
    try {
      replayRecorderRef.current.recordSwapAttempt({
        type: 'swap',
        from: { row: swapFrom.r, col: swapFrom.c },
        to: { row: swapTo.r, col: swapTo.c },
      }, replaySelectionAttemptRef.current);
    } catch (_) {
      // Capture remains inert if a payload exceeds Protocol 1 bounds.
    }
    const swapRes = swapWithEvents(board, swapFrom, swapTo);
    setSelected(null);

    // INVALID: animar swap + swap-back (Candy Crush) y NO consumir movimiento
    if (!swapRes.valid) {
      // Animate swap if adjacent (our UI only reaches here when adjacent)
      const tempSwapped = applySwap(board, swapFrom, swapTo);
      setBoard(tempSwapped);
      await sleep(140);

      // swap-back
      setBoard(board);
      const axis = swapFrom.r === swapTo.r ? 'x' : 'y';
      triggerInvalidFeedback(axis);
      playInvalidThunk();
      showToast(zcText('Movimiento inválido', 'Invalid move'));
      await sleep(180);
      comboStepRef.current = 0;
      setBusy(false);
      transitionRunLifecycle(RUN_LIFECYCLE.READY);
      return;
    }

    logCompletionTrace('swap accepted', {
      from: swapFrom,
      to: swapTo,
      operationId: getZodiacCrushRunOperationId(operationOwnerId),
      levelId: Number(_levelStartedRef.current || levelToPlayNum || 0) || 0,
    });

    // VALID: mostrar swap
    const swapDx = swapTo.c > swapFrom.c ? '-100%' : swapTo.c < swapFrom.c ? '100%' : '0%';
    const swapDy = swapTo.r > swapFrom.r ? '-100%' : swapTo.r < swapFrom.r ? '100%' : '0%';
    const swapKey = `${Date.now()}_${swapFrom.r}_${swapFrom.c}_${swapTo.r}_${swapTo.c}`;
    setSwapPresentation({
      key: swapKey,
      duration: 160,
      cells: [
        { r: swapFrom.r, c: swapFrom.c, x: swapDx, y: swapDy },
        { r: swapTo.r, c: swapTo.c, x: swapTo.c === swapFrom.c ? '0%' : (swapTo.c > swapFrom.c ? '100%' : '-100%'), y: swapTo.r === swapFrom.r ? '0%' : (swapTo.r > swapFrom.r ? '100%' : '-100%') },
      ],
    });
    window.setTimeout(() => setSwapPresentation(null), 190);
    flushSync(() => {
      setBoard(swapRes.board);
    });

    playSwapClick();
    safeVibrate(6);

    let resolveCallId = null;
    if (DEBUG_RESOLVE_LOGS) {
      resolveCallId = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    const resolved = await new Promise((resolve) => {
      logCompletionTrace('resolveUntilStable started', {
        operationId: getZodiacCrushRunOperationId(operationOwnerId),
        levelId: Number(_levelStartedRef.current || levelToPlayNum || 0) || 0,
      });
      setTimeout(() => {
        resolve(resolveUntilStable(swapRes.board, rngRef.current, 30, { swapFrom, swapTo }));
      }, 0);
    });

    if (DEBUG_RESOLVE_LOGS) {
      const zcStatesSnapshot = (resolved?.states || []).map((s) => ({
        phase: s?.phase,
        cause: s?.cause,
        scoreDelta: s?.scoreDelta,
        stripesActivated: s?.stripesActivated,
        stripesActivationBreakdown: s?.stripesActivationBreakdown,
        activated: s?.activated,
      }));

      // Snapshot logs: log ONLY when there are phases (prevents spammy `[]` in console)
      if (zcStatesSnapshot.length) {
        // eslint-disable-next-line no-console
        console.log(`[ZC][DEBUG][resolve:${resolveCallId}][states][snapshot]`, zcStatesSnapshot);
      } else {
        // eslint-disable-next-line no-console
        console.log(`[ZC][DEBUG][resolve:${resolveCallId}] (no-op: stable board)`);
      }
    }
    // Keep an authoritative "current board" for this move so we can derive cleared elements
    // even if the resolver does not provide `clearedCells`.
    let phaseBoard = swapRes.board;

    // IMPORTANT: React state updates are async; use a ref so missions always receive the latest score.
    let scoreAcc = Number(scoreRef.current || 0);

    // Track cascades per move (clear phases), instead of using comboStepRef which is audio/feel.
    let cascadesThisMove = 0;
    let totalDeltaAdded = 0;

    
// Aggregate deltas for the whole move so we POST once per move
const moveMissionAgg = {
  starsCreated: 0,
  starsActivated: 0,
  specialActivations: 0,
  stripesActivated: 0,
  cascades: 0,
  score: 0,
  clearedFire: 0,
  clearedWater: 0,
  clearedAir: 0,
  clearedEarth: 0,
  combo_3: 0,
  combo_4: 0,
  combo_5: 0,
  // Legacy / back-compat aliases (por si el backend usa estos)
  combo3: 0,
  combo4: 0,
  combo5: 0,
};

    for (const st of resolved.states || []) {
  // 1) Derivar cleared cells UNA sola vez (el resolver a veces no los trae)
  const derivedCleared = getClearedCellsForState(phaseBoard, st);
  const clearedCells = Array.isArray(derivedCleared) ? derivedCleared : [];

  // Mantener FX y downstream en sync
  st.clearedCells = clearedCells;

  // 2) Cascada = fase que realmente limpió algo (robusto)
  const phaseStr = String(st?.phase || '').toLowerCase();
  const isClearPhase =
    phaseStr === 'clear' ||
    phaseStr.includes('clear') ||
    phaseStr.includes('explode') ||
    phaseStr.includes('blast') ||
    phaseStr.includes('boom');

  // 🔎 DEBUG: cascades detector (dev only)
  // eslint-disable-next-line no-console
if (DEBUG_ZC_LOGS) console.log('[ZC][cascade]', {
    phaseRaw: st?.phase,
    phaseStr,
    isClearPhase,
    cleared: Array.isArray(clearedCells) ? clearedCells.length : 0,
    hasPhaseStr: !!phaseStr,
  });

  if (clearedCells.length > 0 && (isClearPhase || !phaseStr)) {
    cascadesThisMove += 1;
  }

  // 3) Conteo de elementos para misiones (usa fallback robusto) — UNA sola vez
  if (clearedCells.length > 0) {
    for (const cc of clearedCells) {
      const raw =
        cc?.tile?.element ??
        (cc?.tile?.key ? (SIGN_ELEMENT?.[cc.tile.key] ?? null) : null);

      const elNorm = normalizeElementKey(raw);
      if (elNorm === 'fire') moveMissionAgg.clearedFire += 1;
      else if (elNorm === 'water') moveMissionAgg.clearedWater += 1;
      else if (elNorm === 'air') moveMissionAgg.clearedAir += 1;
      else if (elNorm === 'earth') moveMissionAgg.clearedEarth += 1;
    }
  }

  if (isClearPhase) {
  const count = (clearedCells && clearedCells.length) ? clearedCells.length : 0;

  // IMPORTANT: un 5-match que crea STAR suele limpiar solo 4 (la 5ta se convierte en especial).
  // Entonces inferimos el tamaño del match desde el state cuando existe (matches/groups) y fallback a clearedCells.
  const inferComboSizeFromState = (stLike) => {
    try {
      let best = 0;

      // matches: [{ cells: [...] }]
      if (Array.isArray(stLike?.matches) && stLike.matches.length) {
        for (const m of stLike.matches) {
          const n = Array.isArray(m?.cells) ? m.cells.length : 0;
          if (n > best) best = n;
        }
      }

      // groups: [{ cells: [...] }] o groups: [[...], ...]
      if (Array.isArray(stLike?.groups) && stLike.groups.length) {
        for (const g of stLike.groups) {
          if (Array.isArray(g?.cells)) {
            if (g.cells.length > best) best = g.cells.length;
          } else if (Array.isArray(g)) {
            if (g.length > best) best = g.length;
          }
        }
      }

      // algunos resolvers exponen size directo
      const direct = Number(stLike?.matchSize || stLike?.matchLen || stLike?.size || 0) || 0;
      if (direct > best) best = direct;

      return best;
    } catch {
      return 0;
    }
  };

  const inferredComboSize = inferComboSizeFromState(st);
  const comboCount = Math.max(Number(count || 0) || 0, Number(inferredComboSize || 0) || 0);

  // Visual FX para esta fase clear
  const stForFx = (clearedCells && clearedCells.length)
    ? { ...st, clearedCells }
    : st;
  const inferredMatchTier = comboCount >= 5 ? 5 : comboCount === 4 ? 4 : 3;
  setMatchPresentation(inferredMatchTier);
  window.setTimeout(() => setMatchPresentation(3), inferredMatchTier >= 5 ? 520 : 360);
  try { triggerClearFX(stForFx, cascadesThisMove, inferredMatchTier); } catch (err) {}
  try { spawnClearParticles(stForFx); } catch (err) {}

  // Resolver activation metadata is the sole source for special presentation.
  const activatedSpecials = Array.isArray(st?.activated) ? st.activated : [];
  const hasStripeActivation = activatedSpecials.some((a) => String(a?.special || '').toLowerCase().includes('stripe'));
  const hasStarActivation = activatedSpecials.some((a) => String(a?.special || '').toLowerCase().includes('star'));
  const hasBombActivation = activatedSpecials.some((a) => String(a?.special || '').toLowerCase().includes('bomb'));
  const hasBombCreation = Array.isArray(st?.matches)
    ? st.matches.some((group) => String(group?.powerup?.type || group?.powerUp?.type || '').toLowerCase() === 'bomb')
    : false;
  const creationGroup = Array.isArray(st?.matches)
    ? st.matches.find((group) => String(group?.powerup?.type || group?.powerUp?.type || '').toLowerCase() || '')
    : null;
  const creationPowerup = String(creationGroup?.powerup?.type || creationGroup?.powerUp?.type || '').toLowerCase();
  const creationAnchor = creationGroup?.powerup?.at || creationGroup?.powerUp?.at || creationGroup?.cells?.[creationGroup.cells.length - 1] || null;
  const specialCombo = activatedSpecials.some((a) => String(a?.cause || '').toLowerCase().includes('special_swap'));
  if (creationPowerup || activatedSpecials.length || specialCombo) {
    setSpecialFx({
      key: `special_${Date.now()}_${cascadesThisMove}`,
      activations: activatedSpecials,
      clearedCells,
      creation: creationPowerup ? { type: creationPowerup, at: creationAnchor } : null,
      combo: specialCombo,
    });
    window.setTimeout(() => setSpecialFx(null), specialCombo ? 520 : 420);
  } else {
    setSpecialFx(null);
  }
  try {
    if (hasStripeActivation) playStripeActivation();
    if (hasStarActivation) playStarActivation();
    if (hasBombActivation) playBombActivation();
    if (comboCount === 4) playStripeZap();
    if (comboCount >= 5) playStarCreationCue();
    if (hasBombCreation) playBombCreationCue();
    if (specialCombo) playSpecialComboCue();
  } catch (_) {}
  if (specialCombo) safeVibrate([16, 24, 16]);
  else if (hasStripeActivation || hasStarActivation || hasBombActivation) safeVibrate([12, 24, 12]);
  else if (comboCount >= 5) safeVibrate(16);
  else if (comboCount === 4) safeVibrate(12);

  // For performance: avoid full per-tile counting on simple 3-matches on mobile.
  const countsByEl = { fire: 0, water: 0, air: 0, earth: 0, neutral: 0 };
  if (!(lowFxRef.current && count === 3)) {
    for (const cc of clearedCells || []) {
      const raw =
        cc?.tile?.element ??
        (cc?.tile?.key ? (SIGN_ELEMENT?.[cc.tile.key] ?? null) : null);

      const k = normalizeElementKey(raw) || 'neutral';
      if (k === 'fire') countsByEl.fire += 1;
      else if (k === 'water') countsByEl.water += 1;
      else if (k === 'air') countsByEl.air += 1;
      else if (k === 'earth') countsByEl.earth += 1;
      else countsByEl.neutral += 1;
    }
  }

  // Safety: si este clear no limpió fichas, no dispares FX/SFX.
  if (count <= 0) {
    setBoard(st.board);
    phaseBoard = st.board;
    setClearingIds(null);
    await sleep(120);
    continue;
  }

    // Stripe activations: prefer explicit resolver signals (some versions don't include stripe tile in clearedCells)
      const stripesActivatedEffective = countStripeActivationsFromState(st);

      //Special boom ONLY when a STRIPE actually activates.
      // Guard with `count >= 4` to avoid any accidental boom on simple 3-matches.
      if (stripesActivatedEffective > 0 && count >= 4) {
        try { playSpecialBoom(); } catch (err) {}
      }

  // Choose a representative element for SFX/FX (dominant element in this clear)
  const dominantElKey = (['fire', 'water', 'air', 'earth']
    .sort((a, b) => (countsByEl[b] || 0) - (countsByEl[a] || 0))[0]) || null;

  const el = (dominantElKey && (countsByEl[dominantElKey] || 0) > 0) ? dominantElKey : null;

  if (DEBUG_SOUND) {
    // eslint-disable-next-line no-console
    console.log('[ZC][sound]', { countsByEl, el, count, comboStep: comboStepRef.current });
  }
    
    

  // Element sound (mapped) + safe fallback
  if (el && SOUND_BY_ELEMENT[el]) {
    SOUND_BY_ELEMENT[el]();
  } else {
    playTone({ freq: 520, type: 'sine', gain: 0.10, dur: 0.06 });
  }

  const isAir = el === 'air';
  const isWater = el === 'water';
  const isEarth = el === 'earth';
  const base = isAir ? 760 : isWater ? 600 : isEarth ? 440 : 680;

  if (count === 3) {
    playMatchTick3(isEarth ? 480 : isWater ? 540 : isAir ? 720 : 610);
  } else if (count === 4) {
    playMatchBell4(comboStepRef.current, base + 40);
    window.setTimeout(() => playComboDing(comboStepRef.current + 0, base), 55);
  } else if (count >= 5) {
    playMatchBell4(comboStepRef.current, base + 90);
    window.setTimeout(() => playMatchBell4(comboStepRef.current + 1, base + 160), 70);

    window.setTimeout(() => playComboDing(comboStepRef.current + 0, base + 40), 40);
    window.setTimeout(() => playComboDing(comboStepRef.current + 1, base + 110), 110);

    if (Math.random() < 0.35) {
      window.setTimeout(() => playCrystalSparkle(comboStepRef.current), 85);
    }
  }

  comboStepRef.current = Math.min(12, comboStepRef.current + 1);
  try {
      const shouldVibrate = !(lowFxRef.current && count === 3);
      if (shouldVibrate && comboCount < 4) safeVibrate(8);
      if (cascadesThisMove >= 4) safeVibrate([18, 28, 18]);
  } catch (err) {}

  // Shorter pause on mobile for simple 3-matches (feels more responsive)
  await sleep(lowFxRef.current && count === 3 ? 95 : 160);

  setBoard(st.board);
  phaseBoard = st.board;
  setClearingIds(null);

    if (st.scoreDelta != null) {
    const d = Number(st.scoreDelta || 0);
    scoreAcc = scoreAcc + d;
    scoreRef.current = scoreAcc;
      setScore(scoreAcc);
      logCompletionTrace('score updated', {
        score: scoreAcc,
        operationId: getZodiacCrushRunOperationId(operationOwnerId),
        levelId: Number(_levelStartedRef.current || levelToPlayNum || 0) || 0,
      });
      totalDeltaAdded += d;
  }

// Mission metric: count ONE combo bucket per clear-phase (prefer highest bucket)
// - combo_5: 5+ tiles matched in a single clear-phase
// - combo_4: 4 tiles matched
// - combo_3: 3 tiles matched
// NOTE: un 5-match que crea STAR puede limpiar 4 (la 5ta se convierte), por eso usamos comboCount.
if (comboCount >= 5) {
  moveMissionAgg.combo_5 += 1;
  moveMissionAgg.combo5 += 1;
} else if (comboCount === 4) {
  moveMissionAgg.combo_4 += 1;
  moveMissionAgg.combo4 += 1;
} else if (comboCount === 3) {
  moveMissionAgg.combo_3 += 1;
  moveMissionAgg.combo3 += 1;
}

// Treat any 5+ as STAR creation for missions, even if clearedCells is 4.
if (comboCount >= 5) {
  moveMissionAgg.starsCreated += 1;
}

if (stripesActivatedEffective > 0) {
  moveMissionAgg.stripesActivated += stripesActivatedEffective;
}
const starsActivatedEffective = countStarActivationsFromState(st);
if (starsActivatedEffective > 0) {
  moveMissionAgg.starsActivated += starsActivatedEffective;
}

  await sleep(lowFxRef.current && count === 3 ? 18 : 40);
} else {
  const phaseMotion = phaseStr === 'gravity' && Array.isArray(st?.fallEvents)
    ? {
        key: `fall_${Date.now()}_${cascadesThisMove}`,
        moves: st.fallEvents.map((event) => ({
          id: event?.id,
          type: 'fall',
          y: `${-Math.max(1, Number(event?.to?.r ?? 0) - Number(event?.from?.r ?? 0)) * 100}%`,
          duration: Math.min(220, 140 + Math.max(0, Number(event?.to?.r ?? 0) - Number(event?.from?.r ?? 0)) * 18),
        })).filter((event) => event.id),
      }
    : phaseStr === 'refill' && Array.isArray(st?.spawnEvents)
      ? {
          key: `spawn_${Date.now()}_${cascadesThisMove}`,
          moves: st.spawnEvents.map((event, index) => ({
            id: event?.id,
            type: 'spawn',
            y: `${-Math.max(1, Number(event?.at?.r ?? 0) + 1) * 100}%`,
            duration: lowFxRef.current ? 130 : 160,
            delay: lowFxRef.current ? 0 : Math.min(24, index * 4),
          })).filter((event) => event.id),
        }
      : null;
  if (phaseMotion?.moves?.length) setPresentationMotion(phaseMotion);
  setBoard(st.board);
  await sleep(phaseMotion?.moves?.length ? (lowFxRef.current ? 130 : 180) : 120);
  if (phaseMotion?.moves?.length) setPresentationMotion(null);
}
}

    // If the engine didn't provide per-state deltas, apply the total delta once.
    try {
      const total = Number(resolved?.scoreDelta || 0);
      if (total && !totalDeltaAdded) {
        scoreAcc = Number(scoreRef.current || 0) + total;
        scoreRef.current = scoreAcc;
        setScore(scoreAcc);
        logCompletionTrace('score updated', {
          score: scoreAcc,
          operationId: getZodiacCrushRunOperationId(operationOwnerId),
          levelId: Number(_levelStartedRef.current || levelToPlayNum || 0) || 0,
        });
      }
    } catch (e) {}
    // fallback
    if (!resolved.states || resolved.states.length === 0) {
      setBoard(resolved.board);
      // (missions sync for no-states path handled in single POST below)
    }

    // --- Candy Crush rule: never leave the player without a valid move ---
    // If the board has no possible swaps that create a match, auto-reshuffle (NO move cost).
    // We keep score/missions/moves as-is.
    await afterNextPaint();
    const finalBoard = resolved?.board || board;
    if (finalBoard && !hasAnyValidMove(finalBoard)) {
      const nextBoard = createPlayableBoard(rngRef.current);
      setBoard(nextBoard);
      setSelected(null);
      showToast('🔄 Tablero reordenado');
      // Small pause so the change feels intentional
      await sleep(120);
    }

// ✅ Report cascades as DEPTH (number of clear phases in this move).
// Backend missions expect DEPTH (ej: 2 cascadas = 2 clear phases).
const cascadesDepthThisMove = Math.max(0, Number(cascadesThisMove || 0) || 0);

moveMissionAgg.cascades = Math.max(0, Number(cascadesDepthThisMove || 0));
moveMissionAgg.specialActivations =
  Math.max(0, Number(moveMissionAgg.starsActivated || 0) || 0) +
  Math.max(0, Number(moveMissionAgg.stripesActivated || 0) || 0);

// Back-compat aliases (por si el backend lee alguno de estos nombres)
moveMissionAgg.maxCascade = cascadesDepthThisMove;
moveMissionAgg.cascadeDepth = cascadesDepthThisMove;

moveMissionAgg.score = Number(scoreRef.current || scoreAcc || 0) || 0;

    // Track best cascade count during this run (per-move clear phases)
    maxCascadeRunRef.current = Math.max(Number(maxCascadeRunRef.current || 0), Number(cascadesThisMove || 0));

      // Visual-only multiplier (does NOT change score): conveys \"combo feel\".
const mult =
  cascadesThisMove <= 1 ? 1 :
  cascadesThisMove === 2 ? 1.2 :
  cascadesThisMove === 3 ? 1.4 :
  cascadesThisMove === 4 ? 1.7 : 2.0;

if (mult > 1) {
  setMultPop({ mult, key: Date.now() });
  window.setTimeout(() => setMultPop(null), cascadesThisMove >= 4 ? 820 : 650);
}
if (cascadesThisMove >= 4) {
  setPunchOn(true);
  if (punchTimerRef.current) window.clearTimeout(punchTimerRef.current);
  punchTimerRef.current = window.setTimeout(() => setPunchOn(false), 190);
}
      
    comboStepRef.current = 0;
    const nextMoves = Math.max(0, (Number(movesLeft || 0) - 1));
    setMovesLeft(nextMoves);
    _hasPlayedMoveRef.current = true;

    // Submit the FINAL score once per run (score_reach mission)
    // - Use nextMoves (not stale closure)
    // - Use runStartMsRef for duration (seed is constant but this is clearer)
    // - Use maxCascadeRunRef for best cascade of the run
    if (nextMoves <= 0) {
      setRunOver(true);

      deferAfterFrame(() => {
        submitRunIfNeeded().catch((e) => {
          console.warn('[ZodiacCrush] submitRun failed', e);
        });
      });
    }

    // Finalize per-move aggregate
    
    moveMissionAgg.score = Number(scoreAcc || 0);

    // UX: never keep a tile selected after a completed move
    setSelected(null);

    // The resolver is fully settled; completion may now evaluate the stable board.
    transitionRunLifecycle(RUN_LIFECYCLE.READY);
    // IMPORTANT: release the UI FIRST (prevents micro-lag on mobile)
    setBusy(false);

    // ✅ Optimistic missions UI: update the bar immediately while playing
    try {
      if (typeof setMissions === 'function' && typeof hasAnyMissionAggDelta === 'function' && hasAnyMissionAggDelta(moveMissionAgg)) {
        setMissions((prev) => {
  const next = applyAggToMissions(prev, moveMissionAgg);
  try { pulseMissionIds(getMissionIds(next)); } catch {}
  return next;
});

      }
    } catch {}

    // ✅ Single sync path
    queueMissionSync(moveMissionAgg);

    // 🎯 Update local level progress (onboarding / UI only)
setLevelProgress((prev0) => {
  const prev =
    prev0 && typeof prev0 === 'object'
      ? prev0
      : EMPTY_LEVEL_PROGRESS;

  return {
    ...prev,
    score: Math.max(Number(prev.score || 0), Number(moveMissionAgg.score || 0)),

    stripesActivated:
      (Number(prev.stripesActivated || 0) || 0) +
      (Number(moveMissionAgg.stripesActivated || 0) || 0),

    starsCreated:
      (Number(prev.starsCreated || 0) || 0) +
      (Number(moveMissionAgg.starsCreated || 0) || 0),

    cascades:
      (Number(prev.cascades || 0) || 0) +
      (Number(moveMissionAgg.cascades || 0) || 0),

    clearedFire:
      (Number(prev.clearedFire || 0) || 0) +
      (Number(moveMissionAgg.clearedFire || 0) || 0),

    clearedWater:
      (Number(prev.clearedWater || 0) || 0) +
      (Number(moveMissionAgg.clearedWater || 0) || 0),

    clearedAir:
      (Number(prev.clearedAir || 0) || 0) +
      (Number(moveMissionAgg.clearedAir || 0) || 0),

    clearedEarth:
      (Number(prev.clearedEarth || 0) || 0) +
      (Number(moveMissionAgg.clearedEarth || 0) || 0),
  };
});

  }, [
  clearHintForActivity,
  scheduleIdleHint,
  busy,
  movesLeft,
  selected,
  board,
  showToast,
  sleep,
  triggerClearFX,
  spawnClearParticles,
  triggerInvalidFeedback,
  submitRunIfNeeded,
  deferAfterFrame,
  nextFrame,
  boosterOnServer,
  boosters?.rowBomb,
  pendingBooster?.type,
  hintSwap,
  setHintSwap,
  applyAggToMissions,
  setMissions,
  pulseMissionIds,
  queueMissionSync,
  transitionRunLifecycle,
  levelToPlayNum,
  operationOwnerId,
]);

  // Exit ZodiacCrush screen (always go Home immediately)
  // Avoids needing 2 taps when internal history entries exist.
  const exitZodiacCrush = useCallback(() => {
  setSelectedLevelFromMap(null);
  setPlayNonce(Date.now());
  setGoalsNonce(Date.now()); // ✅ NUEVO

  try {
    window.location.assign('/');
  } catch (_) {
    window.location.href = '/';
  }
}, []);

  const startAuthoritativelyUnlockedNextLevel = useCallback(() => {
    const confirmed = confirmedCompletionRef.current;
    if (
      runLifecycleRef.current !== RUN_LIFECYCLE.WON ||
      !confirmed ||
      confirmed.runId !== activeRunIdRef.current ||
      confirmed.unlockedNext !== true
    ) return;

    primeAudio();
    const next = Math.min(240, Math.max(1, Number(confirmed.nextLevel || 0) || ((Number(confirmed.level || levelToPlay || 1) || 1) + 1)));
    const nextWorldIndex = Math.min(11, Math.floor((next - 1) / 20));
    const nextSignKey = WORLD_ORDER[nextWorldIndex] || 'aries';
    setMapWorld({ worldIndex: nextWorldIndex, signKey: nextSignKey });
    setSelectedLevelFromMap(next);
    _levelStartedRef.current = next;
    levelToPlayNumRef.current = next;
    _levelCompletionFireRef.current = { key: `${next}:${nextSignKey}`, fired: false };
    _levelWinShownRef.current = { key: `${next}:${nextSignKey}`, shown: false };
    _hasPlayedMoveRef.current = false;
    confirmedCompletionRef.current = null;
    setPlayNonce(Date.now());
    setZcView('game');
    setLevelWin(null);
  }, [levelToPlay]);

  // =========================
  // View switch (game / map / world)
  // =========================
  if (zcView === 'map') {
    return (
      <ZodiacCrushMapScreen
        // Back/Close should EXIT the feature (avoid loop: map -> game -> map)
        onBack={exitZodiacCrush}
        onClose={exitZodiacCrush}

        // Navigate deeper
        onOpenWorld={openWorld}

        // Start a level from map
        onPlayLevel={playLevelFromMap}

        // Current selection (optional)
        selectedLevel={levelToPlay}
        selectedLevelFromMap={selectedLevelFromMap}
        setSelectedLevelFromMap={setSelectedLevelFromMap}
      />
    );
  }

  if (zcView === 'world') {
    return (
      <ConstellationMapScreen
        worldIndex={Number(mapWorld?.worldIndex || 0)}
        signKey={String(mapWorld?.signKey || 'aries')}

        // Back to map
        onBack={() => setZcView('map')}
        onClose={() => setZcView('map')}

        // Start a level from world
        onPlayLevel={playLevelFromMap}

        // Optional: keep world UI aware of current selection
        selectedLevel={levelToPlay}
      />
    );
  }

  // Default: zcView === 'game' -> render the existing game UI below
  return (
  <div
    className="zc-root"
    style={{
      minHeight: '100vh',
      padding: '14px 12px 80px',
      background: '#070010',
      color: '#FFD700',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'visible',
      '--zc-world-primary': worldVisualProfile.primary,
      '--zc-world-secondary': worldVisualProfile.secondary,
      '--zc-world-glow': worldVisualProfile.glow,
      '--zc-world-bg-a': worldVisualProfile.bgA,
      '--zc-world-bg-b': worldVisualProfile.bgB,
      '--zc-world-position': worldVisualProfile.position,
      '--zc-world-atmosphere': worldVisualProfile.atmosphere,
    }}
    data-zc-atmosphere={worldVisualProfile.atmosphere}
    data-zc-particle={worldVisualProfile.particle}
  >
    <style>{ZC_UI_GAMER_CSS}</style>
    <style>{`
      @keyframes zcWinBannerIn {
        0% { transform: translateY(-10px) scale(0.98); opacity: 0; }
        100% { transform: translateY(0) scale(1); opacity: 1; }
      }
      @keyframes zcWinBannerGlow {
        0%, 100% { box-shadow: 0 16px 38px rgba(0,0,0,0.45); }
        50% { box-shadow: 0 20px 52px rgba(255,215,0,0.14), 0 16px 38px rgba(0,0,0,0.45); }
      }

    `}</style>
    {/* Premium cosmic background */}
      <div className="zc-bg" aria-hidden="true" />
      
      {/* ✅ Level win premium banner + confetti */}

{showLevelWinConfetti && <ZcConfettiBurst show={showLevelWinConfetti} />}

    {/* ✅ Level win premium banner + confetti */}
{levelWinBanner && (
  <div className="zc-win-banner" key={levelWinBanner.key}>
    <div className="zc-win-banner__card">
      <div className="zc-win-banner__top">
        <div className="zc-win-banner__badge">🏆 {zcText('Nivel completado', 'Level complete')}</div>
        <div className="zc-win-banner__badge">{zcText('Nivel', 'Level')} {Number(levelWinBanner.level || 0) || 0}</div>
      </div>

      <div className="zc-win-banner__title">{zcText('¡Excelente!', 'Excellent!')} 🔥</div>
      <p className="zc-win-banner__sub">
        {levelWinBanner?.unlockedNext
          ? zcText('Desbloqueaste el siguiente nivel. Seguí sumando estrellas y XP.', 'You unlocked the next level. Keep earning stars and XP.')
          : zcText('Nivel guardado. Seguí sumando estrellas y XP.', 'Level saved. Keep earning stars and XP.')}
      </p>

      <div className="zc-win-banner__stars" aria-hidden>
        {Array.from({ length: Math.max(1, Math.min(3, Number(levelWinBanner?.stars || 3) || 3)) }).map((_, i) => (
          <span className="zc-win-banner__star" key={`zc_star_${levelWinBanner.key}_${i}`}>⭐</span>
        ))}
      </div>
    </div>
  </div>
)}



      {flash && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: `radial-gradient(circle at 50% 40%, ${flash.color}, transparent 55%)`,
            pointerEvents: 'none',
            animation: 'zodiacFlash 180ms ease-out',
            zIndex: 2,
          }}
        />
      )}
      <div className="zc-content" style={{ maxWidth: 720, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div className="zc-topbar">
    <button
            className="zc-btn zc-topbar__back"
            onClick={() => {
              primeAudio();
              setRunOver(false);
              setLevelWin(null);

              // ✅ IMPORTANT: limpiar la URL /play?level=XX para que al refrescar NO vuelva al nivel
              try { window.history.replaceState({}, '', '/zodiac-crush'); } catch (_) {}

              // Volver a la lista del mundo actual sin perder el nivel seleccionado.
              openCurrentWorldLevelList();
            }}
            type="button"
          >
            ← {zcText('Volver', 'Back')}
          </button>

          <div className="zc-topbar__titleWrap">
            <div className="zc-topbar__title">ZODIAC CRUSH</div>
            <div className="zc-topbar__subtitle">{zcText('Nivel', 'Level')} {levelToPlayNum} · {zcText('Match-3 cósmico · Combiná signos, ganá XP', 'Cosmic match-3 · Match signs and earn XP')}</div>
          </div>

          <div
  className="zc-topbar__actions"
  style={{
    display: "flex",
    gap: 8,
    position: "relative",
    flexWrap: "wrap",
    alignItems: "center",
    maxWidth: "100%",
  }}
>
            <button
  className="zc-btn zc-btn--icon"
  onClick={() => {
    primeAudio();

    // ✅ IMPORTANT: limpiar la URL /play?level=XX para que al refrescar NO vuelva al nivel
    try { window.history.replaceState({}, '', '/zodiac-crush'); } catch (_) {}

    openMap();
  }}
  type="button"
  aria-label={zcText('Mundos', 'Worlds')}
  title={zcText('Mundos', 'Worlds')}
>
  <span className="zc-btn__icon" aria-hidden="true">🗺️</span>
  <span className="zc-btn__text">{zcText('Mundos', 'Worlds')}</span>
</button>
            <button
              className="zc-btn zc-btn--icon zc-btn--rank"
              onClick={() => {
  primeAudio();
  setLbScope('global'); // abre siempre con data
  setShowLeaderboard(true);
}}
              type="button"
              aria-label="Ranking"
              title="Ranking"
            >
              🏆
            </button>
            <button
              className="zc-btn zc-btn--icon"
              onClick={() => {
                primeAudio();
                const next = !isMuted();
                setMuted(next);
                if (_masterGain && _audioCtx) {
                  _masterGain.gain.setValueAtTime(next ? 0 : 0.85, _audioCtx.currentTime);
                }
                showToast(next ? 'Sonido: OFF' : 'Sonido: ON');
              }}
              type="button"
              aria-label="Sonido"
            >
              {isMuted() ? '🔇' : '🔈'}
            </button>
            <button
  className="zc-btn zc-btn--icon zc-btn--help"
  onClick={() => {
    primeAudio();
    toggleHelp();
  }}
  type="button"
          aria-label={zcText('Cómo jugar', 'How to play')}
  style={{
    whiteSpace: "nowrap",
    fontSize: "12px",
    padding: "8px 10px",
    maxWidth: "100%",
  }}
>
  {zcText('¿Cómo jugar?', 'How to play?')}
</button>
          </div>
        </div>

        {/* HUD */}
        {showHelp && <ZodiacCrushHelpCard onClose={closeHelp} />}
        {showLeaderboard && (
          <ZodiacCrushLeaderboardModal
            onClose={() => setShowLeaderboard(false)}
            scope={lbScope}
            onChangeScope={setLbScope}
            loading={!!lbState?.loading}
            error={lbState?.error}
            entries={Array.isArray(lbState?.entries) ? lbState.entries : []}
            my={lbState?.my || null}
          />
        )}
        {runOver && (
          <ZodiacCrushRunEndModal
            onClose={() => setRunOver(false)}
            onReplay={() => {
              setRunOver(false);
              // Replay should restart the SAME world/level context (keeps element bias + stable seed prefix)
              resetRun({
  toast: true,
  seedPrefix: `${activeSignKey}_w${activeWorldIndex}_lvl${levelToPlayNum}`,
});
            }}
            onBack={() => {
              setRunOver(false);
              openCurrentWorldLevelList();
            }}
            score={score}
            maxCascade={Number(maxCascadeRunRef.current || 0)}
            allDoneBadge={allDoneBadge}
            missions={missions}
            bestScoreLocal={bestScoreLocal}
            isNewRecord={nuevoRecordBadge}
          />
        )}

        {levelWin && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      zIndex: 999999,
      animation: 'zcEndOverlayIn 180ms ease-out',
    }}
    role="dialog"
    aria-modal="true"
  >
    <div
      style={{
        width: 'min(520px, 94vw)',
        borderRadius: 18,
        padding: '16px 14px',
        background: 'linear-gradient(180deg, rgba(10,6,18,0.96), rgba(0,0,0,0.92))',
        border: '1px solid rgba(255,215,0,0.20)',
        boxShadow: '0 26px 60px rgba(0,0,0,0.55)',
        color: 'rgba(255,255,255,0.92)',
        animation: 'zcEndCardIn 220ms ease-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontWeight: 950, letterSpacing: 0.8 }}>{zcText('🏆 NIVEL COMPLETADO', '🏆 LEVEL COMPLETE')}</div>
        <button
          type="button"
          onClick={() => setLevelWin(null)}
          style={{
            border: 'none',
            background: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.92)',
            borderRadius: 999,
            padding: '8px 10px',
            fontWeight: 900,
            cursor: 'pointer',
          }}
          aria-label={zcText('Cerrar', 'Close')}
          title={zcText('Cerrar', 'Close')}
        >
          ✕
        </button>
      </div>

      <div style={{ marginTop: 10, fontWeight: 900, opacity: 0.92 }}>
        {zcText('Nivel', 'Level')} {Number(levelWin.level || levelToPlay || 1)} · ⭐ {Number(levelWin.stars || 3)}
      </div>

      <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
        <button
          type="button"
          onClick={() => {
            primeAudio();
            setLevelWin(null);
            resetRun({
  toast: true,
  seedPrefix: `${activeSignKey}_w${activeWorldIndex}_lvl${levelToPlayNum}`,
});
          }}
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.92)',
            fontWeight: 950,
            cursor: 'pointer',
          }}
        >
          {zcText('↻ Rejugar nivel', '↻ Replay level')}
        </button>

        <button
          type="button"
          onClick={startAuthoritativelyUnlockedNextLevel}
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 14,
            border: 'none',
            background: 'linear-gradient(135deg, #FFD700, #FFB700)',
            color: '#4B0082',
            fontWeight: 950,
            cursor: 'pointer',
            boxShadow: '0 16px 36px rgba(0,0,0,0.45)',
          }}
        >
          {zcText('➡️ Siguiente nivel', '➡️ Next level')}
        </button>

        <button
          type="button"
          onClick={() => {
            primeAudio();
            setLevelWin(null);

            // ✅ IMPORTANT: limpiar la URL /play?level=XX para que al refrescar NO vuelva al nivel
            try { window.history.replaceState({}, '', '/zodiac-crush'); } catch (_) {}

            openMap();
          }}
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(0,0,0,0.22)',
            color: 'rgba(255,255,255,0.92)',
            fontWeight: 950,
            cursor: 'pointer',
          }}
        >
          🗺️ {zcText('Volver al mapa', 'Back to map')}
        </button>
      </div>
    </div>
  </div>
)}

        {/* Compact mission access remains in the active gameplay context. */}
        <MissionsPanelGamer
          missions={missions}
          missionPulse={missionPulse}
          streak={Number(profile?.dailyReward?.dayCompleteStreak ?? 0) || 0}
        />

        {/* Daily Missions are rendered above the active HUD. */}
        <div className="zc-hud" aria-label="Stats">
  <div className="zc-hud__chip" title={zcText('Puntaje actual', 'Current score')}>
    <span className="zc-hud__label">Score</span>
    <span className="zc-hud__value">
      {Number(score || 0).toLocaleString('es-AR')}
    </span>
  </div>
  <XpLevelChip profile={profile} rewards={rewardsUnlocked} />

  <div className="zc-hud__chip" title={zcText('Movimientos restantes', 'Moves remaining')}>
    <span className="zc-hud__label">{zcText('Cristales', 'Crystals')}</span>
    <span className="zc-hud__value zc-crystalCompact" aria-label={zcText('Movimientos restantes', 'Moves remaining')}>
      💎 {Math.max(0, Number(movesLeft || 0))}/{Number(movesTotal || 0)}
    </span>
  </div>

  <div className={`zc-hud__chip zc-hud__chip--boosters ${busy ? 'is-busy' : ''}`} title={zcText('Boosters', 'Boosters')}>
    <div className="zc-hud__boostersLabel">
      <span className="zc-hud__label">{zcText('Boosters', 'Boosters')}</span>
    </div>
    <div className="zc-hud__boosterControls">
      <button
        className="zc-hudBoosterButton"
        type="button"
        onClick={handleUseHintBooster}
        disabled={!boosters?.hint}
        aria-label={boosters?.hint ? zcText('Usar pista', 'Use hint') : zcText('No tenés pistas disponibles', 'No hints available')}
        title={boosters?.hint ? zcText('Usar pista', 'Use hint') : zcText('No tenés pistas disponibles', 'No hints available')}
      >
        💡 <span>{boosters?.hint || 0}</span>
      </button>
      <button
        className="zc-hudBoosterButton"
        type="button"
        onClick={handleUseRowBombBooster}
        disabled={!boosters?.rowBomb}
        aria-label={boosters?.rowBomb ? zcText('Usar bomba de fila', 'Use row bomb') : zcText('No tenés bombas disponibles', 'No bombs available')}
        title={boosters?.rowBomb
          ? (pendingBooster?.type === 'rowBomb'
            ? zcText('Elegí una ficha para explotar la fila', 'Choose a tile to clear its row')
            : zcText('Usar bomba de fila', 'Use row bomb'))
          : zcText('No tenés bombas disponibles', 'No bombs available')}
      >
        💣 <span>{boosters?.rowBomb || 0}</span>
      </button>
    </div>
  </div>
</div>

        {scorePop && (
          <div
            key={scorePop.key}
            style={{
              position: 'absolute',
              top: 96,
              left: '50%',
              transform: `translateX(-50%) scale(${Number(scorePop.scale || 1).toFixed(2)})`,
              fontWeight: 900,
              fontSize: scorePop.tier >= 5 ? 25 : scorePop.tier === 4 ? 23 : 22,
              color: '#FFD700',
              textShadow: '0 6px 18px rgba(0,0,0,0.45)',
              animation: `scorePop ${Number(scorePop.lifetime || 520)}ms ease-out`,
              pointerEvents: 'none',
            }}
          >
            +{scorePop.value}
          </div>
        )}

        {xpFly && (
          <div
            key={xpFly.key}
            style={{
              position: 'absolute',
              top: 72,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '8px 12px',
              borderRadius: 999,
              background: 'linear-gradient(135deg, rgba(218,165,32,0.92), rgba(255,215,0,0.92))',
              color: '#4B0082',
              fontWeight: 950,
              fontSize: 13,
              boxShadow: '0 16px 34px rgba(0,0,0,0.40)',
              border: '1px solid rgba(255,255,255,0.18)',
              animation: 'zcXpFly 1150ms ease-out',
              pointerEvents: 'none',
              zIndex: 6,
            }}
          >
            +{Number(xpFly.value || 0)} XP
          </div>
        )}
              
              {multPop && (
  <div
    key={multPop.key}
    style={{
      position: 'absolute',
      top: 128,
      left: '50%',
      transform: 'translateX(-50%)',
      fontWeight: 950,
      fontSize: 16,
      color: '#FFD700',
      textShadow: '0 8px 18px rgba(0,0,0,0.45)',
      animation: 'zcMultPop 650ms ease-out',
      pointerEvents: 'none',
      opacity: 0.95,
    }}
  >
    ✨ x{Number(multPop.mult).toFixed(1)}
  </div>
)}

        {/* Board */}
        {/* 🎯 Objetivos del nivel (render UNA sola vez, fuera del overlay de partículas) */}
        <div className="zc-goalsWrap" style={{ display: 'flex', justifyContent: 'center', marginTop: 8, marginBottom: 6 }}>
          <ZodiacCrushLevelGoals
  key={`zc-goals_${String(activeSignKey || 'x')}_w${String(activeWorldIndex ?? '0')}_lvl${String(levelToPlayNum)}_g${String(goalsNonce ?? '0')}`}
  level={levelToPlayNum}
  signKey={activeSignKey}
  goals={levelGoals}
  nonce={goalsNonce}
  levelProgress={canonicalGoalState.progress}
  canonicalGoalState={canonicalGoalState}
  onGoalCompleted={handleGoalCompletedToast}
/>
        </div>
        <div
          ref={boardWrapRef}
          className={
            (shake.on ? (shake.axis === 'y' ? 'zc-shake-y' : 'zc-shake-x') : '') +
            (punchOn ? ' zc-punch' : '') +
            ' zc-boardWrap'
          }
          style={{
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            marginTop: 8,
            boxShadow: `0 0 0 1px ${worldVisualProfile.glow}, 0 18px 42px ${worldVisualProfile.glow}`,
            // transform: 'scale(1.08)',       // 🔥 TABLERO MÁS GRANDE
            // transformOrigin: 'top center',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              overflow: 'visible',
              zIndex: 5,
            }}
          >
            {particles.map((p) => (
              <div
                key={p.id}
                className={`zc-p zc-${p.element} zc-${p.kind}`}
                style={{
                  position: 'absolute',
                  left: `${p.x * 100}%`,
                  top: `${p.y * 100}%`,
                  width: 10,
                  height: 10,
                  transform: `translate(-50%, -50%) rotate(${p.rot}deg) scale(${p.scale})`,
                  '--dx': `${p.dx}px`,
                  '--dy': `${p.dy}px`,
                  '--c1': p.colorA,
                  '--c2': p.colorB,
                }}
              />
            ))}
          </div>
         <BoardView
            board={board}
            selected={selected}
            onCellClick={onCellClick}
            level={levelToPlayNum}
            signKey={activeSignKey}
            worldIndex={activeWorldIndex}
            disabled={!!busy || !!runOver || !!levelWin}
            clearingIds={clearingIds}
            hintCells={
              visibleHintSwap
                ? [
                    { r: visibleHintSwap.a.r, c: visibleHintSwap.a.c },
                    { r: visibleHintSwap.b.r, c: visibleHintSwap.b.c },
                  ]
                : null
            }
            hasHint={!!visibleHintSwap}
            swapAnimation={swapPresentation}
            presentationMotion={presentationMotion}
            matchTier={matchPresentation}
            specialFx={specialFx}
            worldTint={worldVisualProfile.glow}
          />
        </div>

        <div className="zc-legendWrap">
  <div className="zc-legend">
    {zcText('Tocá un signo y luego uno adyacente para intercambiar.', 'Tap a sign, then an adjacent one, to swap them.')}
  </div>
</div>

        {/* Toast */}
        {toast && (
          <div
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 18,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 999999,
            }}
          >
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,215,0,0.18)',
                color: '#FFD700',
                boxShadow: '0 12px 26px rgba(0,0,0,0.35)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              {toast}
            </div>
          </div>
        )}

              <style>{`
        /* ===== Topbar actions (Help / Sound / Ranking / Mundos) ===== */
.zc-topbar{
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "back actions"
    "title title";
  gap: 10px;
  margin: 10px 12px 8px;
  padding: 12px 12px 12px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255,255,255,.12), rgba(255,255,255,.06));
  border: 1px solid rgba(255,255,255,.14);
  box-shadow: 0 16px 46px rgba(0,0,0,.28);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  overflow: hidden;
}

.zc-topbar__back{
  grid-area: back;
  justify-self: start;
  max-width: min(52vw, 280px);
}

.zc-topbar__actions{
  grid-area: actions;
  justify-self: end;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.zc-topbar__titleWrap{
  grid-area: title;
  min-width: 0;
  display:flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.zc-topbar__title{
  font-weight: 950;
  letter-spacing: 1.1px;
  line-height: 1.1;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
}

.zc-topbar__subtitle{
  font-weight: 900;
  opacity: 0.9;
  line-height: 1.15;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
}

/* Mobile safe-area + slightly tighter spacing */
@media (max-width: 520px){
  .zc-topbar{
    gap: 8px;
    padding: calc(10px + env(safe-area-inset-top, 0px)) 10px 10px;
    border-radius: 16px;
  }

  .zc-topbar__actions{ gap: 6px; }

  .zc-topbar__title{
    font-size: 13px;
  }

  .zc-topbar__subtitle{
    font-size: 11.5px;
  }
}

/* Ranking button: premium icon chip (same language as other icon buttons) */
.zc-btn--rank{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width: 42px;
  height: 42px;
  padding: 0 12px;
  border-radius: 999px;
  font-weight: 950;
  letter-spacing: 0.2px;
}

@media (min-width: 900px){
  .zc-btn--rank{
    min-width: 44px;
    height: 44px;
  }
}

/* Map pill: "🗺️ Mundos" */
.zc-btn__icon{ display:inline-flex; align-items:center; justify-content:center; }
.zc-btn__text{ display:inline-block; }

.zc-btn--icon{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap: 8px;
  white-space: nowrap;
}

/* When the icon button has text (Mundos), make it a premium pill */
.zc-topbar__actions .zc-btn--icon .zc-btn__text{
  font-weight: 950;
  letter-spacing: 0.2px;
  font-size: 12.5px;
}

.zc-topbar__actions .zc-btn--icon{
  height: 42px;
  padding: 0 12px;
  border-radius: 999px;
}

@media (max-width: 520px){
  .zc-topbar__actions .zc-btn--icon{
    height: 38px;
    padding: 0 10px;
    gap: 6px;
  }
  .zc-topbar__actions .zc-btn--icon .zc-btn__text{
    font-size: 12px;
  }
}
        /* ===== Premium Candy Background (NO WHITE DOTS / iOS-safe) ===== */
.zc-root{ isolation:isolate; }

.zc-bg{
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;

  /* Clean candy-sky base (no tiny radials = no speckles) */
  background:
    linear-gradient(180deg,
      #6fd3ff 0%,
      #a78bfa 32%,
      #f9a8d4 64%,
      #fde68a 100%
    );

  /* Pop premium but stable */
  filter: saturate(1.22) contrast(1.06);
}

/* Only BIG soft glows (avoid 1px/2px sparkles that show as dots on iOS) */
.zc-bg::before{
  content: "";
  position: absolute;
  inset: -14%;
  pointer-events: none;

  background:
    radial-gradient(980px 760px at 16% 18%, rgba(255,215,0,0.30), rgba(255,215,0,0) 72%),
    radial-gradient(1060px 820px at 86% 18%, rgba(99,102,241,0.28), rgba(99,102,241,0) 74%),
    radial-gradient(1040px 820px at 78% 84%, rgba(236,72,153,0.26), rgba(236,72,153,0) 74%),
    radial-gradient(1040px 820px at 22% 86%, rgba(34,211,238,0.22), rgba(34,211,238,0) 74%),
    radial-gradient(860px 760px at 52% 54%, rgba(255,255,255,0.10), rgba(255,255,255,0) 68%);

  opacity: 0.92;
  transform: translate3d(0,0,0);
  animation: zcCandyDrift 14s ease-in-out infinite;
}

/* Readability vignette only (no glitter) */
.zc-bg::after{
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;

  background:
    radial-gradient(circle at 50% 18%, rgba(255,255,255,0.10), rgba(255,255,255,0) 52%),
    radial-gradient(circle at 50% 62%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.46) 92%);

  opacity: 0.92;
  animation: zcCandyPulse 10s ease-in-out infinite;
}

@keyframes zcCandyDrift{
  0%   { transform: translate3d(0,0,0) scale(1.02); }
  50%  { transform: translate3d(0,-14px,0) scale(1.04); }
  100% { transform: translate3d(0,0,0) scale(1.02); }
}

@keyframes zcCandyPulse{
  0%   { opacity: 0.88; filter: brightness(1.00); }
  50%  { opacity: 0.96; filter: brightness(1.06); }
  100% { opacity: 0.88; filter: brightness(1.00); }
}

/* ===== Mobile perf guardrails (iOS-friendly) ===== */
@media (max-width: 520px){
  .zc-bg::before,
  .zc-bg::after{
    animation: none !important;
  }
  .zc-bg{
    filter: saturate(1.16) contrast(1.05);
  }

  /* blur = artifacts + speckles on iOS; disable where it matters */
  .zc-topbar,
  .zc-hud,
  .zc-legend{
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
}

@media (prefers-reduced-motion: reduce){
  .zc-bg::before,
  .zc-bg::after{
    animation: none !important;
  }
}

/* Hint compositor */
.zc-bg,
.zc-bg::before,
.zc-bg::after{
  transform: translateZ(0);
}

/* V2 atmosphere categories reuse the existing background effect system. */
.zc-root[data-zc-atmosphere="solar"] .zc-bg{ filter: saturate(1.28) brightness(1.04); }
.zc-root[data-zc-atmosphere="moonlit"] .zc-bg{ filter: saturate(.92) brightness(.96); }
.zc-root[data-zc-atmosphere="grounded"] .zc-bg{ filter: saturate(1.04) contrast(1.04); }
.zc-root[data-zc-atmosphere="futuristic"] .zc-bg{ filter: saturate(1.18) hue-rotate(-8deg); }
.zc-root[data-zc-atmosphere="mysterious"] .zc-bg{ filter: saturate(1.12) brightness(.92); }

        /* ===== Compact HUD (4 chips aligned & centered) ===== */
.zc-hud{
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  width: min(560px, 92vw);
  margin: 10px auto 10px;
  padding: 6px;
  border-radius: 14px;
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,215,0,0.12);
  box-shadow: 0 10px 20px rgba(0,0,0,0.24);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  justify-items: stretch;
}

.zc-hud__chip{
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 10px 8px;
  border-radius: 13px;
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.16));
  border: 1px solid rgba(255,255,255,0.10);
  min-height: 56px;
  text-align: center;
  overflow: hidden;
}

  .zc-hud__chip.is-busy{
    border-color: rgba(255,215,0,0.26);
    box-shadow: 0 12px 22px rgba(0,0,0,0.28);
  }

  /* Hard reset: prevent any inherited positioning from pushing HUD text outside */
  .zc-hud__chip .zc-hud__label,
  .zc-hud__chip .zc-hud__value{
    position: static !important;
    top: auto !important;
    right: auto !important;
    bottom: auto !important;
    left: auto !important;
    transform: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .zc-hud__label{
    display: block;
    font-size: 9px;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.9px;
    opacity: 0.78;
    line-height: 1;
    white-space: nowrap;
  }

/* IMPORTANT: keep this purely typographic so it doesn't fight older .zc-hud__value rules */
.zc-hud__value{
  display: inline-block;
  font-size: 15px;
  font-weight: 950;
  line-height: 1.05;
  text-shadow: 0 10px 20px rgba(0,0,0,0.42);
  white-space: nowrap;
}

/* Crystal compact value styling */
.zc-crystalCompact{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(0,0,0,0.16);
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
  font-size: 13.5px;
}

@media (max-width: 520px){
  .zc-hud{
    width: min(560px, 96vw);
    gap: 6px;
    padding: 6px;
    border-radius: 13px;
  }
  .zc-hud__chip{
    min-height: 52px;
    padding: 9px 6px;
    border-radius: 12px;
  }
  .zc-hud__label{
    font-size: 8.5px;
    letter-spacing: 0.8px;
  }
  .zc-hud__value{
    font-size: 13.5px;
  }
  .zc-crystalCompact{
    font-size: 12.5px;
    padding: 4px 8px;
    gap: 5px;
  }
}

        /* ===== Board sizing (bigger board, responsive) ===== */
        /* IMPORTANTE: transform NO afecta el flujo del layout, por eso reservamos espacio abajo */
        .zc-boardWrap{
          transform: scale(1.12);
          transform-origin: top center;
          margin-bottom: 56px;   /* ✅ reserva suficiente para la leyenda */
          padding-bottom: 10px;
        }

        @media (max-width: 520px){
          .zc-boardWrap{
            transform: scale(1.03);
            margin-bottom: 42px;
            padding-bottom: 8px;
          }
        }

        @media (min-width: 860px){
          .zc-boardWrap{
            transform: scale(1.16);
            margin-bottom: 64px;
            padding-bottom: 12px;
          }
        }

/* legend: compact pill, never overlaps board */
.zc-legend{
  margin-top: 0px;
  width: fit-content;
max-width: min(560px, 92vw);
  display: inline-block;
  align-self: center;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1px;
  opacity: 0.92;
  background: rgba(0,0,0,0.30);
  border: 1px solid rgba(255,215,0,0.14);
  box-shadow: 0 10px 22px rgba(0,0,0,0.30);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  text-align: center;
  line-height: 1.25;
}
  .zc-legendWrap{
  display:flex;
  justify-content:center;
  margin-top: 12px;
  margin-bottom: 10px;
  padding: 0 8px;
}

@keyframes zodiacFlash {
  from { opacity: 0.85; }
  to { opacity: 0; }
}
@keyframes scorePop {
  0% { transform: translate(-50%, 10px) scale(0.8); opacity: 0; }
  30% { opacity: 1; }
  100% { transform: translate(-50%, -26px) scale(1.15); opacity: 0; }
}

.zc-p {
  border-radius: 999px;
  background: radial-gradient(circle at 30% 30%, var(--c1), var(--c2) 55%, rgba(255,255,255,0) 75%);
  filter: drop-shadow(0 10px 18px rgba(0,0,0,0.28));
  opacity: 0;
  animation: zcPop 900ms ease-out forwards;
}

@keyframes zcPop {
  0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.2); opacity: 0; }
}

/* FIRE */
.zc-fire.zc-spark {
  width: 8px; height: 8px;
  animation-timing-function: cubic-bezier(.1,.9,.2,1);
}
.zc-fire.zc-ember {
  width: 12px; height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--c1), var(--c2));
  filter: blur(0.2px) drop-shadow(0 10px 18px rgba(0,0,0,0.25));
}

/* WATER */
.zc-water.zc-drop {
  width: 7px; height: 11px;
  border-radius: 999px 999px 999px 999px;
  background: linear-gradient(180deg, var(--c1), rgba(255,255,255,0.25));
}
.zc-water.zc-mist {
  width: 14px; height: 14px;
  filter: blur(0.6px);
  opacity: 0;
}

/* AIR */
.zc-air.zc-streak {
  width: 16px; height: 4px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255,255,255,0), var(--c1), rgba(255,255,255,0));
  filter: blur(0.2px);
}
.zc-air.zc-glint {
  width: 10px; height: 10px;
  background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.9), var(--c1) 45%, rgba(255,255,255,0) 75%);
}

/* EARTH */
.zc-earth.zc-dust {
  width: 12px; height: 12px;
  filter: blur(0.4px);
}
.zc-earth.zc-chip {
  width: 10px; height: 6px;
  border-radius: 6px;
  background: linear-gradient(135deg, var(--c1), rgba(255,255,255,0.15));
}
  .zc-shake-x {
    animation: zcShakeX 280ms cubic-bezier(.2,.8,.2,1);
  }
  @keyframes zcShakeX {
    0% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-4px); }
    80% { transform: translateX(4px); }
    100% { transform: translateX(0); }
  }

  .zc-shake-y {
    animation: zcShakeY 280ms cubic-bezier(.2,.8,.2,1);
  }
  @keyframes zcShakeY {
    0% { transform: translateY(0); }
    20% { transform: translateY(-6px); }
    40% { transform: translateY(6px); }
    60% { transform: translateY(-4px); }
    80% { transform: translateY(4px); }
    100% { transform: translateY(0); }
  }

  /* micro "thunk" punch on swap-back */
  .zc-punch {
    animation: zcPunch 190ms cubic-bezier(.2,.9,.2,1);
  }
  @keyframes zcPunch {
    0% { transform: scale(1); }
    45% { transform: scale(0.985); }
    100% { transform: scale(1); }
  }
@keyframes zcMissionPulse {
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(255,215,0,0); }
  40% { transform: scale(1.02); box-shadow: 0 18px 38px rgba(255,215,0,0.18); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(255,215,0,0); }
}
.zc-mission-pulse {
  animation: zcMissionPulse 850ms cubic-bezier(.2,.9,.2,1);
}

@keyframes zcXpFly {
  0% { opacity: 0; transform: translateX(-50%) translateY(18px) scale(0.9); }
  18% { opacity: 1; }
  70% { opacity: 1; }
  100% { opacity: 0; transform: translateX(-50%) translateY(-26px) scale(1.08); }
}

@keyframes zcBadgePop {
  0% { transform: scale(0.85); opacity: 0; }
  35% { transform: scale(1.04); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes zcHelpIn {
  from { opacity: 0; transform: translateY(-8px) scale(0.965); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes zcHelpOverlayIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes zcHelpCardIn {
  0% { transform: translateY(10px) scale(0.98); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}

@keyframes zcEndOverlayIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes zcEndCardIn {
  0% { transform: translateY(10px) scale(0.985); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}

@keyframes zcMultPop {
  0% { transform: translateX(-50%) translateY(10px) scale(0.96); opacity: 0; }
  18% { opacity: 1; }
  100% { transform: translateX(-50%) translateY(-16px) scale(1.02); opacity: 0; }
}

/* ===== Win Confetti (no libs) ===== */
@keyframes zcConfettiFall {
  0% { transform: translate3d(var(--x, 0px), -18vh, 0) rotate(var(--r0, 0deg)); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translate3d(var(--x, 0px), 118vh, 0) rotate(var(--r1, 360deg)); opacity: 0; }
}
@keyframes zcWinGlow {
  0% { box-shadow: 0 20px 60px rgba(0,0,0,0.55); transform: translateY(6px) scale(0.98); }
  100% { box-shadow: 0 28px 80px rgba(0,0,0,0.62); transform: translateY(0px) scale(1); }
}

.zc-confetti {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 999998;
  overflow: hidden;
}
.zc-confetti i {
  position: absolute;
  top: -18vh;
  left: 50%;
  width: var(--w, 8px);
  height: var(--h, 14px);
  border-radius: 999px;
  background: var(--c, rgba(255,215,0,0.9));
  filter: drop-shadow(0 10px 18px rgba(0,0,0,0.35));
  opacity: 0;
  animation: zcConfettiFall var(--d, 1400ms) cubic-bezier(.2,.8,.2,1) forwards;
}

/* Final board-first mobile overrides are intentionally last so they win over
   the legacy gamer skin rules above. */
@media (max-width: 520px){
  .zc-root{ padding: 6px 10px 60px !important; }
  .zc-topbar{
    margin: 4px 8px 3px;
    padding: calc(6px + env(safe-area-inset-top, 0px)) 8px 6px;
    gap: 6px;
  }
  .zc-topbar__subtitle{ font-size: 10.5px; }
  .zc-hud{
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(104px, 1.35fr);
    gap: 5px;
    padding: 4px;
    margin: 3px auto;
  }
  .zc-hud__chip{ min-height: 46px; padding: 7px 4px; }
  .zc-hud__chip:nth-child(2) .zc-xpDetailBar,
  .zc-hud__chip:nth-child(2) .zc-xpDetailValue,
  .zc-hud__chip:nth-child(2) [aria-label="Level unlocks"]{ display:none !important; }
}

}
`}</style>
      </div>
    </div>
  );


}


export default function ZodiacCrushScreen(props) {
  const location = useLocation();
  const routeKey = String(location?.key || `${location.pathname}|${location.search}`);
  return <ZodiacCrushScreenInner key={routeKey} {...props} />;
}

// Named export keeps the compact mission presentation independently testable
// without exposing or changing the production screen authority.
export { MissionsPanelGamer };
